import { useState, useEffect, useRef, useCallback, useImperativeHandle } from "react";
import { flushSync } from "react-dom";
import {
  getEmployee,
  getEmployeeBankDetails,
  getEmployees,
  getNextEmployeeCode,
  onboardEmployee,
  sendEmployeeWelcomeEmail,
  updateEmployee,
} from "./EmployeeApi";
import EmployeeBankSection from "../Bank/EmployeeBankSection";
import { getOrgHierarchyForest } from "./hierarchyApi";
import { hierarchyRoleOf, indexForest, isEligibleManagerNode } from "./hierarchy/hierarchyTree";
import { formatRoleLabel } from "./employeeRoleConstants";
import {
  createEmployeeStatutory,
  getEmployeeStatutory,
  upsertEmployeeStatutory,
} from "../Statutary/employeeStatutoryApi";
import {
  blankToNull,
  collectStatutoryFormErrors,
  isStatutoryFieldValid,
  sanitizeStatutoryValue,
  statutoryErrorsFromApiDetail,
} from "../Statutary/employeeStatutoryFields";
import { createAccount, listAccounts, resolveIfsc, updateAccount } from "../Bank/employeeBankingApi";
import { bankApiError } from "../Bank/bankErrorUtils";
import {
  assignEmployeeSalary,
  calculateTemplateSalary,
  getEmployeeSalaryStructure,
  getTemplates,
} from "../Salary/SalaryApi";
import API from "../../services/api";
import { FullPayNotice, normalizeSalaryPayoutMode } from "../Salary/SalaryPayoutModeField";
import { useLocation, useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import Select from "react-select";
import DatePickerField from "../../components/common/DatePickerField";
import { validateDateOfBirth } from "../../components/common/datePickerUtils";
import ReportingInfoSection from "./components/ReportingInfoSection";
import SetupIncompleteBanner from "../../components/common/SetupIncompleteBanner";
import OrgProfileBanner from "../../components/setup/OrgProfileBanner";
import { useOrgProfile } from "../Context/OrgProfileContext";
import "../../styles/orgProfileWizard.css";
import BrixigoPageLoader from "../../components/common/BrixigoPageLoader";
import GuidedNotice from "../../components/ui/GuidedNotice";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import SuccessScreen, { SuccessMark, SuccessTitle } from "../../components/common/SuccessScreen";
import { canGoBackInApp } from "../../components/common/smartBack";
import { useFoundationSetup } from "../Context/FoundationSetupContext";
import { useSetupProgress } from "../Context/SetupProgressContext";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateEmployeesCache } from "../../hooks/useEmployees";
import { invalidateDashboardOverview } from "../../services/dashboardApi";
import { DEFAULT_EMPLOYEE_TYPE, EMPLOYEE_TYPES } from "./employeeTypeConstants";
import { DEFAULT_EMPLOYEE_ROLE, SYSTEM_ROLES } from "./employeeRoleConstants";

/** Welcome email states that allow an explicit Admin resend (never sent automatically). */
const WELCOME_RESENDABLE = new Set(["DELIVERY_FAILED", "NOT_SENT"]);

function readWelcomeEmailStatus(emp) {
  const meta = emp?.metadata || emp?.extra_metadata || {};
  return meta?.welcome_email?.status || null;
}

function isSalaryTemplateAssignable(t) {
  const raw = String(t?.status || t?.meta?.status || "").trim().toLowerCase();
  if (raw === "inactive") return false;
  if (t?.is_active === false) return false;
  return true;
}

/** Phone input: digits only, exactly 10 digits (no country code — that's separate). */
function sanitizePhoneInput(value = "") {
  let s = String(value || "").replace(/\D/g, "");
  return s.slice(0, 10);
}

function validateMobilePhone(value = "") {
  const raw = String(value || "").trim();
  if (!raw) {
    return "Mobile Phone is required. Enter 10 digit number.";
  }
  if (/[a-zA-Z]/.test(raw)) {
    return "Phone number cannot contain letters. Enter digits only.";
  }
  const digits = raw.replace(/\D/g, "");
  if (digits.length !== 10) {
    return "Enter a valid 10-digit mobile number.";
  }
  return null;
}

function validateWorkEmail(value = "") {
  const email = String(value || "").trim();
  if (!email) return "Work Email is required.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return "Enter a valid email address (e.g. name@company.com).";
  }
  return null;
}

function resolveOrgNumbering(hr = {}) {
  const nested = hr.employee_numbering && typeof hr.employee_numbering === "object"
    ? hr.employee_numbering
    : {};
  const auto = nested.auto_generate ?? hr.employee_auto_increment ?? true;
  const usePrefix = nested.use_prefix ?? hr.employee_use_prefix ?? true;
  const prefix = String(nested.prefix || hr.employee_prefix || "EMP")
    .replace(/[^a-zA-Z]/g, "")
    .toUpperCase()
    .slice(0, 4);
  const start = Number(
    nested.starting_number ?? nested.start_number ?? hr.employee_number_start ?? 1
  ) || 1;
  const length = Math.max(1, Math.min(9, Number(nested.number_length || 4) || 4));
  return {
    auto_generate: Boolean(auto),
    use_prefix: usePrefix !== false,
    prefix: prefix || "EMP",
    starting_number: start,
    number_length: length,
  };
}

function padCodeNumber(raw, length = 4) {
  const digits = String(raw ?? "").replace(/\D/g, "").slice(0, 9);
  if (!digits) return "";
  const len = Math.max(1, Math.min(9, Number(length) || 4));
  return digits.length >= len ? digits : digits.padStart(len, "0");
}

function composeEmployeeCode(prefix, number, usePrefix, length = 4) {
  const letters = usePrefix
    ? String(prefix || "").replace(/[^a-zA-Z]/g, "").toUpperCase().slice(0, 4)
    : "";
  const digits = padCodeNumber(number, length);
  if (!digits) return "";
  return `${letters}${digits}`;
}

const STEP_FIELD_ORDER = [
  "employee_type",
  "employee_name",
  "display_name",
  "work_email",
  "mobile_phone",
  "gender",
  "date_of_birth",
  "role",
  "department_id",
  "designation_id",
  "location_id",
  "date_of_joining",
  "annual_ctc",
  "salary_template_id",
];

function scrollToFirstError(errors) {
  if (!errors || typeof errors !== "object") return;
  const keys = Object.keys(errors);
  if (!keys.length) return;
  const firstKey = STEP_FIELD_ORDER.find((k) => errors[k]) || keys[0];
  requestAnimationFrame(() => {
    const el =
      document.querySelector(`[name="${firstKey}"]`)
      || document.querySelector(`[data-field="${firstKey}"]`)
      || document.querySelector(".ec-ferr")
      || document.querySelector(".ec-step-error-banner");
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    if (typeof el.focus === "function" && !el.disabled) {
      try {
        el.focus({ preventScroll: true });
      } catch {
        el.focus();
      }
    }
  });
}

/* ═══════════════════════════════════════════════════════
   PREMIUM CSS
═══════════════════════════════════════════════════════ */
const EC_CSS = `
@keyframes ecSlideIn  { from { opacity:0; transform:translateX(18px); } to { opacity:1; transform:none; } }
@keyframes ecFadeUp   { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:none; } }
@keyframes ecPulse    { 0%,100%{box-shadow:0 0 0 0 rgba(37,99,235,.45)} 60%{box-shadow:0 0 0 8px rgba(37,99,235,0)} }
@keyframes ecSpin     { to { transform:rotate(360deg); } }
@keyframes ecBounce   { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
@keyframes ecPop      { 0%{transform:scale(.6);opacity:0} 70%{transform:scale(1.08)} 100%{transform:scale(1);opacity:1} }
@keyframes ecRipple   { 0%{transform:scale(0);opacity:.5} 100%{transform:scale(4);opacity:0} }
@keyframes ecShimmer  {
  0%   { background-position: -400px 0; }
  100% { background-position:  400px 0; }
}
@keyframes ecOverlayIn { from { opacity:0; } to { opacity:1; } }
@keyframes ecModalIn {
  from { opacity:0; transform:translateY(22px) scale(.96); }
  to   { opacity:1; transform:none; }
}

/* ── PAGE ─── */
.ec-page {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  background: linear-gradient(160deg, #f0f4ff 0%, #f8fafc 45%, #eef2ff 100%);
  min-height: 100vh;
  min-height: 100dvh;
  padding: 20px 24px 32px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}
.page-wrapper:has(.ec-page--full) {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
}
.ec-page--full {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  padding: 8px 10px 10px;
  display: flex;
  flex-direction: column;
  background: transparent;
}
.ec-page--full .ec-wrap {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  max-width: 1180px;
  width: 100%;
  margin: 0 auto;
}
.ec-page--full .ec-card {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 16px;
}
.ec-page--full .ec-step-bar {
  flex-shrink: 0;
  border-radius: 16px 16px 0 0;
  border-bottom: 1px solid #eef2ff;
}
.ec-page--full .ec-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 14px 18px 16px;
  scrollbar-width: thin;
  scrollbar-color: #94a3b8 transparent;
}
.ec-page--full .ec-body::-webkit-scrollbar { width: 8px; }
.ec-page--full .ec-body::-webkit-scrollbar-track { background: transparent; }
.ec-page--full .ec-body::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 999px;
  border: 2px solid transparent;
  background-clip: padding-box;
}
.ec-page--full .ec-body::-webkit-scrollbar-thumb:hover { background: #94a3b8; background-clip: padding-box; }
.ec-page--full .ec-sec-hd { display: none; }
.ec-page--full .ec-stat-notes { display: none; }
.ec-page--full .ec-step-item { padding: 10px 12px; gap: 8px; }
.ec-page--full .ec-step-num { width: 28px; height: 28px; font-size: 12px; }
.ec-page--full .ec-step-lbl { font-size: 12.5px; }
.ec-page--full .ec-step-sub { display: none; }
.ec-page--full .ec-flbl { margin-bottom: 4px; font-size: 10.5px; }
.ec-page--full .ec-inp,
.ec-page--full .ec-sel { height: 38px; font-size: 13.5px; }
.ec-page--full .ec-inp { padding: 0 12px 0 38px; }
.ec-page--full .ec-sel { padding: 0 32px 0 38px; }
.ec-page--full .ec-iico { left: 12px; }
.ec-page--full .ec-g2,
.ec-page--full .ec-g3,
.ec-page--full .ec-org-grid,
.ec-page--full .ec-comp-grid { gap: 12px 16px; }
.ec-page--full .ec-org-grid,
.ec-page--full .ec-comp-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.ec-page--full .ec-org-wrap {
  gap: 8px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 10px 14px 8px;
  box-shadow: none;
}
.ec-page--full .ec-org-group {
  padding: 12px 14px;
  border-radius: 12px;
  box-shadow: none;
}
.ec-page--full .ec-org-group-hd { margin-bottom: 10px; padding-bottom: 8px; gap: 8px; }
.ec-page--full .ec-org-group-sub { display: none; }
.ec-page--full .ec-org-group-icon { width: 28px; height: 28px; border-radius: 8px; }
.ec-page--full .ec-org-group-title { font-size: 13.5px; }
.ec-page--full .ec-org-grid { gap: 8px 14px; }
.ec-page--full .ec-org-role { grid-column: span 2; }
.ec-page--full .ec-report { margin-top: 2px; padding-top: 6px; border-top: 1px solid #eef2ff; }
.ec-page--full .ec-report-hd { margin-bottom: 6px; padding-bottom: 4px; }
.ec-page--full .ec-report-grid { gap: 6px 12px; }
.ec-page--full .ec-report-cell span { font-size: 10px; }
.ec-page--full .ec-report-cell strong { font-size: 12.5px; }
.ec-page--full .ec-report-chain { margin-top: 4px; }
.ec-page--full .ec-report-chain span { font-size: 10px; }
.ec-page--full .ec-report-chain p { font-size: 12px; }
.ec-page--full .ec-org-wrap .ec-org-group-hd { margin-bottom: 6px; padding-bottom: 4px; gap: 8px; }
.ec-page--full .ec-org-wrap .ec-org-group-icon { width: 22px; height: 22px; border-radius: 6px; }
.ec-page--full .ec-org-wrap .ec-org-group-title { font-size: 12.5px; }
.ec-page--full .ec-body--org .ec-inp,
.ec-page--full .ec-body--org .ec-sel { height: 34px; font-size: 13px; }
.ec-page--full .ec-body--org .ec-rs .react-select__control {
  min-height: 34px !important; height: 34px !important; font-size: 13px;
}
.ec-page--full .ec-body--org .ec-flbl { margin-bottom: 2px; font-size: 10px; }
.ec-page--full .ec-body--org .ec-quick-add-btn { margin-top: 2px; }
.ec-page--full .ec-nav { padding: 10px 16px; flex-shrink: 0; }
.ec-page--full .ec-btn-back-nav,
.ec-page--full .ec-btn-next,
.ec-page--full .ec-btn-save { min-height: 38px; padding: 8px 16px; font-size: 13.5px; }
.ec-page--full .ec-back { margin-bottom: 8px; padding: 6px 12px; align-self: flex-start; }
.ec-page--full .ec-fhint { display: none; }
.ec-page--full .ec-ferr,
.ec-page--full .ec-fgreen { margin-top: 3px; font-size: 11px; }
.ec-page--full .ec-code-next { margin-top: 3px; font-size: 11px; }
.ec-page--full .ec-code-sep { line-height: 38px; }
.ec-page--full .ec-rs .react-select__control {
  min-height: 38px !important; height: 38px !important; font-size: 13.5px;
}
.ec-page--full .ec-bank-info { padding: 8px 12px; margin-bottom: 12px; }
.ec-page--full .ec-bank-info-ico { width: 28px; height: 28px; }
.ec-page--full .ec-bank-body { padding: 10px 12px; }
.ec-page--full .ec-bank-head { padding: 8px 12px; }
.ec-page--full .ec-role-highlight { padding: 0; background: none; border: none; }
.ec-page--full .ec-role-tip { display: none; }
.ec-page--full .ec-quick-add-btn {
  margin-top: 4px; padding: 0; min-height: 0;
  background: none; border: none; box-shadow: none;
  font-size: 11px; transform: none;
}
.ec-page--full .ec-quick-add-btn:hover { background: none; transform: none; }
.ec-page--full button.ec-inp { display: inline-flex; align-items: center; cursor: pointer; text-align: left; }
.ec-page--full .ebank-header { margin-bottom: 8px; gap: 8px; align-items: center; }
.ec-page--full .ebank-header-icon { width: 28px; height: 28px; border-radius: 8px; }
.ec-page--full .ebank-title { font-size: 15px; }
.ec-page--full .ebank-subtitle { display: none; }
.ec-page--full .ebank-form { margin-top: 8px; padding: 12px; }
.ec-page--full .ebank-ifsc-box { padding: 8px 10px; margin-bottom: 10px; }
.ec-page--full .ebank-ifsc-box input,
.ec-page--full .ebank-grid input,
.ec-page--full .ebank-grid select {
  min-height: 38px !important; font-size: 13.5px !important; padding: 6px 10px !important;
}
.ec-page--full .ebank-grid { gap: 12px 16px; }
.ec-page--full .ebank-btn.add { min-height: 38px; margin-top: 8px; font-size: 13px; }

/* ── BACK BUTTON ─── */
.ec-back {
  display: inline-flex; align-items: center; gap: 7px;
  font-size: 13px; font-weight: 700; color: #1e40af;
  background: rgba(255,255,255,.85); border: 1.5px solid rgba(37,99,235,.2);
  border-radius: 10px; padding: 8px 14px;
  cursor: pointer; transition: all .2s;
  margin-bottom: 18px;
  backdrop-filter: blur(4px);
  font-family: inherit;
}
.ec-back:hover { background: #fff; color: #1e3a8a; border-color: rgba(37,99,235,.4);
  box-shadow: 0 2px 8px rgba(30,58,138,.12); transform: translateX(-2px); }

/* ── WRAPPER ─── */
.ec-wrap {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

/* ── HERO BANNER ─── */
.ec-hero {
  background: var(--brix-hero-gradient);
  border-radius: 20px 20px 0 0;
  padding: var(--brix-hero-padding);
  position: relative; overflow: hidden;
}
.ec-hero::before {
  content:''; position:absolute; inset: 0;
  background: var(--brix-hero-overlay);
  pointer-events:none;
}
.ec-hero::after {
  display: none;
}
.ec-hero-inner {
  display: flex; align-items: center; justify-content: space-between;
  position: relative; z-index: 1; gap: 20px; flex-wrap: wrap;
}
.ec-hero-left { flex: 1; min-width: 200px; }
.ec-hero-badge {
  display: inline-flex; align-items: center; gap: 6px;
  background: rgba(255,255,255,.15); border: 1px solid rgba(255,255,255,.22);
  border-radius: 20px; padding: 4px 13px;
  font-size: 11.5px; font-weight: 700; color: rgba(255,255,255,.92);
  letter-spacing: .3px; margin-bottom: 10px;
}
.ec-hero-title {
  font-size: 26px; font-weight: 900; color: #fff;
  letter-spacing: -.025em; line-height: 1.1; margin-bottom: 6px;
}
.ec-hero-sub { font-size: 13px; color: rgba(255,255,255,.68); line-height: 1.5; }

/* employee preview card in hero */
.ec-emp-card {
  display: flex; align-items: center; gap: 14px;
  background: rgba(255,255,255,.12); border: 1px solid rgba(255,255,255,.22);
  border-radius: 16px; padding: 14px 18px;
  backdrop-filter: blur(10px);
  animation: ecFadeUp .35s ease;
  min-width: 220px;
}
.ec-emp-ava {
  width: 50px; height: 50px; border-radius: 50%;
  background: linear-gradient(135deg,rgba(255,255,255,.25),rgba(255,255,255,.15));
  border: 2px solid rgba(255,255,255,.3);
  color: #fff; font-size: 18px; font-weight: 900;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; letter-spacing: -.02em;
}
.ec-emp-name { font-size: 15px; font-weight: 800; color: #fff; letter-spacing: -.01em; }
.ec-emp-code { font-size: 12px; color: rgba(255,255,255,.65); margin-top: 2px; font-weight: 600; }
.ec-emp-mode {
  display: inline-flex; align-items: center; gap: 4px; margin-top: 4px;
  background: rgba(34,195,122,.25); border-radius: 20px; padding: 2px 8px;
  font-size: 10.5px; font-weight: 700; color: rgba(255,255,255,.9);
}
.ec-emp-placeholder {
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  background: rgba(255,255,255,.07); border: 1.5px dashed rgba(255,255,255,.2);
  border-radius: 16px; padding: 16px 28px; min-width: 200px; text-align: center;
}
.ec-emp-placeholder-ico { font-size: 22px; opacity: .55; }
.ec-emp-placeholder-txt { font-size: 12px; color: rgba(255,255,255,.4); font-weight: 500; }

/* ── STEP BAR ─── */
.ec-step-bar {
  background: #fff; display: flex;
  border-bottom: 1px solid #f0eef9;
  position: sticky; top: 0; z-index: 20;
}
.ec-step-item {
  flex: 1; display: flex; align-items: center; gap: 11px;
  padding: 18px 16px; position: relative;
  background: transparent; border: 0; font-family: inherit; text-align: left;
  cursor: pointer; color: inherit;
  transition: background .2s;
}
.ec-step-item:hover { background: #f8fafc; }
.ec-step-item:focus-visible {
  outline: 2px solid #2563eb; outline-offset: -2px; z-index: 1;
}
.ec-step-item::after {
  content:''; position:absolute; right:0; top:50%; transform:translateY(-50%);
  width:1px; height:30px; background:#dbeafe;
}
.ec-step-item:last-child::after { display:none; }

.ec-step-num {
  width: 34px; height: 34px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 800; flex-shrink: 0;
  transition: all .3s;
}
.ec-step-num.done {
  background: linear-gradient(135deg,#22c37a,#16a863);
  color: #fff;
  box-shadow: 0 2px 8px rgba(34,195,122,.3);
}
.ec-step-num.active {
  background: linear-gradient(135deg,#1e3a8a,#2563eb);
  color: #fff;
  box-shadow: 0 2px 12px rgba(30,58,138,.35);
  animation: ecPulse 2.2s infinite;
}
.ec-step-num.pending {
  background: #f1f5f9; color: #94a3b8;
  border: 1.5px solid #e2e8f0;
}
.ec-step-lbl {
  font-size: 12.5px; font-weight: 700;
  white-space: nowrap; transition: color .2s;
}
.ec-step-lbl.done   { color: #22c37a; }
.ec-step-lbl.active { color: #1e3a8a; }
.ec-step-lbl.pending{ color: #94a3b8; }
.ec-step-sub { font-size: 10.5px; color: #94a3b8; font-weight: 500; margin-top: 1px; }
.ec-step-sub.active { color: #60a5fa; }
.ec-step-item.active-step {
  box-shadow: inset 0 -2px 0 #2563eb;
}

/* ── CARD BODY ─── */
.ec-card {
  background: #fff;
  border-radius: 0 0 20px 20px;
  box-shadow: 0 8px 40px rgba(30,58,138,.11), 0 2px 8px rgba(0,0,0,.04);
}
.ec-body {
  padding: 28px 28px 32px;
  animation: ecSlideIn .28s ease;
  width: 100%;
  box-sizing: border-box;
}

/* ── SECTION HEADER ─── */
.ec-sec-hd { margin-bottom: 24px; }
.ec-sec-title {
  font-size: 17px; font-weight: 900; color: #0f0e1a;
  letter-spacing: -.02em; margin-bottom: 4px;
}
.ec-sec-sub { font-size: 13px; color: #0a0a0a; font-weight: 500; }

/* ── FIELD ─── */
.ec-field { display: flex; flex-direction: column; gap: 0; min-width: 0; }
.ec-flbl {
  font-size: 11px; font-weight: 700; color: #334155;
  text-transform: uppercase; letter-spacing: .6px;
  margin-bottom: 7px; display: inline-flex; align-items: center; gap: 6px; flex-wrap: wrap;
}
.ec-flbl-req { color: #e53e3e; font-size: 13px; line-height: 1; }

/* icon-wrap for inputs/selects */
.ec-iwrap { position: relative; width: 100%; }
.ec-iico {
  position: absolute; left: 13px; top: 50%; transform: translateY(-50%);
  color: #60a5fa; display: flex; align-items: center;
  pointer-events: none; transition: color .2s;
}
.ec-iwrap:focus-within .ec-iico { color: #2563eb; }

/* base input — reference: white fields, readable placeholder */
.ec-inp {
  width: 100%; height: 46px;
  padding: 0 14px 0 42px;
  font-size: 14px; font-weight: 500; color: #0f172a;
  background: #ffffff; border: 1.5px solid #e2e8f0;
  border-radius: 10px; outline: none;
  transition: all .22s; font-family: inherit;
  -webkit-appearance: none; appearance: none;
  box-sizing: border-box;
}
.ec-inp::placeholder { color: #64748b; font-weight: 500; opacity: 1; }
.ec-inp::-webkit-input-placeholder { color: #64748b; font-weight: 500; opacity: 1; }
.ec-inp::-moz-placeholder { color: #64748b; font-weight: 500; opacity: 1; }
.ec-inp:focus { border-color: #2563eb; background: #fff; box-shadow: 0 0 0 3px rgba(37,99,235,.12); }
.ec-inp.err { border-color: #e53e3e; background: #fff5f5; }
.ec-inp.err:focus { box-shadow: 0 0 0 3px rgba(229,62,62,.1); }
.ec-inp.ro { background: #eff6ff; cursor: not-allowed; color: #1e40af; font-weight: 700;
  letter-spacing: .03em; }
.ec-code-split {
  display: grid;
  grid-template-columns: minmax(88px, 120px) 18px minmax(100px, 140px);
  align-items: center;
  gap: 0;
  width: 100%;
  max-width: 300px;
}
.ec-code-prefix {
  width: 100%;
  text-transform: uppercase; font-weight: 700; letter-spacing: 0.06em;
  text-align: center;
  background: #dbeafe !important;
  border-color: #93c5fd !important;
  color: #1e3a8a !important;
}
.ec-code-num {
  width: 100%;
  font-family: 'IBM Plex Mono', ui-monospace, monospace; text-align: center;
  background: #ffffff !important;
  border-color: #e2e8f0 !important;
  color: #0f172a !important;
  font-weight: 600 !important;
}
.ec-code-sep { color: #64748b; font-weight: 700; text-align: center; line-height: 46px; font-size: 16px; }
.ec-inp.no-ico,
.ec-inp.ec-inp-no-icon { padding-left: 14px; padding-right: 14px; }
.ec-code-next {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 12.5px; font-weight: 700; color: #16a34a; margin-top: 8px;
}
.ec-code-next svg { flex-shrink: 0; }
.ec-code-split--num-only {
  grid-template-columns: minmax(120px, 180px);
  max-width: 180px;
}
.ec-code-num.is-editable {
  background: #fff !important;
  cursor: text;
  font-weight: 700 !important;
}
.ec-code-choice {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 10px;
}
.ec-code-mode {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.ec-code-mode-btn {
  border: 1.5px solid #e2e8f0;
  background: #fff;
  color: #334155;
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
}
.ec-code-mode-btn.is-active {
  border-color: #2563eb;
  background: #eff6ff;
  color: #1d4ed8;
}
.ec-code-prefix-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12.5px;
  font-weight: 600;
  color: #334155;
  cursor: pointer;
  user-select: none;
}
.ec-code-prefix-toggle input { accent-color: #2563eb; }
.ec-code-save-org {
  border: none;
  background: none;
  padding: 0;
  color: #2563eb;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  text-align: left;
}
.ec-code-save-org:hover { text-decoration: underline; }
.ec-code-save-org:disabled { color: #94a3b8; cursor: default; text-decoration: none; }

/* select */
.ec-sel {
  width: 100%; height: 46px;
  padding: 0 36px 0 42px;
  font-size: 14px; font-weight: 500; color: #0f172a;
  background: #ffffff; border: 1.5px solid #e2e8f0;
  border-radius: 10px; outline: none; cursor: pointer;
  appearance: none; -webkit-appearance: none;
  transition: all .22s; font-family: inherit;
  box-sizing: border-box;
}
.ec-sel:focus { border-color: #2563eb; background: #fff; box-shadow: 0 0 0 3px rgba(37,99,235,.12); }
.ec-sel.err { border-color: #e53e3e; background: #fff5f5; }
.ec-sel.no-ico { padding-left: 14px; }
.ec-sel-arr {
  position: absolute; right: 13px; top: 50%; transform: translateY(-50%);
  color: #94a3b8; pointer-events: none; font-size: 10px; line-height: 1;
}

/* error / hint text */
.ec-ferr {
  font-size: 11.5px; font-weight: 600; color: #e53e3e;
  margin-top: 5px; display: flex; align-items: center; gap: 4px;
}
.ec-fhint { font-size: 11.5px; color: #64748b; font-weight: 500; margin-top: 5px; line-height: 1.4; }
.ec-fgreen { font-size: 11.5px; font-weight: 700; color: #16a34a; margin-top: 4px; display: flex; align-items: center; gap: 4px; }
.ec-ftip {
  display: inline-flex; align-items: center; justify-content: center;
  width: 16px; height: 16px; color: #94a3b8; cursor: help;
  border-radius: 50%; flex-shrink: 0; position: relative;
}
.ec-ftip:hover { color: #2563eb; }
.ec-ftip svg { width: 14px; height: 14px; }
.ec-ftip[data-tip]::after {
  content: attr(data-tip);
  position: absolute; left: 50%; bottom: calc(100% + 8px);
  transform: translateX(-50%);
  width: min(260px, 70vw); padding: 8px 10px;
  background: #0f172a; color: #f8fafc; font-size: 11.5px; font-weight: 500;
  line-height: 1.45; border-radius: 8px; text-align: left;
  opacity: 0; pointer-events: none; transition: opacity .15s;
  z-index: 40; box-shadow: 0 8px 20px rgba(15,23,42,.25);
  white-space: normal;
}
.ec-ftip[data-tip]:hover::after { opacity: 1; }
.ec-inp.ok { border-color: #86efac !important; background: #f0fdf4 !important; }
.ec-stat-notes {
  margin-top: 18px; background: #f8fafc; border: 1.5px solid #e2e8f0;
  border-radius: 14px; padding: 16px 18px;
}
.ec-stat-notes-title {
  font-size: 13.5px; font-weight: 800; color: #0f172a; margin-bottom: 8px;
  display: flex; align-items: center; gap: 8px;
}
.ec-stat-notes ul {
  margin: 0; padding-left: 18px; color: #475569; font-size: 12.5px;
  line-height: 1.65; font-weight: 500;
}
.ec-stat-notes li { margin-bottom: 4px; }

/* ── GRIDS ─── */
.ec-g2 { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px 20px; align-items: start; }
.ec-g3 { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 18px 20px; align-items: start; }

/* ── NOTICE / WARN / ERROR ─── */
.ec-notice {
  grid-column: 1/-1;
  background: #fffbea; border: 1px solid #fde68a;
  border-radius: 10px; padding: 12px 14px;
  font-size: 13px; color: #78350f; display: flex; gap: 8px; line-height: 1.55;
}
.ec-warn {
  grid-column: 1/-1;
  background: #fff7f0; border: 1px solid #fbd0aa;
  border-radius: 10px; padding: 12px 14px;
  font-size: 13px; color: #7c2d12; display: flex; gap: 8px; line-height: 1.55;
}
.ec-err-banner {
  background: #fef2f2; border: 1px solid #fecaca;
  border-radius: 10px; padding: 12px 14px;
  font-size: 13px; color: #991b1b; font-weight: 600;
  margin-top: 20px; display: flex; gap: 8px; align-items: flex-start;
}
.ec-step-error-banner {
  background: #fef2f2; border: 1px solid #fecaca;
  border-radius: 12px; padding: 12px 14px;
  margin: 0 0 16px;
  color: #991b1b;
}
.ec-step-error-banner strong {
  display: block;
  font-size: 13.5px;
  font-weight: 700;
  margin-bottom: 6px;
}
.ec-step-error-banner ul {
  margin: 0;
  padding-left: 18px;
  font-size: 13px;
  line-height: 1.5;
  font-weight: 500;
}
.ec-step-error-banner li + li { margin-top: 4px; }

.ec-bank-queue {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 0 0 16px;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid #bfdbfe;
  background: #eff6ff;
  color: #1e3a8a;
}
.ec-bank-queue-top { display: flex; align-items: center; gap: 12px; }
.ec-bank-queue-ava {
  width: 42px; height: 42px; border-radius: 12px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: #1e3a8a; color: #fff; font-weight: 800; font-size: 14px;
}
.ec-bank-queue-kicker { font-size: 12px; font-weight: 700; letter-spacing: .02em; color: #1d4ed8; }
.ec-bank-queue-name { font-size: 16px; font-weight: 800; color: #0f172a; line-height: 1.3; }
.ec-bank-queue-code { font-size: 12.5px; font-weight: 700; color: #1e40af; }
.ec-bank-queue-facts {
  display: flex; flex-wrap: wrap; gap: 8px;
}
.ec-bank-queue-facts span {
  background: #fff; border: 1px solid #dbeafe; border-radius: 999px;
  padding: 4px 10px; font-size: 12px; font-weight: 600; color: #1e3a8a;
}
.ec-bank-queue-facts span.is-current {
  background: #1e3a8a; border-color: #1e3a8a; color: #fff;
}
.ec-bank-queue-next { font-size: 12.5px; font-weight: 600; color: #1e40af; }
.ec-bank-onfile {
  margin: 0 0 14px; padding: 12px 14px; border-radius: 12px;
  border: 1px solid #bbf7d0; background: #f0fdf4; color: #14532d;
}
.ec-bank-onfile-title { font-size: 13px; font-weight: 800; margin-bottom: 6px; }
.ec-bank-onfile-row { font-size: 12.5px; font-weight: 600; line-height: 1.5; }
.ec-bank-onfile-note { margin-top: 6px; font-size: 12px; font-weight: 500; color: #166534; }

/* ── REACT-SELECT OVERRIDE ─── */
.ec-rs .react-select__control {
  min-height: 46px !important; height: 46px !important;
  border: 1.5px solid #bfdbfe !important;
  border-radius: 10px !important; background: #faf9ff !important;
  box-shadow: none !important; font-size: 14px; transition: all .22s;
}
.ec-rs .react-select__control--is-focused {
  border-color: #2563eb !important; background: #fff !important;
  box-shadow: 0 0 0 3px rgba(37,99,235,.1) !important;
}
.ec-rs .react-select__control--is-focused .react-select__dropdown-indicator { color: #2563eb; }
.ec-rs .react-select__value-container { padding: 0 14px !important; }
.ec-rs .react-select__placeholder { color: #64748b !important; font-size: 14px; font-weight: 500; }
.ec-rs .react-select__single-value { color: #0f0e1a !important; font-size: 14px; font-weight: 500; }
.ec-rs .react-select__indicator-separator { background-color: #bfdbfe !important; }
.ec-rs .react-select__dropdown-indicator { color: #93c5fd !important; transition: color .2s; }
.ec-rs .react-select__clear-indicator { color: #93c5fd !important; }
.ec-rs .react-select__clear-indicator:hover { color: #e53e3e !important; }
.ec-rs .react-select__menu {
  border-radius: 12px !important; border: 1px solid #bfdbfe !important;
  box-shadow: 0 8px 30px rgba(30,58,138,.12) !important; overflow: hidden;
}
.ec-rs .react-select__option { font-size: 14px !important; }
.ec-rs .react-select__option--is-focused { background: #eff6ff !important; color: #1e3a8a !important; }
.ec-rs .react-select__option--is-selected { background: linear-gradient(135deg,#1e3a8a,#2563eb) !important; color: #fff !important; }

/* ── BANK CARD ─── */
.ec-bank-card {
  border: 1.5px solid #bfdbfe; border-radius: 16px;
  overflow: hidden; margin-bottom: 14px;
  transition: box-shadow .2s, border-color .2s;
}
.ec-bank-card:hover { box-shadow: 0 4px 18px rgba(30,58,138,.1); border-color: #c8c0f0; }
.ec-bank-card.primary { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.1); }
.ec-bank-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px; background: #faf9ff; border-bottom: 1px solid #dbeafe;
}
.ec-bank-head.primary { background: linear-gradient(90deg,#1e3a8a,#2563eb); }
.ec-bank-num {
  width: 32px; height: 32px; border-radius: 9px;
  display: flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 800; flex-shrink: 0;
}
.ec-bank-num.primary { background: rgba(255,255,255,.2); color: #fff; }
.ec-bank-num.other { background: #dbeafe; color: #2563eb; }
.ec-bank-title { font-size: 14px; font-weight: 800; }
.ec-bank-title.primary { color: #fff; }
.ec-bank-title.other { color: #0f0e1a; }
.ec-bank-pill {
  font-size: 10px; font-weight: 700; letter-spacing: .4px;
  padding: 2px 9px; border-radius: 20px; display: inline-flex; align-items: center; gap: 4px;
}
.ec-bank-pill.primary { background: rgba(255,255,255,.2); color: #fff; }
.ec-bank-body { padding: 18px; }

/* ── BANKING INFO ─── */
.ec-bank-info {
  background: linear-gradient(135deg,#f0eef9,#e8eef8);
  border: 1px solid #bfdbfe; border-radius: 12px;
  padding: 14px 16px; margin-bottom: 20px;
  display: flex; gap: 12px; align-items: flex-start;
}
.ec-bank-info-ico {
  width: 36px; height: 36px; border-radius: 10px;
  background: linear-gradient(135deg,#1e3a8a,#2563eb);
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 16px; flex-shrink: 0;
}

/* ── ADD BANK BTN ─── */
.ec-add-bank-btn {
  width: 100%; padding: 14px;
  border: 2px dashed #d4d0f0;
  border-radius: 12px; background: transparent;
  font-size: 13.5px; font-weight: 700; color: #a0a0c8;
  cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px;
  transition: all .2s; font-family: inherit;
}
.ec-add-bank-btn:hover { border-color: #2563eb; color: #1e3a8a; background: #eff6ff; }

/* ── NAVIGATION FOOTER ─── */
.ec-nav {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 36px; border-top: 1px solid #f0eef9;
  background: #faf9ff; border-radius: 0 0 20px 20px;
}
.ec-step-counter { font-size: 13.5px; font-weight: 700; color: #93c5fd; letter-spacing: .3px; }

.ec-btn-back-nav {
  display: flex; align-items: center; gap: 8px;
  min-height: 48px;
  padding: 12px 22px; border: 1.5px solid #bfdbfe; border-radius: 12px;
  font-size: 14.5px; font-weight: 700; color: #7070a0;
  background: transparent; cursor: pointer; font-family: inherit;
  transition: all .2s;
}
.ec-btn-back-nav:hover:not(:disabled) { border-color: #b0a8e0; color: #1e3a8a; background: #eff6ff; }
.ec-btn-back-nav:disabled { opacity: .28; cursor: not-allowed; }

.ec-btn-next {
  display: flex; align-items: center; gap: 8px;
  min-height: 48px;
  padding: 12px 30px; background: linear-gradient(135deg,#1e3a8a,#2563eb);
  border: none; border-radius: 12px;
  font-size: 15px; font-weight: 800; color: #fff;
  cursor: pointer; font-family: inherit;
  box-shadow: 0 3px 14px rgba(30,58,138,.28);
  transition: all .2s;
}
.ec-btn-next:hover:not(:disabled) { box-shadow: 0 5px 22px rgba(30,58,138,.42); transform: translateY(-1px); }
.ec-btn-next:disabled { opacity: .6; cursor: not-allowed; transform: none; }

.ec-btn-save {
  display: flex; align-items: center; gap: 8px;
  min-height: 48px;
  padding: 12px 30px; background: linear-gradient(135deg,#059669,#22c37a);
  border: none; border-radius: 12px;
  font-size: 15px; font-weight: 800; color: #fff;
  cursor: pointer; font-family: inherit;
  box-shadow: 0 3px 14px rgba(34,195,122,.25);
  transition: all .2s;
}
.ec-btn-save:hover:not(:disabled) { box-shadow: 0 5px 22px rgba(34,195,122,.4); transform: translateY(-1px); }
.ec-btn-save:disabled { opacity: .6; cursor: not-allowed; transform: none; }

.ec-nav-actions {
  display: flex; align-items: center; gap: 10px;
  flex-shrink: 0;
}
.ec-btn-skip {
  display: flex; align-items: center; gap: 6px;
  min-height: 48px;
  padding: 12px 20px; border: 1.5px solid #bfdbfe; border-radius: 12px;
  font-size: 14px; font-weight: 700; color: #64748b;
  background: #fff; cursor: pointer; font-family: inherit;
  transition: all .2s;
}
.ec-btn-skip:hover:not(:disabled) { border-color: #93c5fd; color: #334155; background: #f8fafc; }
.ec-btn-skip:disabled { opacity: .6; cursor: not-allowed; }

.ec-stat-modal-overlay {
  position: fixed; inset: 0; z-index: 80;
  background: rgba(8, 15, 35, .58);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
  animation: ecOverlayIn .22s ease;
}
.ec-stat-modal {
  width: 100%; max-width: 540px;
  background: #fff;
  border-radius: 24px;
  box-shadow: 0 40px 90px rgba(15, 23, 42, .32), 0 0 0 1px rgba(255,255,255,.08);
  overflow: hidden;
  position: relative;
  animation: ecModalIn .32s cubic-bezier(.22,1,.36,1);
}
.ec-stat-modal-banner {
  background: linear-gradient(135deg, #1e3a8a 0%, #2563eb 52%, #3b82f6 100%);
  padding: 22px 24px 20px;
  color: #fff;
  position: relative;
}
.ec-stat-modal-banner::after {
  content: "";
  position: absolute; right: -28px; top: -36px;
  width: 140px; height: 140px; border-radius: 50%;
  background: rgba(255,255,255,.08);
  pointer-events: none;
}
.ec-stat-modal-close {
  position: absolute; top: 14px; right: 14px; z-index: 2;
  width: 36px; height: 36px;
  border: none; border-radius: 11px;
  background: rgba(255,255,255,.14);
  color: #fff; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: background .15s;
}
.ec-stat-modal-close:hover { background: rgba(255,255,255,.26); }
.ec-stat-modal-icon {
  width: 52px; height: 52px; border-radius: 16px;
  background: rgba(255,255,255,.16);
  color: #fff;
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 12px;
  box-shadow: inset 0 0 0 1px rgba(255,255,255,.22);
}
.ec-stat-modal-kicker {
  font-size: 11px; font-weight: 800; letter-spacing: .14em;
  text-transform: uppercase; opacity: .72; margin: 0 0 6px;
}
.ec-stat-modal-title {
  font-size: 20px; font-weight: 800; color: #fff; margin: 0;
  letter-spacing: -.02em; line-height: 1.25;
}
.ec-stat-modal-body { padding: 22px 24px 22px; }
.ec-stat-modal-text {
  font-size: 14px; line-height: 1.65; color: #475569; margin: 0 0 16px;
}
.ec-stat-modal-chips {
  display: flex; flex-wrap: wrap; gap: 8px;
  margin: 0 0 18px;
}
.ec-stat-chip {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 11px; border-radius: 999px;
  background: #fff7ed; border: 1px solid #fed7aa;
  font-size: 11.5px; font-weight: 700; color: #9a3412;
}
.ec-stat-chip-dot {
  width: 6px; height: 6px; border-radius: 50%; background: #f97316; flex-shrink: 0;
}
.ec-stat-choices {
  display: grid; grid-template-columns: 1fr 1fr; gap: 12px;
}
.ec-stat-choice {
  text-align: left;
  border-radius: 16px;
  padding: 16px 15px 15px;
  cursor: pointer;
  font-family: inherit;
  border: 1.5px solid #e2e8f0;
  background: #fff;
  transition: transform .18s, box-shadow .18s, border-color .18s;
}
.ec-stat-choice:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 12px 28px rgba(15,23,42,.1);
}
.ec-stat-choice:disabled { opacity: .65; cursor: not-allowed; transform: none; }
.ec-stat-choice--fill {
  border-color: #bfdbfe;
  background: linear-gradient(180deg, #eff6ff 0%, #fff 72%);
}
.ec-stat-choice--fill:hover:not(:disabled) { border-color: #2563eb; }
.ec-stat-choice--go {
  border-color: #a7f3d0;
  background: linear-gradient(180deg, #ecfdf5 0%, #fff 72%);
}
.ec-stat-choice--go:hover:not(:disabled) { border-color: #059669; }
.ec-stat-choice-icon {
  width: 36px; height: 36px; border-radius: 11px;
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 10px;
}
.ec-stat-choice--fill .ec-stat-choice-icon { background: #dbeafe; color: #1d4ed8; }
.ec-stat-choice--go .ec-stat-choice-icon { background: #d1fae5; color: #047857; }
.ec-stat-choice-title {
  font-size: 14px; font-weight: 800; color: #0f172a; margin: 0 0 4px;
}
.ec-stat-choice-sub {
  font-size: 12px; line-height: 1.5; color: #64748b; margin: 0;
}
.ec-stat-modal-foot {
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid #f1f5f9;
  font-size: 12px; color: #94a3b8; line-height: 1.5;
  display: flex; align-items: flex-start; gap: 8px;
}
.ec-stat-modal-foot svg { flex-shrink: 0; margin-top: 1px; }
@media (max-width: 560px) {
  .ec-stat-choices { grid-template-columns: 1fr; }
}

.ec-spinner {
  width: 15px; height: 15px; border-radius: 50%;
  border: 2px solid rgba(255,255,255,.3); border-top-color: #fff;
  animation: ecSpin .6s linear infinite;
  display: inline-block; flex-shrink: 0;
}

/* ── LOADING / ERROR SCREENS ─── */
.ec-loading {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 80px 40px; gap: 16px;
}
.ec-loading-ring {
  width: 48px; height: 48px; border-radius: 50%;
  border: 3px solid #dbeafe; border-top-color: #2563eb;
  animation: ecSpin .7s linear infinite;
}
.ec-loading-txt { font-size: 14px; color: #a0a0c8; font-weight: 600; }

/* ── SUCCESS SCREEN ─── */
.ec-success-page {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  background: linear-gradient(160deg, #f0f4ff 0%, #f8fafc 45%, #eef2ff 100%);
  min-height: 100vh;
  min-height: 100dvh;
  display: flex; align-items: center; justify-content: center; padding: 24px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}
.ec-success-card {
  background: #fff; border-radius: 24px;
  box-shadow: 0 12px 50px rgba(30,58,138,.14), 0 2px 10px rgba(0,0,0,.05);
  padding: 56px 48px 48px; max-width: 480px; width: 100%;
  text-align: center; animation: ecFadeUp .4s ease;
}
.ec-success-ring {
  width: 104px; height: 104px; border-radius: 50%;
  background: linear-gradient(135deg,#e6f9f0,#ccf5e4);
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 24px;
  box-shadow: 0 0 0 16px rgba(34,195,122,.12);
  animation: ecPop .55s ease .1s both;
}
.ec-success-check { color: #22c37a; font-size: 38px; }
.ec-success-icon { display: flex; align-items: center; justify-content: center; color: #16a34a; animation: ecBounce 1.4s ease infinite .4s; }
.ec-emp-placeholder-ico { display: flex; align-items: center; justify-content: center; opacity: .55; color: rgba(255,255,255,.7); }
.ec-bank-info-ico { display: flex; align-items: center; justify-content: center; color: #1e40af; flex-shrink: 0; }
.ec-hero-badge-ico { display: inline-flex; align-items: center; margin-right: 4px; vertical-align: middle; }
.ec-emp-mode { display: inline-flex; align-items: center; gap: 4px; }
.ec-warn, .ec-notice { display: flex; align-items: flex-start; gap: 8px; }
.ec-warn svg, .ec-notice svg { flex-shrink: 0; margin-top: 1px; }
.ec-hero-badge { display: inline-flex; align-items: center; gap: 5px; }
.ec-success-h { font-size: 28px; font-weight: 900; color: #0f0e1a; letter-spacing: -.025em; margin-bottom: 8px; }
.ec-success-name { color: #1e3a8a; }
.ec-success-p { font-size: 14px; color: #0a0a0a; line-height: 1.7; margin-bottom: 6px; }
.ec-success-checklist {
  text-align: left;
  margin: 18px 0 8px;
  padding: 14px 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}
.ec-success-checklist-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 0;
  font-size: 14.5px;
  font-weight: 650;
  color: #1e293b;
  border-bottom: 1px solid #f1f5f9;
}
.ec-success-checklist-item:last-child { border-bottom: none; }
.ec-success-checklist-item .ok { color: #16a34a; flex-shrink: 0; }
.ec-success-checklist-item .skip { color: #b45309; flex-shrink: 0; }
.ec-success-card { position: relative; overflow: hidden; }
.ec-success-burst { position: absolute; inset: 0; pointer-events: none; overflow: hidden; border-radius: 24px; }
.ec-success-confetti {
  position: absolute; width: 9px; height: 9px; border-radius: 2px;
  animation: ecConfetti 1.7s ease-out forwards; opacity: 0;
}
@keyframes ecConfetti {
  0% { transform: translateY(0) rotate(0); opacity: 1; }
  100% { transform: translateY(160px) rotate(260deg); opacity: 0; }
}
.ec-success-code-box {
  background: linear-gradient(135deg,#eff6ff,#ede9f8);
  border: 1px solid #bfdbfe; border-radius: 12px;
  padding: 14px 18px; margin: 16px 0;
  display: flex; align-items: center; justify-content: space-between;
}
.ec-success-code-lbl { font-size: 11px; font-weight: 700; color: #9090b8; text-transform: uppercase; letter-spacing: .6px; }
.ec-success-code-val { font-size: 20px; font-weight: 900; color: #1e3a8a; letter-spacing: .05em; }
.ec-success-skip-note {
  font-size: 13.5px; color: #92400e; background: #fffbeb; border: 1px solid #fde68a;
  border-radius: 10px; padding: 12px 14px; margin-bottom: 16px; line-height: 1.5; text-align: left;
}
.ec-success-actions { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; margin-top: 24px; }
.ec-success-btn-back {
  min-height: 46px;
  padding: 12px 22px; border: 1.5px solid #bfdbfe; border-radius: 12px;
  font-size: 14.5px; font-weight: 700; color: #7070a0;
  background: transparent; cursor: pointer; font-family: inherit;
  transition: all .2s;
}
.ec-success-btn-back:hover { border-color: #b0a8e0; color: #1e3a8a; }
.ec-success-btn-add {
  min-height: 46px;
  padding: 12px 22px; background: linear-gradient(135deg,#1e3a8a,#2563eb);
  border: none; border-radius: 12px;
  font-size: 14.5px; font-weight: 800; color: #fff;
  cursor: pointer; font-family: inherit;
  box-shadow: 0 3px 14px rgba(30,58,138,.28);
  transition: all .2s;
}
.ec-success-btn-add:hover { box-shadow: 0 5px 20px rgba(30,58,138,.42); transform: translateY(-1px); }
.ec-success-btn-salary {
  min-height: 46px;
  padding: 12px 22px; background: #fff;
  border: 1.5px solid #0284c7; border-radius: 12px;
  font-size: 14.5px; font-weight: 800; color: #0369a1;
  cursor: pointer; font-family: inherit;
  box-shadow: 0 2px 10px rgba(2,132,199,.12);
  transition: all .2s;
}
.ec-success-btn-salary:hover { background: #f0f9ff; border-color: #0c4a6e; transform: translateY(-1px); }

/* ── ORG STEP ─── */
.ec-org-wrap {
  display: flex; flex-direction: column; gap: 10px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 12px 16px 10px;
}
.ec-org-group {
  background: linear-gradient(160deg, #ffffff 0%, #f8faff 100%);
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 22px 24px 24px;
  box-shadow: 0 4px 20px rgba(30,58,138,.05);
  transition: box-shadow .25s, border-color .25s;
}
.ec-org-group:hover { box-shadow: 0 8px 28px rgba(30,58,138,.08); border-color: #c7d2fe; }
.ec-org-group-hd {
  display: flex; align-items: flex-start; gap: 14px;
  margin-bottom: 20px; padding-bottom: 16px;
  border-bottom: 1px solid #eef2ff;
}
.ec-org-group-icon {
  width: 42px; height: 42px; border-radius: 13px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; color: #fff;
  box-shadow: 0 4px 14px rgba(37,99,235,.28);
}
.ec-org-group-icon.team { background: linear-gradient(135deg,#1e3a8a,#3b82f6); }
.ec-org-group-icon.access { background: linear-gradient(135deg,#0f766e,#14b8a6); }
.ec-org-group-title { font-size: 15.5px; font-weight: 800; color: #0f172a; letter-spacing: -.02em; }
.ec-org-group-sub { font-size: 12.5px; color: #64748b; margin-top: 3px; font-weight: 500; line-height: 1.45; }
.ec-org-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 18px 20px; }
.ec-org-role { grid-column: span 1; min-width: 0; }

.ec-report { min-width: 0; }
.ec-report-hd { margin-bottom: 8px; padding-bottom: 6px; }
.ec-report-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 8px 14px;
}
.ec-report-cell span {
  display: block; font-size: 10.5px; font-weight: 700; color: #64748b;
  text-transform: uppercase; letter-spacing: .4px; margin-bottom: 2px;
}
.ec-report-cell strong {
  display: block; font-size: 13px; font-weight: 700; color: #0f172a;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.ec-report-chain { margin-top: 6px; }
.ec-report-chain span {
  display: block; font-size: 10.5px; font-weight: 700; color: #64748b;
  text-transform: uppercase; letter-spacing: .4px; margin-bottom: 2px;
}
.ec-report-chain p {
  margin: 0; font-size: 12.5px; color: #475569; font-weight: 500;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.ec-report-link-row { display: flex; align-items: center; gap: 10px; margin-top: 4px; }
.ec-report-link {
  background: none; border: none; padding: 0; font-family: inherit;
  font-size: 12.5px; font-weight: 700; color: #2563eb; cursor: pointer;
}
.ec-report-link:hover { text-decoration: underline; }
.ec-report-edit { margin-top: 6px; display: flex; flex-direction: column; gap: 8px; }
.ec-report-actions { display: flex; flex-wrap: wrap; gap: 6px; }
.ec-report-btn {
  min-height: 32px; padding: 4px 12px; border-radius: 8px; font-family: inherit;
  font-size: 12px; font-weight: 700; cursor: pointer;
  background: #fff; border: 1px solid #e2e8f0; color: #334155;
}
.ec-report-btn.primary { background: #1e40af; border-color: #1e40af; color: #fff; }
.ec-report-btn:disabled { opacity: .5; cursor: not-allowed; }
.ec-report-msg { margin: 0; font-size: 12px; font-weight: 600; }
.ec-report-msg.err { color: #dc2626; }
.ec-report-msg.ok { color: #15803d; }

@media (max-width: 900px) {
  .ec-report-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .ec-page--full .ec-org-role { grid-column: span 1; }
  .ec-page--full .ec-body.ec-body--org { overflow-y: auto; }
}
.ec-quick-add-btn {
  display: inline-flex; align-items: center; gap: 5px;
  margin-top: 8px; padding: 6px 13px;
  font-size: 12px; font-weight: 700; color: #2563eb;
  background: #eff6ff; border: 1px dashed #93c5fd;
  border-radius: 8px; cursor: pointer; transition: all .2s;
  font-family: inherit;
}
.ec-quick-add-btn:hover { background: #dbeafe; border-color: #2563eb; color: #1e40af; transform: translateY(-1px); }
.ec-quick-add-row {
  display: flex; flex-wrap: wrap; gap: 8px; margin-top: 8px; align-items: center;
}
.ec-quick-add-row .ec-inp { flex: 1 1 160px; min-width: 0; padding-left: 14px; }
.ec-role-highlight {
  background: linear-gradient(135deg,#f0fdf4,#ecfdf5);
  border: 1.5px solid #a7f3d0; border-radius: 14px;
  padding: 16px 18px; margin-top: 4px;
}
.ec-role-highlight .ec-sel { background: #fff; border-color: #6ee7b7; }
.ec-role-highlight .ec-sel:focus { border-color: #059669; box-shadow: 0 0 0 3px rgba(5,150,105,.12); }
.ec-role-tip {
  display: flex; align-items: flex-start; gap: 8px;
  margin-top: 10px; padding: 10px 12px;
  background: rgba(255,255,255,.7); border-radius: 10px;
  font-size: 12px; color: #047857; font-weight: 500; line-height: 1.5;
}

/* ── COMPENSATION STEP ─── */
.ec-comp-wrap { display: flex; flex-direction: column; gap: 0; }
.ec-comp-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px 16px; }
.ec-ctc-lpa {
  margin-top: 6px; font-size: 12px; font-weight: 700; color: #64748b;
}

/* ── SALARY CALCULATOR ─── */
.ec-sal-top { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px 16px; }
.ec-sal-method { margin-top: 4px; }
.ec-sal-method-label {
  font-size: 11px; font-weight: 700; color: #475569; letter-spacing: .3px;
  text-transform: uppercase; margin-bottom: 8px;
}
.ec-sal-toggle { display: flex; gap: 10px; }
.ec-sal-card {
  flex: 1; display: flex; align-items: center; gap: 10px;
  padding: 12px 14px; border-radius: 10px; cursor: pointer;
  border: 1.5px solid #e2e8f0; background: #fff;
  transition: all .18s ease;
}
.ec-sal-card:hover { border-color: #93c5fd; background: #f8fafc; }
.ec-sal-card.active {
  border-color: #2563eb; background: #eff6ff;
  box-shadow: 0 0 0 3px rgba(37,99,235,.1);
}
.ec-sal-card-radio {
  width: 18px; height: 18px; border-radius: 50%;
  border: 2px solid #cbd5e1; background: #fff;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; transition: all .15s ease;
}
.ec-sal-card.active .ec-sal-card-radio {
  border-color: #2563eb; background: #2563eb;
}
.ec-sal-card-radio-dot {
  width: 7px; height: 7px; border-radius: 50%;
  background: #fff; opacity: 0; transition: opacity .12s ease;
}
.ec-sal-card.active .ec-sal-card-radio-dot { opacity: 1; }
.ec-sal-card-text { display: flex; flex-direction: column; gap: 1px; }
.ec-sal-card-title { font-size: 13px; font-weight: 700; color: #1e293b; }
.ec-sal-card-sub { font-size: 10.5px; color: #64748b; font-weight: 500; }
.ec-sal-input-row { margin-top: 6px; }
.ec-sal-summary {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px;
  margin-top: 14px;
}
.ec-sal-summary.is-full-pay { grid-template-columns: repeat(3, 1fr); }
.ec-sal-stat {
  background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px;
  padding: 12px 14px; display: flex; flex-direction: column; gap: 4px;
}
.ec-sal-stat.highlight {
  background: #eff6ff; border-color: #bfdbfe;
}
.ec-sal-stat-label { font-size: 10.5px; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: .3px; }
.ec-sal-stat-val { font-size: 16px; font-weight: 800; color: #1e293b; }
.ec-sal-stat.highlight .ec-sal-stat-val { color: #1e40af; }
.ec-sal-stat-sub { font-size: 10px; color: #94a3b8; font-weight: 500; }
.ec-sal-calc-msg {
  margin-top: 10px; font-size: 12px; font-weight: 600; display: flex; align-items: center; gap: 6px;
}
.ec-sal-calc-msg.loading { color: #2563eb; }
.ec-sal-calc-msg.loading svg { animation: ecSpin .8s linear infinite; }
.ec-sal-calc-msg.err { color: #b91c1c; }
.ec-sal-breakdown-btn {
  margin-top: 10px; display: inline-flex; align-items: center; gap: 6px;
  font-size: 12.5px; font-weight: 700; color: #2563eb;
  background: none; border: none; cursor: pointer; padding: 6px 0;
  transition: color .15s ease;
}
.ec-sal-breakdown-btn:hover { color: #1d4ed8; text-decoration: underline; }
.ec-sal-breakdown-btn:disabled { color: #94a3b8; cursor: not-allowed; }

/* ── SALARY BREAKDOWN MODAL ─── */
.ec-sal-modal-overlay {
  position: fixed; inset: 0; z-index: 9999;
  background: rgba(15,23,42,.45); backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  animation: ecOverlayIn .2s ease;
}
.ec-sal-modal {
  background: #fff; border-radius: 16px; width: 560px; max-width: 92vw;
  max-height: 85vh; display: flex; flex-direction: column;
  box-shadow: 0 24px 48px rgba(0,0,0,.15);
  animation: ecModalIn .25s ease;
}
.ec-sal-modal-hd {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 22px 14px; border-bottom: 1px solid #e2e8f0;
}
.ec-sal-modal-title { font-size: 16px; font-weight: 800; color: #0f172a; }
.ec-sal-modal-close {
  width: 32px; height: 32px; border-radius: 8px; border: none;
  background: #f1f5f9; display: flex; align-items: center; justify-content: center;
  cursor: pointer; color: #475569; transition: all .15s ease;
}
.ec-sal-modal-close:hover { background: #e2e8f0; color: #1e293b; }
.ec-sal-modal-body { flex: 1; overflow-y: auto; padding: 18px 22px 22px; }
.ec-sal-modal-section { margin-bottom: 18px; }
.ec-sal-modal-section:last-child { margin-bottom: 0; }
.ec-sal-modal-sec-hd {
  font-size: 11px; font-weight: 800; color: #475569; text-transform: uppercase;
  letter-spacing: .5px; margin-bottom: 10px; padding-bottom: 6px;
  border-bottom: 1px solid #f1f5f9;
}
.ec-sal-modal-sec-hd.earn { color: #15803d; }
.ec-sal-modal-sec-hd.ded { color: #b91c1c; }
.ec-sal-modal-row {
  display: flex; align-items: center; justify-content: space-between;
  padding: 7px 0; font-size: 13px;
}
.ec-sal-modal-row + .ec-sal-modal-row { border-top: 1px solid #f8fafc; }
.ec-sal-modal-row-label { color: #475569; font-weight: 500; }
.ec-sal-modal-row-val { font-weight: 700; color: #1e293b; font-variant-numeric: tabular-nums; }
.ec-sal-modal-row.total {
  border-top: 1.5px solid #e2e8f0; margin-top: 4px; padding-top: 10px;
  font-weight: 800; font-size: 13.5px;
}
.ec-sal-modal-row.total .ec-sal-modal-row-label { color: #1e293b; }
.ec-sal-modal-row.net {
  background: #eff6ff; margin: 10px -22px 0; padding: 14px 22px;
  border-radius: 0 0 16px 16px; border-top: 1.5px solid #bfdbfe;
  font-size: 15px; font-weight: 800; color: #1e40af;
}
.ec-sal-modal-row.net .ec-sal-modal-row-val { color: #1e40af; }
.ec-acct-eye {
  position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
  display: inline-flex; align-items: center; justify-content: center;
  width: 28px; height: 28px; border: 0; border-radius: 8px;
  background: transparent; color: #64748b; cursor: pointer; padding: 0;
}
.ec-acct-eye:hover { color: #1e40af; background: #eff6ff; }
.ec-inp.has-eye { padding-right: 40px; letter-spacing: 0.04em; }
.ec-ifsc-status {
  margin-top: 6px; font-size: 12px; font-weight: 600; line-height: 1.35;
}
.ec-ifsc-status.ok { color: #15803d; }
.ec-ifsc-status.err { color: #b91c1c; }
.ec-ifsc-status.loading { color: #2563eb; display: inline-flex; align-items: center; gap: 6px; }
.ec-ifsc-status.loading svg { animation: ecSpin .8s linear infinite; }
@keyframes ecSpin { to { transform: rotate(360deg); } }
.ec-field-locked .ec-sel,
.ec-field-locked .ec-inp,
.ec-sel.locked,
.ec-inp.locked {
  background: #f8fafc !important; color: #475569 !important;
  border-color: #e2e8f0 !important; cursor: not-allowed;
  font-weight: 600; pointer-events: none;
}
.ec-field-locked .ec-sel-arr,
.ec-sel.locked + .ec-sel-arr { color: #cbd5e1; }
.ec-locked-tag {
  display: inline-flex; align-items: center; gap: 4px;
  margin-left: 6px; padding: 2px 8px; border-radius: 20px;
  font-size: 9.5px; font-weight: 800; text-transform: uppercase;
  letter-spacing: .5px; color: #64748b; background: #f1f5f9;
  vertical-align: middle;
}

/* ── RESPONSIVE ─── */
@media (max-width:768px) {
  .ec-page { padding: 14px 12px 24px; }
  .ec-hero { padding: 22px 20px; border-radius: 16px 16px 0 0; }
  .ec-hero-title { font-size: 20px; }
  .ec-body { padding: 22px 18px; }
  .ec-nav { padding: 14px 18px; }
  .ec-g2, .ec-g3, .ec-org-grid, .ec-comp-grid { grid-template-columns: 1fr; }
  .ec-org-role { grid-column: span 1; }
  .ec-org-group { padding: 18px 16px; }
  .ec-step-sub { display: none; }
  .ec-step-item { padding: 14px 8px; gap: 7px; }
  .ec-success-card { padding: 36px 24px; }
  .ec-step-lbl { font-size: 11px; }
}
/* Full-page layout sets 3 columns with .ec-page--full (higher specificity),
   so the narrow-screen overrides must use the same prefix to take effect. */
@media (max-width:900px) {
  .ec-page--full .ec-org-grid,
  .ec-page--full .ec-comp-grid,
  .ec-sal-top { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .ec-sal-summary { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width:600px) {
  .ec-page--full .ec-g2,
  .ec-page--full .ec-g3,
  .ec-page--full .ec-org-grid,
  .ec-page--full .ec-comp-grid,
  .ec-sal-top { grid-template-columns: minmax(0, 1fr); gap: 12px; }
  .ec-page--full .ec-org-role { grid-column: auto; }
  .ec-page--full .ec-org-wrap { padding: 12px; }
  .ec-page--full .ec-body { padding: 12px; }
  .ec-quick-add-row { flex-wrap: wrap; }
  .ec-quick-add-row .ec-inp { flex: 1 1 100%; }

  /* Step bar: all 5 steps fit — circles for every step, label only on the
     current one (each button keeps its full aria-label). */
  .ec-step-bar,
  .ec-page--full .ec-step-bar { overflow-x: auto; scrollbar-width: none; }
  .ec-step-bar::-webkit-scrollbar { display: none; }
  .ec-step-item,
  .ec-page--full .ec-step-item { flex: 0 0 auto; justify-content: center; padding: 10px 8px; gap: 6px; }
  .ec-step-item.active-step,
  .ec-page--full .ec-step-item.active-step { flex: 1 1 auto; justify-content: flex-start; min-width: 0; }
  .ec-step-item:not(.active-step) .ec-step-lbl { display: none; }
  .ec-step-item.active-step .ec-step-lbl { white-space: normal; font-size: 12.5px; }

  /* Salary input method: stack the two cards. */
  .ec-sal-toggle { flex-direction: column; }
  .ec-sal-summary { gap: 8px; }
  .ec-sal-stat { padding: 10px 12px; }

  /* Bank card header + bottom navigation wrap instead of overflowing. */
  .ec-bank-head,
  .ec-page--full .ec-bank-head { flex-wrap: wrap; gap: 8px; }
  .ec-nav,
  .ec-page--full .ec-nav { flex-wrap: wrap; gap: 8px 10px; padding: 12px; }
  .ec-nav .ec-step-counter { order: -1; flex-basis: 100%; text-align: center; font-size: 12px; }
  .ec-nav .ec-nav-actions { margin-left: auto; gap: 8px; }
  .ec-nav .ec-btn-back-nav,
  .ec-nav .ec-btn-skip,
  .ec-nav .ec-btn-next { min-height: 44px; padding: 10px 14px; }
}
@media (max-width:480px) {
  .ec-emp-card, .ec-emp-placeholder { display: none; }
  .ec-hero-inner { justify-content: flex-start; }
}
`;

/* ═══════════════════════════════════════════════════════
   STEP CONFIG (after EI — see below)
═══════════════════════════════════════════════════════ */

/* ═══════════════════════════════════════════════════════
   SVG ICONS
═══════════════════════════════════════════════════════ */
const EI = {
  User: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
    </svg>
  ),
  Mail: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
      <polyline points="22,6 12,13 2,6"/>
    </svg>
  ),
  Phone: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.17 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.11 1h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 8.1a16 16 0 0 0 6 6l.38-.37a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 21 16.92z"/>
    </svg>
  ),
  Hash: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/>
      <line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/>
    </svg>
  ),
  Briefcase: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
    </svg>
  ),
  Building: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
      <polyline points="9 22 9 12 15 12 15 22"/>
    </svg>
  ),
  MapPin: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
    </svg>
  ),
  Users: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  Shield: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  ),
  Dollar: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23"/>
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
    </svg>
  ),
  Rupee: () => (
    <span style={{ fontSize: 15, fontWeight: 800, lineHeight: 1, fontFamily: "inherit" }}>₹</span>
  ),
  Clock: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
    </svg>
  ),
  Activity: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
    </svg>
  ),
  Bank: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/>
    </svg>
  ),
  CreditCard: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>
    </svg>
  ),
  Eye: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
    </svg>
  ),
  EyeOff: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  ),
  Loader: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
    </svg>
  ),
  ArrowLeft: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
    </svg>
  ),
  ArrowRight: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
    </svg>
  ),
  Alert: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
  ),
  Check: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
  CheckSm: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
  Plus: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  ),
  Info: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="8"/><line x1="12" y1="12" x2="12" y2="16"/>
    </svg>
  ),
  Globe: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
    </svg>
  ),
  Pencil: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  ),
  PlusCircle: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>
    </svg>
  ),
  AlertTriangle: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
      <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  ),
  CircleCheck: () => (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
    </svg>
  ),
  IdCard: () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="5" width="20" height="14" rx="2"/><circle cx="8" cy="12" r="2"/><path d="M14 10h4M14 14h4"/>
    </svg>
  ),
  FileText: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/>
      <line x1="16" y1="17" x2="8" y2="17"/>
      <line x1="10" y1="9" x2="8" y2="9"/>
    </svg>
  ),
  X: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  PencilLg: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  ),
  CheckLg: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
  Frown: () => (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><path d="M16 16s-1.5-2-4-2-4 2-4 2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/>
    </svg>
  ),
};

const STEPS = [
  { label: "Basic Details",  sub: "Personal info",    Icon: EI.User },
  { label: "Employment",     sub: "Team & access",     Icon: EI.Building },
  { label: "Compensation",   sub: "Salary details",    Icon: EI.Dollar },
  { label: "Banking",        sub: "Payment details",   Icon: EI.Bank },
  { label: "Statutory & Tax", sub: "UAN / PAN / ESIC", Icon: EI.Bank },
];

/* ═══════════════════════════════════════════════════════
   HELPERS (unchanged)
═══════════════════════════════════════════════════════ */
function toDateInput(val) {
  if (!val) return "";
  return String(val).slice(0, 10);
}

function todayIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Default Date of Joining: 1st of the current calendar month (local). */
function firstOfCurrentMonthIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}-01`;
}

function mapEmployeeToForm(emp) {
  const id = emp.employee_id || emp.id;
  const employeeName = [emp.first_name, emp.middle_name, emp.last_name].filter(Boolean).join(" ");
  return {
    employee_code:        emp.employee_code || "",
    employee_type:        emp.employee_type || DEFAULT_EMPLOYEE_TYPE,
    role:                 emp.role || DEFAULT_EMPLOYEE_ROLE,
    employee_name:        employeeName,
    first_name:           emp.first_name || "",
    middle_name:          emp.middle_name || "",
    last_name:            emp.last_name || "",
    display_name:         emp.display_name || "",
    work_email:           emp.work_email || emp.email || "",
    mobile_phone:         emp.mobile_phone || emp.phone || "",
    gender:               emp.gender || "",
    date_of_birth:        toDateInput(emp.date_of_birth),
    marital_status:       emp.marital_status || "",
    fathers_name:         emp.fathers_name || "",
    date_of_joining:      toDateInput(emp.date_of_joining),
    date_of_leaving:      toDateInput(emp.date_of_leaving),
    status:               emp.status || "active",
    department_id:        emp.department_id ? String(emp.department_id) : "",
    designation_id:       emp.designation_id ? String(emp.designation_id) : "",
    location_id:          emp.location_id ? String(emp.location_id) : "",
    business_unit:        emp.business_unit || "",
    pay_structure_id:     emp.pay_structure_id ? String(emp.pay_structure_id) : "",
    salary_template_id:   "",
    annual_ctc:           emp.annual_ctc != null && emp.annual_ctc !== "" ? String(emp.annual_ctc) : "",
    pay_frequency:        emp.pay_frequency || "Monthly",
    uan_link_status:      emp.uan_link_status || "Unlinked",
    is_active:            emp.is_active === false ? "false" : "true",
    _created_employee_id: id ? String(id) : "",
  };
}

/** Split a single "Employee Name" into API first/middle/last fields. */
function splitEmployeeName(full) {
  const parts = String(full || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return { first_name: "", middle_name: "", last_name: "" };
  if (parts.length === 1) return { first_name: parts[0], middle_name: "", last_name: "" };
  if (parts.length === 2) return { first_name: parts[0], middle_name: "", last_name: parts[1] };
  return {
    first_name: parts[0],
    middle_name: parts.slice(1, -1).join(" "),
    last_name: parts[parts.length - 1],
  };
}

function getInitialsFromName(full = "") {
  const parts = String(full || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
}

function queueEmployeeName(emp) {
  if (!emp) return "";
  const named = [emp.first_name, emp.middle_name, emp.last_name].filter(Boolean).join(" ").trim();
  return String(emp.display_name || named || emp.employee_code || "Employee").trim();
}

function queueEmployeeId(emp) {
  return String(emp?.employee_id || emp?.id || "");
}

/* ═══════════════════════════════════════════════════════
   REACT-SELECT STYLES
═══════════════════════════════════════════════════════ */
const ecSelectPortalProps = {
  menuPortalTarget: typeof document !== "undefined" ? document.body : null,
  styles: { menuPortal: (b) => ({ ...b, zIndex: 9999 }) },
};

/* ═══════════════════════════════════════════════════════
   STEP BAR
═══════════════════════════════════════════════════════ */
function StepBar({ current, onSelect }) {
  return (
    <div className="ec-step-bar" role="navigation" aria-label="Employee form steps">
      {STEPS.map((s, i) => {
        const num = i + 1;
        const isDone   = current > num;
        const isActive = current === num;
        const state    = isDone ? "done" : isActive ? "active" : "pending";
        return (
          <button
            key={num}
            type="button"
            className={`ec-step-item${isActive ? " active-step" : ""}`}
            onClick={() => onSelect?.(num)}
            aria-current={isActive ? "step" : undefined}
            aria-label={`${s.label}, step ${num} of ${STEPS.length}`}
          >
            <div className={`ec-step-num ${state}`}>
              {isDone ? <EI.CheckSm /> : num}
            </div>
            <div>
              <div className={`ec-step-lbl ${state}`}>{s.label}</div>
              <div className={`ec-step-sub${isActive ? " active" : ""}`}>{s.sub}</div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   HERO BANNER
═══════════════════════════════════════════════════════ */
// eslint-disable-next-line no-unused-vars
function HeroBanner({ step, form, isEdit }) {
  const s        = STEPS[step - 1];
  const fullName = (form.employee_name || [form.first_name, form.last_name].filter(Boolean).join(" ")).trim();
  const initials = getInitialsFromName(fullName);

  return (
    <div className="ec-hero">
      <div className="ec-hero-inner">
        <div className="ec-hero-left">
          <div className="ec-hero-badge">
            <span className="ec-hero-badge-ico"><s.Icon /></span>
            Step {step} of 5
          </div>
          <div className="ec-hero-title">
            {isEdit ? "Edit Employee" : "New Employee"}
          </div>
          <div className="ec-hero-sub">
            {s.label} — {s.sub}
          </div>
        </div>

        {fullName ? (
          <div className="ec-emp-card">
            <div className="ec-emp-ava">{initials}</div>
            <div>
              <div className="ec-emp-name">{fullName}</div>
              {form.employee_code && (
                <div className="ec-emp-code">#{form.employee_code}</div>
              )}
              <div className="ec-emp-mode">
                {isEdit ? (
                  <><EI.Pencil /> Editing</>
                ) : (
                  <><EI.PlusCircle /> Creating</>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="ec-emp-placeholder">
            <div className="ec-emp-placeholder-ico"><EI.User /></div>
            <div className="ec-emp-placeholder-txt">
              Enter a name to see live preview
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   FIELD WRAPPER
═══════════════════════════════════════════════════════ */
function Field({ label, required, error, hint, green, children, fullWidth, noIcon, tooltip }) {
  return (
    <div className="ec-field" style={fullWidth ? { gridColumn: "1 / -1" } : undefined}>
      {label && (
        <label className="ec-flbl">
          {label}
          {required && <span className="ec-flbl-req">*</span>}
          {tooltip ? (
            <span
              className="ec-ftip"
              data-tip={tooltip}
              tabIndex={0}
              role="img"
              aria-label={tooltip}
            >
              <EI.Info />
            </span>
          ) : null}
        </label>
      )}
      {noIcon ? children : (
        <div className="ec-iwrap">{children}</div>
      )}
      {error && <div className="ec-ferr"><EI.Alert /> {error}</div>}
      {!error && green && <div className="ec-fgreen"><EI.CheckSm /> {green}</div>}
      {!error && !green && hint && <div className="ec-fhint">{hint}</div>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   STEP 1 — BASIC DETAILS
═══════════════════════════════════════════════════════ */
function Step1BasicDetails({
  form,
  handleChange,
  handleDobChange,
  errors,
  codeLoading,
  isEdit,
  codePrefix,
  onPrefixChange,
  codeNumber,
  onNumberChange,
  codeAuto,
  onCodeAutoChange,
  codeUsePrefix,
  onUsePrefixChange,
  onSaveOrgDefault,
  savingOrgDefault,
}) {
  const codeHint = isEdit
    ? "Code cannot be changed"
    : codeAuto
      ? (codeUsePrefix
        ? "Auto increment is on. Change the prefix if needed, or switch to manual to type a number such as 1001."
        : "Auto increment is on. This organisation uses numbers only — no letter prefix.")
      : (codeUsePrefix
        ? "Type the prefix and number yourself (e.g. RRR 1001 or AAA 0111)."
        : "Type the number only (e.g. 0001, 1001, or 10001).");

  return (
    <div>
      <div className="ec-sec-hd">
        <div className="ec-sec-title">Personal Information</div>
        <div className="ec-sec-sub">Fill in the employee's core identity and contact details</div>
      </div>

      <div className="ec-g3">
        {/* Employee Code */}
        <Field
          label="Employee Code"
          hint={codeHint}
          error={errors.employee_code}
          fullWidth
          noIcon
        >
          {isEdit ? (
            <input readOnly className="ec-inp ro no-ico" value={form.employee_code} />
          ) : (
            <>
              <div className="ec-code-choice">
                <div className="ec-code-mode" role="group" aria-label="Employee code mode">
                  <button
                    type="button"
                    className={`ec-code-mode-btn${codeAuto ? " is-active" : ""}`}
                    onClick={() => onCodeAutoChange(true)}
                  >
                    Continue with auto increment
                  </button>
                  <button
                    type="button"
                    className={`ec-code-mode-btn${!codeAuto ? " is-active" : ""}`}
                    onClick={() => onCodeAutoChange(false)}
                  >
                    Set code manually
                  </button>
                </div>
                <label className="ec-code-prefix-toggle">
                  <input
                    type="checkbox"
                    checked={Boolean(codeUsePrefix)}
                    onChange={(e) => onUsePrefixChange(e.target.checked)}
                  />
                  Use letter prefix (EMP, AAA…). Turn off for numbers only.
                </label>
                {onSaveOrgDefault && (
                  <button
                    type="button"
                    className="ec-code-save-org"
                    onClick={onSaveOrgDefault}
                    disabled={savingOrgDefault}
                  >
                    {savingOrgDefault ? "Saving organisation default…" : "Save this as the default for this organisation"}
                  </button>
                )}
              </div>
              <div className={`ec-code-split${codeUsePrefix ? "" : " ec-code-split--num-only"}`}>
                {codeUsePrefix && (
                  <>
                    <input
                      className="ec-inp no-ico ec-code-prefix"
                      placeholder="EMP"
                      maxLength={4}
                      value={codePrefix}
                      onChange={(e) => onPrefixChange(e.target.value)}
                      aria-label="Employee code prefix"
                    />
                    <span className="ec-code-sep" aria-hidden>-</span>
                  </>
                )}
                <input
                  readOnly={codeAuto}
                  className={`ec-inp no-ico ec-code-num${codeAuto ? "" : " is-editable"}`}
                  placeholder={codeLoading ? "…" : "1001"}
                  maxLength={9}
                  inputMode="numeric"
                  value={codeLoading && codeAuto ? "" : codeNumber}
                  onChange={(e) => onNumberChange?.(e.target.value)}
                  aria-label="Employee code number"
                />
              </div>
              {codeUsePrefix && codePrefix.length === 1 && (
                <div className="ec-fhint" style={{ marginTop: 6 }}>
                  Prefix can be 1–4 letters (e.g. A, BA, EMP, AAAA), or turn prefix off for numbers only.
                </div>
              )}
              {!codeLoading && form.employee_code && (!codeUsePrefix || codePrefix.length >= 1) && (
                <div className="ec-code-next">
                  <EI.CheckSm />
                  {codeAuto ? "Next code:" : "Code will be:"} <strong>{form.employee_code}</strong>
                </div>
              )}
            </>
          )}
        </Field>

        {/* Row 1: Employee Name | Display Name */}
        <Field label="Employee Name" required error={errors.employee_name}>
          <span className="ec-iico"><EI.User /></span>
          <input
            className={`ec-inp${errors.employee_name ? " err" : ""}`}
            name="employee_name"
            placeholder="Enter full employee name"
            value={form.employee_name}
            onChange={handleChange}
            autoComplete="name"
          />
        </Field>

        <Field
          label="Display Name"
          required
          error={errors.display_name}
          hint="Required. Auto-fills from Employee Name and is shown on payslips."
        >
          <span className="ec-iico"><EI.User /></span>
          <input
            className={`ec-inp${errors.display_name ? " err" : ""}`}
            name="display_name"
            placeholder="Same as employee name"
            value={form.display_name}
            onChange={handleChange}
          />
        </Field>

        {/* Row 2: Work Email | Mobile Phone | Gender */}
        <Field label="Work Email" required error={errors.work_email}>
          <span className="ec-iico"><EI.Mail /></span>
          <input
            className={`ec-inp${errors.work_email ? " err" : ""}`}
            type="email" name="work_email" placeholder="name@company.com"
            value={form.work_email} onChange={handleChange}
          />
        </Field>

        <Field
          label="Mobile Phone"
          required
          error={errors.mobile_phone}
          noIcon
        >
          <div style={{ display: "flex", gap: 0, width: "100%" }}>
            <div
              style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "0 10px", height: 46,
                background: "#f1f5f9", border: "1px solid #cbd5e1",
                borderRight: "none", borderRadius: "10px 0 0 10px",
                fontSize: 14, fontWeight: 600, color: "#334155",
                whiteSpace: "nowrap", userSelect: "none",
              }}
            >
              <EI.Phone style={{ color: "#60a5fa", width: 16, height: 16 }} />
              +91
            </div>
            <input
              className={`ec-inp${errors.mobile_phone ? " err" : ""}`}
              style={{ borderRadius: "0 10px 10px 0", paddingLeft: 12 }}
              name="mobile_phone"
              placeholder="9876543210"
              value={form.mobile_phone}
              onChange={handleChange}
              inputMode="numeric"
              autoComplete="tel"
              maxLength={10}
              pattern="[0-9]*"
            />
          </div>
        </Field>

        <Field label="Gender" required error={errors.gender}>
          <span className="ec-iico"><EI.User /></span>
          <select
            className={`ec-sel${errors.gender ? " err" : ""}`}
            name="gender" value={form.gender} onChange={handleChange}
          >
            <option value="">Select gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
            <option value="Prefer not to say">Prefer not to say</option>
          </select>
          <span className="ec-sel-arr">▾</span>
        </Field>

        {/* Row 3: Date of Birth | Marital Status | Father's Name */}
        <Field
          label="Date of Birth"
          required
          error={errors.date_of_birth}
          noIcon
        >
          <DatePickerField
            hideLabel birthdate compact
            value={form.date_of_birth}
            onChange={handleDobChange}
            error={errors.date_of_birth}
            inputClassName={`ec-inp ec-inp-no-icon${errors.date_of_birth ? " err" : ""}`}
            placeholder="Select date of birth"
          />
        </Field>

        <Field label="Marital Status">
          <span className="ec-iico"><EI.Globe /></span>
          <select
            className="ec-sel" name="marital_status"
            value={form.marital_status} onChange={handleChange}
          >
            <option value="">Select status</option>
            <option value="Single">Single</option>
            <option value="Married">Married</option>
            <option value="Divorced">Divorced</option>
            <option value="Widowed">Widowed</option>
          </select>
          <span className="ec-sel-arr">▾</span>
        </Field>

        <Field label="Father's Name">
          <span className="ec-iico"><EI.User /></span>
          <input
            className="ec-inp" name="fathers_name" placeholder="Enter father's name"
            value={form.fathers_name} onChange={handleChange}
          />
        </Field>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   STEP 2 — EMPLOYMENT
═══════════════════════════════════════════════════════ */
function Step2Employment({
  form,
  handleChange,
  errors,
  departments,
  designations,
  locations,
  loading,
  dropdownError,
  onRefreshOrg,
  onOrgCreated,
  employmentTypes,
  reportingManagers,
  showReportingManager = true,
  children,
}) {
  const [quickAdd, setQuickAdd] = useState(null);
  const [quickDraft, setQuickDraft] = useState("");
  const [quickSaving, setQuickSaving] = useState(false);
  const [quickErr, setQuickErr] = useState("");

  const quickMeta = {
    department: { label: "Department", field: "department_id", post: "/setup/departments", listKey: "departments" },
    designation: { label: "Designation", field: "designation_id", post: "/setup/designations", listKey: "designations" },
    location: { label: "Location", field: "location_id", post: "/setup/locations", listKey: "locations" },
  };

  const saveQuickAdd = async (kind) => {
    const meta = quickMeta[kind];
    const name = quickDraft.trim();
    if (!name) return;
    setQuickSaving(true);
    setQuickErr("");
    try {
      const res = await API.post(meta.post, { name });
      await onRefreshOrg?.();
      onOrgCreated?.();
      handleChange({ target: { name: meta.field, value: String(res.data.id) } });
      setQuickAdd(null);
      setQuickDraft("");
    } catch (e) {
      setQuickErr(e?.response?.data?.detail || "Could not save. Try again.");
    } finally {
      setQuickSaving(false);
    }
  };

  if (loading) {
    return <BrixigoPageLoader message="Loading employment data…" />;
  }

  const deptOptions  = departments.map((d)  => ({ value: String(d.id), label: d.name }));
  const desigOptions = designations.map((d) => ({ value: String(d.id), label: d.name }));
  const locOptions   = locations.map((l)    => ({ value: String(l.id), label: l.name }));

  const handleSel = (name, sel) =>
    handleChange({ target: { name, value: sel ? String(sel.value) : "" } });

  const renderOrgSelect = (kind, options, fieldName, errorKey, required) => {
    const meta = quickMeta[kind];
    return (
      <Field label={meta.label} required={required} error={errors[errorKey]} noIcon>
        <div className="ec-rs">
          <Select
            {...ecSelectPortalProps}
            options={options}
            value={options.find((o) => String(o.value) === String(form[fieldName])) || null}
            onChange={(s) => handleSel(fieldName, s)}
            placeholder={`Select ${meta.label}`}
            isClearable
          />
        </div>
        {options.length === 0 && (
          <div className="ec-fhint" style={{ color: "#b45309" }}>
            No {meta.label.toLowerCase()} yet — add one below or complete{" "}
            <Link to="/Setup" style={{ color: "#1e40af", fontWeight: 600 }}>Initial Setup</Link>.
          </div>
        )}
        {quickAdd !== kind ? (
          <button
            type="button"
            className="ec-quick-add-btn"
            onClick={() => { setQuickAdd(kind); setQuickDraft(""); setQuickErr(""); }}
          >
            + Add {meta.label}
          </button>
        ) : (
          <div className="ec-quick-add-row">
            <input
              className="ec-inp"
              placeholder={`New ${meta.label}`}
              value={quickDraft}
              onChange={(e) => setQuickDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), saveQuickAdd(kind))}
              autoFocus
            />
            <button type="button" className="ec-btn-next" style={{ padding: "8px 14px", fontSize: 12 }} disabled={quickSaving} onClick={() => saveQuickAdd(kind)}>
              {quickSaving ? "Saving…" : "Save"}
            </button>
            <button type="button" className="ec-btn-back" style={{ padding: "8px 10px", fontSize: 12 }} onClick={() => { setQuickAdd(null); setQuickErr(""); }}>
              Cancel
            </button>
          </div>
        )}
        {quickAdd === kind && quickErr && <div className="ec-ferr">{quickErr}</div>}
      </Field>
    );
  };

  const typeOptions = (employmentTypes || []).length > 0
    ? employmentTypes.map((t) => (typeof t === "string" ? t : t.name))
    : EMPLOYEE_TYPES;

  return (
    <div className="ec-org-wrap">
      <div className="ec-sec-hd">
        <div className="ec-sec-title">Employment Details</div>
        <div className="ec-sec-sub">Employment type, joining date, department, designation, location and system access</div>
      </div>

      <div className="ec-org-grid">
        <Field label="Employment Type" required error={errors.employee_type}>
          <span className="ec-iico"><EI.Briefcase /></span>
          <select
            className={`ec-sel${errors.employee_type ? " err" : ""}`}
            name="employee_type"
            value={form.employee_type || DEFAULT_EMPLOYEE_TYPE}
            onChange={handleChange}
          >
            {typeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <span className="ec-sel-arr">▾</span>
        </Field>

        <Field
          label="Date of Joining"
          required
          error={errors.date_of_joining}
          hint="Defaults to the 1st of this month — change if needed"
          noIcon
        >
          <DatePickerField
            hideLabel
            compact
            value={form.date_of_joining}
            onChange={(d) => handleChange({ target: { name: "date_of_joining", value: d } })}
            inputClassName="ec-inp ec-inp-no-icon"
          />
        </Field>

        <Field label="Employee Status">
          <span className="ec-iico"><EI.Activity /></span>
          <select className="ec-sel" name="status" value={form.status} onChange={handleChange}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="on_leave">On Leave</option>
            <option value="terminated">Terminated</option>
          </select>
          <span className="ec-sel-arr">▾</span>
        </Field>
      </div>

      {dropdownError && (
        <GuidedNotice
          domain="employee"
          message={dropdownError}
          className="mb-4"
        />
      )}

      <div className="ec-org-grid" style={{ marginTop: 16 }}>
        {renderOrgSelect("department", deptOptions, "department_id", "department_id", true)}
        {renderOrgSelect("designation", desigOptions, "designation_id", "designation_id", true)}
        {renderOrgSelect("location", locOptions, "location_id", "location_id", true)}
        {/* Business Unit commented out — replaced by Reporting Manager */}
        {/* Add only — in edit mode ReportingInfoSection (below) changes the manager directly. */}
        {showReportingManager ? (
          <Field
            label="Reporting Manager"
            hint={
              (reportingManagers || []).length === 0
                ? "No managers yet — you can assign one later in People Hierarchy."
                : undefined
            }
          >
            <span className="ec-iico"><EI.User /></span>
            <select
              className="ec-sel"
              name="manager_id"
              value={form.manager_id || ""}
              onChange={handleChange}
            >
              <option value="">Not assigned (set later in People Hierarchy)</option>
              {(reportingManagers || []).length > 0 ? (
                <optgroup label="Managers">
                  {reportingManagers.map((m) => (
                    <option key={m.employee_id} value={m.employee_id}>
                      {m.employee_name}{m.employee_code ? ` (${m.employee_code})` : ""} — {formatRoleLabel(m.role)}
                    </option>
                  ))}
                </optgroup>
              ) : null}
            </select>
            <span className="ec-sel-arr">▾</span>
          </Field>
        ) : null}
        <div className="ec-org-role">
          <Field label="System Access Role" required error={errors.role} noIcon>
            <div className="ec-role-highlight">
              <div className="ec-iwrap">
                <span className="ec-iico"><EI.Shield /></span>
                <select
                  className={`ec-sel${errors.role ? " err" : ""}`}
                  name="role"
                  value={form.role || DEFAULT_EMPLOYEE_ROLE}
                  onChange={handleChange}
                >
                  {SYSTEM_ROLES.map((r) => (
                    <option key={r.value} value={r.value}>{r.label} — {r.description}</option>
                  ))}
                </select>
                <span className="ec-sel-arr">▾</span>
              </div>
            </div>
          </Field>
        </div>
      </div>

      {children}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   STEP 3 — COMPENSATION (single card)
═══════════════════════════════════════════════════════ */
function Step3Compensation({ form, handleChange, setForm, errors, salaryTemplates = [] }) {
  const [inputMethod, setInputMethod] = useState("annual_ctc");
  const [salaryAmount, setSalaryAmount] = useState("");
  const [breakdown, setBreakdown] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [calcError, setCalcError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [payoutMode, setPayoutMode] = useState("with_deductions");
  const calcTimerRef = useRef(null);
  const fullPay = payoutMode === "full_pay";

  const showTemplateField = salaryTemplates.length > 0;
  const defaultTpl = salaryTemplates.find((t) => t.is_default) || (salaryTemplates.length === 1 ? salaryTemplates[0] : null);
  const templateRequired = salaryTemplates.length >= 2 && !defaultTpl;

  const resolvedTemplateId = String(
    form.salary_template_id
    || (salaryTemplates.length === 1 ? salaryTemplates[0]?.template_id : "")
    || ""
  ).trim();

  useEffect(() => {
    let cancelled = false;
    API.get("/organisation/me/hr-settings")
      .then((res) => {
        if (cancelled) return;
        const hr = res.data?.hr_settings || {};
        const payroll = res.data?.payroll_settings || hr.payroll || {};
        setPayoutMode(normalizeSalaryPayoutMode(payroll.salary_payout_mode || hr.salary_payout_mode));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (form.annual_ctc && !salaryAmount) {
      const ctc = parseFloat(form.annual_ctc);
      if (!isNaN(ctc) && ctc > 0) {
        if (inputMethod === "annual_ctc") {
          setSalaryAmount(String(form.annual_ctc));
        } else {
          setSalaryAmount(String(Math.round(ctc / 12)));
        }
      }
    }
    // Mount-only seed from the saved annual CTC.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (calcTimerRef.current) clearTimeout(calcTimerRef.current);
    setBreakdown(null);
    setCalcError("");

    if (!resolvedTemplateId || !salaryAmount) return;

    const amt = parseFloat(salaryAmount);
    if (isNaN(amt) || amt <= 0) return;

    const annualCtc = inputMethod === "annual_ctc" ? amt : Math.round(amt * 12);

    setCalculating(true);
    calcTimerRef.current = setTimeout(async () => {
      try {
        const result = await calculateTemplateSalary(resolvedTemplateId, annualCtc);
        console.log("SALARY_DEBUG", JSON.stringify(result._debug, null, 2));
        setBreakdown(result.salary_breakdown);
        setCalcError("");
        if (result.salary_payout_mode) {
          setPayoutMode(normalizeSalaryPayoutMode(result.salary_payout_mode));
        }
        setForm((prev) => ({ ...prev, annual_ctc: String(annualCtc) }));
      } catch (err) {
        setCalcError(err?.detail || err?.message || "Failed to calculate salary");
        setBreakdown(null);
      } finally {
        setCalculating(false);
      }
    }, 600);

    return () => { if (calcTimerRef.current) clearTimeout(calcTimerRef.current); };
    // setForm is a parent state setter and must not retrigger this debounce.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedTemplateId, salaryAmount, inputMethod]);

  const handleMethodChange = (method) => {
    if (method === inputMethod) return;
    const prevAmt = parseFloat(salaryAmount);
    setInputMethod(method);
    if (!isNaN(prevAmt) && prevAmt > 0) {
      if (method === "monthly_gross") {
        const monthly = Math.round(prevAmt / 12);
        setSalaryAmount(String(monthly));
        setForm((prev) => ({ ...prev, annual_ctc: String(Math.round(monthly * 12)) }));
      } else {
        const annual = Math.round(prevAmt * 12);
        setSalaryAmount(String(annual));
        setForm((prev) => ({ ...prev, annual_ctc: String(annual) }));
      }
    }
  };

  const handleAmountChange = (e) => {
    const val = e.target.value.replace(/[^0-9]/g, "");
    setSalaryAmount(val);
    const num = parseFloat(val);
    if (!isNaN(num) && num > 0) {
      const ctc = inputMethod === "annual_ctc" ? num : Math.round(num * 12);
      setForm((prev) => ({ ...prev, annual_ctc: String(ctc) }));
    } else {
      setForm((prev) => ({ ...prev, annual_ctc: "" }));
    }
  };

  const fmtCurrency = (n) => {
    if (n == null || isNaN(n)) return "₹0";
    return "₹" + Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });
  };

  const effectiveFrom = form.date_of_joining || firstOfCurrentMonthIso();
  const shownBreakdown = breakdown && fullPay
    ? {
        ...breakdown,
        deductions: {},
        total_deductions: 0,
        net_salary: breakdown.gross_salary,
      }
    : breakdown;
  const lpa = shownBreakdown
    ? ((inputMethod === "annual_ctc" ? parseFloat(salaryAmount) : parseFloat(salaryAmount) * 12) / 100000).toFixed(2)
    : form.annual_ctc && !isNaN(parseFloat(form.annual_ctc))
      ? (parseFloat(form.annual_ctc) / 100000).toFixed(2)
      : null;

  return (
    <div className="ec-comp-wrap">
      <div className="ec-sec-hd">
        <div className="ec-sec-title">Compensation</div>
        <div className="ec-sec-sub">Salary structure, input method and calculated breakdown</div>
      </div>

      <div className="ec-org-group">
        <div className="ec-org-group-hd">
          <div className="ec-org-group-icon team"><EI.Dollar /></div>
          <div className="ec-org-group-title">Salary Configuration</div>
        </div>

        <div className="ec-sal-top">
          <Field label="Salary Effective From" required>
            <span className="ec-iico"><EI.Clock /></span>
            <input
              className="ec-inp locked"
              type="date"
              value={effectiveFrom}
              disabled
              tabIndex={-1}
            />
          </Field>

          <Field label={<>Pay Frequency <span className="ec-locked-tag">Fixed</span></>}>
            <span className="ec-iico"><EI.Clock /></span>
            <select className="ec-sel locked" name="pay_frequency" value="Monthly" disabled tabIndex={-1}>
              <option value="Monthly">Monthly</option>
            </select>
            <span className="ec-sel-arr">▾</span>
          </Field>

          {showTemplateField ? (
            <Field
              label="Salary structure"
              required={templateRequired}
              error={errors.salary_template_id}
              hint={
                salaryTemplates.length === 1
                  ? "Only one template — selected automatically"
                  : defaultTpl
                    ? `Default: "${defaultTpl.name}"`
                    : "Choose the salary structure template"
              }
            >
              <span className="ec-iico"><EI.Dollar /></span>
              <select
                className={`ec-sel${errors.salary_template_id ? " err" : ""}`}
                name="salary_template_id"
                value={form.salary_template_id || ""}
                onChange={(e) => {
                  handleChange(e);
                  setBreakdown(null);
                }}
                disabled={salaryTemplates.length === 1}
              >
                {salaryTemplates.length >= 2 ? (
                  <option value="">Select salary structure</option>
                ) : null}
                {salaryTemplates.map((t) => (
                  <option key={t.template_id} value={String(t.template_id)}>
                    {t.name || "Untitled template"}{t.is_default ? " (default)" : ""}
                  </option>
                ))}
              </select>
              <span className="ec-sel-arr">▾</span>
            </Field>
          ) : null}
        </div>

        {/* ── Salary Input Method ── */}
        <div className="ec-sal-method">
          <div className="ec-sal-method-label">Salary Input Method</div>
          <div className="ec-sal-toggle">
            <div
              className={`ec-sal-card${inputMethod === "monthly_gross" ? " active" : ""}`}
              onClick={() => handleMethodChange("monthly_gross")}
              role="radio" aria-checked={inputMethod === "monthly_gross"} tabIndex={0}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleMethodChange("monthly_gross"); } }}
            >
              <div className="ec-sal-card-radio"><div className="ec-sal-card-radio-dot" /></div>
              <div className="ec-sal-card-text">
                <div className="ec-sal-card-title">Monthly Gross</div>
                <div className="ec-sal-card-sub">Enter monthly gross salary</div>
              </div>
            </div>
            <div
              className={`ec-sal-card${inputMethod === "annual_ctc" ? " active" : ""}`}
              onClick={() => handleMethodChange("annual_ctc")}
              role="radio" aria-checked={inputMethod === "annual_ctc"} tabIndex={0}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleMethodChange("annual_ctc"); } }}
            >
              <div className="ec-sal-card-radio"><div className="ec-sal-card-radio-dot" /></div>
              <div className="ec-sal-card-text">
                <div className="ec-sal-card-title">Annual CTC</div>
                <div className="ec-sal-card-sub">Enter annual cost to company</div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Salary Amount Input ── */}
        <div className="ec-sal-input-row">
          <Field
            label={inputMethod === "annual_ctc" ? "Annual CTC (₹)" : "Monthly Gross Salary (₹)"}
            required
            error={errors.annual_ctc}
          >
            <span className="ec-iico"><EI.Rupee /></span>
            <input
              className={`ec-inp${errors.annual_ctc ? " err" : ""}`}
              name="annual_ctc"
              type="text"
              inputMode="numeric"
              placeholder={inputMethod === "annual_ctc" ? "e.g. 600000" : "e.g. 50000"}
              value={salaryAmount}
              onChange={handleAmountChange}
            />
          </Field>
          {lpa && !errors.annual_ctc ? (
            <div className="ec-ctc-lpa">
              {"≈"} {"₹"}{lpa} LPA
              {inputMethod === "monthly_gross" && salaryAmount ? (
                <> &middot; Annual CTC: {fmtCurrency(parseFloat(salaryAmount) * 12)}</>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* ── Calculation Status ── */}
        {calculating ? (
          <div className="ec-sal-calc-msg loading">
            <EI.Loader /> Calculating salary breakdown...
          </div>
        ) : null}
        {calcError ? (
          <div className="ec-sal-calc-msg err">
            <EI.Alert /> {calcError}
          </div>
        ) : null}

        {/* ── Calculated Summary ── */}
        {shownBreakdown && !calculating ? (
          <>
            {fullPay ? <FullPayNotice /> : null}
            <div className={`ec-sal-summary${fullPay ? " is-full-pay" : ""}`}>
              <div className="ec-sal-stat">
                <div className="ec-sal-stat-label">Monthly Gross</div>
                <div className="ec-sal-stat-val">{fmtCurrency(shownBreakdown.gross_salary)}</div>
                <div className="ec-sal-stat-sub">per month</div>
              </div>
              {fullPay ? null : (
                <div className="ec-sal-stat">
                  <div className="ec-sal-stat-label">Deductions</div>
                  <div className="ec-sal-stat-val">{fmtCurrency(shownBreakdown.total_deductions)}</div>
                  <div className="ec-sal-stat-sub">per month</div>
                </div>
              )}
              <div className="ec-sal-stat highlight">
                <div className="ec-sal-stat-label">{fullPay ? "Salary Payable" : "Net Salary"}</div>
                <div className="ec-sal-stat-val">{fmtCurrency(shownBreakdown.net_salary)}</div>
                <div className="ec-sal-stat-sub">{fullPay ? "full amount / month" : "take-home / month"}</div>
              </div>
              <div className="ec-sal-stat">
                <div className="ec-sal-stat-label">Annual CTC</div>
                <div className="ec-sal-stat-val">{fmtCurrency(parseFloat(form.annual_ctc))}</div>
                <div className="ec-sal-stat-sub">per year</div>
              </div>
            </div>
            <button
              type="button"
              className="ec-sal-breakdown-btn"
              onClick={() => setShowModal(true)}
            >
              <EI.Eye /> View Full Salary Breakdown
            </button>
          </>
        ) : null}
      </div>

      {/* ── Salary Breakdown Modal ── */}
      {showModal && shownBreakdown ? (
        <div className="ec-sal-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="ec-sal-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ec-sal-modal-hd">
              <div className="ec-sal-modal-title">Salary Breakdown</div>
              <button type="button" className="ec-sal-modal-close" onClick={() => setShowModal(false)}>
                <EI.X />
              </button>
            </div>
            <div className="ec-sal-modal-body">
              {/* Earnings */}
              <div className="ec-sal-modal-section">
                <div className="ec-sal-modal-sec-hd earn">Earnings (Monthly)</div>
                {Object.entries(shownBreakdown.earnings || {}).map(([name, amount]) => (
                  <div key={name} className="ec-sal-modal-row">
                    <span className="ec-sal-modal-row-label">{name}</span>
                    <span className="ec-sal-modal-row-val">{fmtCurrency(amount)}</span>
                  </div>
                ))}
                <div className="ec-sal-modal-row total">
                  <span className="ec-sal-modal-row-label">Gross Salary</span>
                  <span className="ec-sal-modal-row-val">{fmtCurrency(shownBreakdown.gross_salary)}</span>
                </div>
              </div>

              {!fullPay && Object.keys(shownBreakdown.deductions || {}).length > 0 ? (
                <div className="ec-sal-modal-section">
                  <div className="ec-sal-modal-sec-hd ded">Deductions (Monthly)</div>
                  {Object.entries(shownBreakdown.deductions || {}).map(([name, amount]) => (
                    <div key={name} className="ec-sal-modal-row">
                      <span className="ec-sal-modal-row-label">{name}</span>
                      <span className="ec-sal-modal-row-val">{fmtCurrency(amount)}</span>
                    </div>
                  ))}
                  <div className="ec-sal-modal-row total">
                    <span className="ec-sal-modal-row-label">Total Deductions</span>
                    <span className="ec-sal-modal-row-val">{fmtCurrency(shownBreakdown.total_deductions)}</span>
                  </div>
                </div>
              ) : null}

              <div className="ec-sal-modal-row net">
                <span className="ec-sal-modal-row-label">{fullPay ? "Salary Payable" : "Net Salary (Take-Home)"}</span>
                <span className="ec-sal-modal-row-val">{fmtCurrency(shownBreakdown.net_salary)}</span>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function digitsOnlyAccount(value = "") {
  return String(value || "").replace(/\D/g, "").slice(0, 18);
}

/** Display helper: 123456789012 → 1234 5678 9012 */
function formatAccountGroups(digits = "") {
  const raw = digitsOnlyAccount(digits);
  return raw.replace(/(\d{4})(?=\d)/g, "$1 ").trim();
}

/** Mask all but last 4 digits with X (e.g. 123456789012 → XXXX XXXX 9012). */
function maskAccountNumber(value = "") {
  const raw = digitsOnlyAccount(value);
  if (!raw) return "";
  // Last 4 stay visible; shorter values are the "last 4" while typing.
  const masked =
    raw.length <= 4
      ? raw
      : `${"X".repeat(raw.length - 4)}${raw.slice(-4)}`;
  // Group without stripping X (digitsOnly would remove the mask).
  return masked.replace(/(.{4})(?=.)/g, "$1 ").trim();
}

function AccountNumberInput({
  label,
  value,
  onChange,
  error,
  required = false,
  placeholder = "Enter account number",
}) {
  const [revealed, setRevealed] = useState(false);
  const [focused, setFocused] = useState(false);
  const raw = digitsOnlyAccount(value);
  const showFull = revealed || focused;
  const display = showFull ? formatAccountGroups(raw) : maskAccountNumber(raw);

  return (
    <Field label={label} required={required} error={error}>
      <span className="ec-iico"><EI.CreditCard /></span>
      <input
        type="text"
        className={`ec-inp has-eye${error ? " err" : ""}`}
        value={display}
        placeholder={placeholder}
        maxLength={22}
        autoComplete="off"
        inputMode="numeric"
        pattern="[0-9 ]*"
        spellCheck={false}
        readOnly={!showFull}
        onFocus={() => {
          // Unlock immediately so the first keystroke edits raw digits, not the mask.
          flushSync(() => setFocused(true));
        }}
        onBlur={() => setFocused(false)}
        onChange={(e) => {
          if (!showFull) return;
          onChange(digitsOnlyAccount(e.target.value));
        }}
      />
      <button
        type="button"
        className="ec-acct-eye"
        aria-label={revealed ? "Hide account number" : "Show account number"}
        title={revealed ? "Hide account number" : "Show account number"}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setRevealed((v) => !v)}
      >
        {revealed ? <EI.EyeOff /> : <EI.Eye />}
      </button>
    </Field>
  );
}

function maskAadhaarNumber(value = "") {
  const raw = String(value || "").replace(/\D/g, "").slice(0, 12);
  if (!raw) return "";
  if (raw.length <= 4) return "•".repeat(raw.length);
  return `${"•".repeat(raw.length - 4)}${raw.slice(-4)}`;
}

function formatAadhaarGroups(digits = "") {
  const raw = String(digits || "").replace(/\D/g, "").slice(0, 12);
  return raw.replace(/(\d{4})(?=\d)/g, "$1 ").trim();
}

function AadhaarNumberInput({
  label,
  value,
  onChange,
  error,
  hint,
  green,
  tooltip,
}) {
  const [revealed, setRevealed] = useState(false);
  const [focused, setFocused] = useState(false);
  const raw = String(value || "").replace(/\D/g, "").slice(0, 12);
  const showFull = revealed || focused;
  const display = showFull ? formatAadhaarGroups(raw) : formatAadhaarGroups(maskAadhaarNumber(raw));
  const ok = !error && raw.length === 12;

  return (
    <Field label={label} error={error} hint={hint} green={green} tooltip={tooltip}>
      <span className="ec-iico"><EI.Hash /></span>
      <input
        type="text"
        className={`ec-inp mono has-eye${error ? " err" : ok ? " ok" : ""}`}
        value={display}
        placeholder="Enter 12-digit Aadhaar Number"
        maxLength={14}
        autoComplete="off"
        inputMode="numeric"
        spellCheck={false}
        readOnly={!showFull}
        onFocus={() => flushSync(() => setFocused(true))}
        onBlur={() => setFocused(false)}
        onChange={(e) => {
          if (!showFull) return;
          onChange(String(e.target.value || "").replace(/\D/g, "").slice(0, 12));
        }}
      />
      <button
        type="button"
        className="ec-acct-eye"
        aria-label={revealed ? "Hide Aadhaar" : "Show Aadhaar"}
        title={revealed ? "Hide Aadhaar" : "Show Aadhaar"}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setRevealed((v) => !v)}
      >
        {revealed ? <EI.EyeOff /> : <EI.Eye />}
      </button>
    </Field>
  );
}

/* ═══════════════════════════════════════════════════════
   BANK ACCOUNT CARD
═══════════════════════════════════════════════════════ */
function BankAccountCard({ account, index, onChange, onRemove, errors, isPrimary, onSetPrimary }) {
  const isFirst = index === 0;
  const hs = isPrimary ? "primary" : "other";
  const ifscTimer = useRef(null);
  const ifscReqId = useRef(0);
  const [ifscState, setIfscState] = useState({ status: "idle", message: "" });

  useEffect(() => () => clearTimeout(ifscTimer.current), []);

  const handleIfscChange = (raw) => {
    const code = String(raw || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 11);
    const reqId = ++ifscReqId.current;
    clearTimeout(ifscTimer.current);

    // Incomplete IFSC — clear stale lookup UI + auto-filled bank/branch (avoids green+red conflict).
    if (code.length < 11) {
      setIfscState({ status: "idle", message: "" });
      onChange({
        ifsc_code: code,
        bank_name: "",
        branch_name: "",
        bank_id: "",
        bank_branch_id: "",
      });
      return;
    }

    // Keep full 11-char code in state immediately so async lookup cannot overwrite it.
    onChange({
      ifsc_code: code,
      bank_name: "",
      branch_name: "",
      bank_id: "",
      bank_branch_id: "",
    });

    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(code)) {
      setIfscState({ status: "err", message: "IFSC format looks invalid (e.g. HDFC0001234)." });
      return;
    }

    setIfscState({ status: "loading", message: "Looking up IFSC…" });
    ifscTimer.current = setTimeout(async () => {
      try {
        const res = await resolveIfsc(code);
        if (reqId !== ifscReqId.current) return; // stale response after edit
        // Always re-apply ifsc_code with bank/branch so a stale closure cannot truncate to 10 chars.
        onChange({
          ifsc_code: code,
          bank_name: res.bank_name || "",
          branch_name: res.branch_name || "",
          bank_id: res.bank_id || "",
          bank_branch_id: res.branch_id || "",
        });
        const place = [res.city, res.state].filter(Boolean).join(", ");
        setIfscState({
          status: "ok",
          message: `${res.bank_name || "Bank"}${res.branch_name ? ` · ${res.branch_name}` : ""}${place ? ` · ${place}` : ""}`,
        });
      } catch (e) {
        if (reqId !== ifscReqId.current) return;
        const detail = e?.response?.data?.detail;
        onChange({ ifsc_code: code, bank_id: "", bank_branch_id: "" });
        setIfscState({
          status: "err",
          message: typeof detail === "string" ? detail : "Could not resolve this IFSC. Check the code and try again.",
        });
      }
    }, 350);
  };

  return (
    <div className={`ec-bank-card${isPrimary ? " primary" : ""}`}>
      <div className={`ec-bank-head${isPrimary ? " primary" : ""}`}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div className={`ec-bank-num ${hs}`}>{index + 1}</div>
          <div>
            <div className={`ec-bank-title ${hs}`}>
              {account.bank_name || `Bank Account ${index + 1}`}
            </div>
            {isPrimary && (
              <div className="ec-bank-pill primary">
                <EI.CheckSm /> Primary Account
              </div>
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {!isPrimary && (
            <button
              type="button" onClick={onSetPrimary}
              style={{
                fontSize: 12, fontWeight: 700, padding: "5px 12px",
                borderRadius: 8, border: "1.5px solid #bfdbfe",
                background: "transparent", color: "#2563eb",
                cursor: "pointer", fontFamily: "inherit", transition: "all .2s",
              }}
            >
              Set Primary
            </button>
          )}
          {!isFirst && (
            <button
              type="button" onClick={onRemove}
              style={{
                fontSize: 12, fontWeight: 700, padding: "5px 12px",
                borderRadius: 8, border: "1.5px solid #fecaca",
                background: "transparent", color: "#e53e3e",
                cursor: "pointer", fontFamily: "inherit", transition: "all .2s",
              }}
            >
              Remove
            </button>
          )}
        </div>
      </div>

      <div className="ec-bank-body">
        <div className="ec-g2" style={{ gap: 16, marginBottom: 14 }}>
          <Field label="IFSC Code" required error={errors?.ifsc_code}>
            <span className="ec-iico"><EI.Hash /></span>
            <input
              className={`ec-inp${errors?.ifsc_code ? " err" : ""}`}
              placeholder="e.g. HDFC0001234"
              maxLength={11}
              value={account.ifsc_code}
              onChange={(e) => handleIfscChange(e.target.value)}
              autoComplete="off"
              spellCheck={false}
            />
            {ifscState.status === "loading" ? (
              <div className="ec-ifsc-status loading"><EI.Loader /> {ifscState.message}</div>
            ) : null}
            {ifscState.status === "ok" && !errors?.ifsc_code ? (
              <div className="ec-ifsc-status ok">{ifscState.message}</div>
            ) : null}
            {ifscState.status === "err" && !errors?.ifsc_code ? (
              <div className="ec-ifsc-status err">{ifscState.message}</div>
            ) : null}
          </Field>

          <Field label="Bank Name" required error={errors?.bank_name}>
            <span className="ec-iico"><EI.Bank /></span>
            <input
              className={`ec-inp${errors?.bank_name ? " err" : ""}`}
              placeholder="Auto-filled from IFSC"
              maxLength={100}
              value={account.bank_name}
              onChange={(e) => onChange("bank_name", e.target.value)}
            />
          </Field>
        </div>

        <Field label="Branch" hint="Auto-filled from IFSC">
          <span className="ec-iico"><EI.MapPin /></span>
          <input
            className="ec-inp"
            placeholder="Auto-filled branch name"
            maxLength={120}
            value={account.branch_name || ""}
            onChange={(e) => onChange("branch_name", e.target.value)}
          />
        </Field>

        <Field label="Account Holder Name" required error={errors?.account_holder_name} hint="Auto-filled from employee name — you can change it if needed">
          <span className="ec-iico"><EI.User /></span>
          <input
            className={`ec-inp${errors?.account_holder_name ? " err" : ""}`}
            placeholder="Account holder name"
            maxLength={120}
            value={account.account_holder_name || ""}
            onChange={(e) => onChange("account_holder_name", e.target.value)}
          />
        </Field>

        <div className="ec-g2" style={{ gap: 16, marginTop: 14, marginBottom: 14 }}>
          <AccountNumberInput
            label="Account Number"
            required
            error={errors?.account_number}
            value={account.account_number}
            onChange={(v) => onChange("account_number", v)}
            placeholder="e.g. 1234 5678 9012"
          />
          <AccountNumberInput
            label="Confirm Account Number"
            required
            error={errors?.confirm_account_number}
            value={account.confirm_account_number || ""}
            onChange={(v) => onChange("confirm_account_number", v)}
            placeholder="Re-enter account number"
          />
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   STEP 4 — BANKING (same form as Add Employee onboarding)
═══════════════════════════════════════════════════════ */
function emptyBankAccount(employeeName = "") {
  return {
    account_holder_name: employeeName || "",
    bank_name: "",
    branch_name: "",
    bank_id: "",
    bank_branch_id: "",
    account_number: "",
    confirm_account_number: "",
    ifsc_code: "",
  };
}

function bankAccountHasData(acc) {
  if (!acc) return false;
  return Boolean(
    String(acc.ifsc_code || "").trim()
    || String(acc.bank_branch_id || "").trim()
    || String(acc.bank_name || "").trim()
    || digitsOnlyAccount(acc.account_number)
    || digitsOnlyAccount(acc.confirm_account_number)
  );
}

function validateOnboardingAccount(acc) {
  const errors = {};
  const ifsc = String(acc?.ifsc_code || "").toUpperCase();
  if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc) || !acc?.bank_branch_id) {
    errors.ifsc_code = "Enter a valid IFSC (e.g. HDFC0001234) so the bank and branch fill in.";
  }
  if (!String(acc?.bank_name || "").trim()) {
    errors.bank_name = "Bank name is filled from the IFSC.";
  }
  if (!String(acc?.account_holder_name || "").trim()) {
    errors.account_holder_name = "Enter the account holder name.";
  }
  const acct = digitsOnlyAccount(acc?.account_number);
  const confirm = digitsOnlyAccount(acc?.confirm_account_number);
  if (!acct || acct.length < 9 || acct.length > 18) {
    errors.account_number = "Account number must be 9–18 digits";
  }
  if (!confirm) {
    errors.confirm_account_number = "Re-enter the account number.";
  } else if (acct && confirm !== acct) {
    errors.confirm_account_number = "Account numbers do not match";
  }
  return errors;
}

function Step4Banking({
  sectionRef,
  employeeId,
  employeeName,
  defaultEffectiveFrom,
  requireAccount = false,
  onSaved,
}) {
  const [bankAccounts, setBankAccounts] = useState(() => [emptyBankAccount(employeeName)]);
  const [primaryIndex, setPrimaryIndex] = useState(0);
  const [bankErrors, setBankErrors] = useState({});
  const [savedActive, setSavedActive] = useState(0);
  const [onFile, setOnFile] = useState([]);
  const accountsRef = useRef(bankAccounts);
  const primaryRef = useRef(primaryIndex);
  accountsRef.current = bankAccounts;
  primaryRef.current = primaryIndex;

  useEffect(() => {
    if (!employeeId) {
      setSavedActive(0);
      setOnFile([]);
      return undefined;
    }
    let cancelled = false;
    setOnFile([]);
    setBankAccounts([emptyBankAccount(employeeName)]);
    setPrimaryIndex(0);
    setBankErrors({});
    listAccounts(employeeId)
      .then(async (rows) => {
        if (cancelled) return;
        const active = (Array.isArray(rows) ? rows : []).filter((a) => a?.is_active !== false);
        const verified = active.filter((a) =>
          a?.verification_status === "verified" || a?.verification_status === "active"
        );
        setOnFile(active);
        setSavedActive(verified.length);
        if (active.length) return;

        let legacy = [];
        try {
          legacy = await getEmployeeBankDetails(employeeId);
        } catch {
          legacy = [];
        }
        if (cancelled || !Array.isArray(legacy) || !legacy.length) return;
        const row = legacy.find((item) => item?.is_primary) || legacy[0];
        const ifsc = String(row?.ifsc_code || "").trim().toUpperCase();
        const acct = digitsOnlyAccount(row?.account_number);
        let bankName = row?.bank_name || "";
        let branchName = "";
        let branchId = "";
        let bankId = "";
        if (ifsc) {
          try {
            const resolved = await resolveIfsc(ifsc);
            if (cancelled) return;
            bankName = resolved?.bank_name || bankName;
            branchName = resolved?.branch_name || "";
            branchId = resolved?.branch_id || resolved?.bank_branch_id || "";
            bankId = resolved?.bank_id || "";
          } catch {
            /* keep the bank name already stored on the employee */
          }
        }
        if (cancelled) return;
        setBankAccounts([{
          account_holder_name: employeeName || "",
          bank_name: bankName,
          branch_name: branchName,
          bank_id: bankId,
          bank_branch_id: branchId,
          account_number: acct,
          confirm_account_number: acct,
          ifsc_code: ifsc,
        }]);
      })
      .catch(() => {
        if (!cancelled) {
          setSavedActive(0);
          setOnFile([]);
        }
      });
    return () => {
      cancelled = true;
    };
    // Prefill once per employee. Holder name is filled by the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  useEffect(() => {
    if (!employeeName) return;
    setBankAccounts((prev) =>
      prev.map((acc) =>
        !acc.account_holder_name ? { ...acc, account_holder_name: employeeName } : acc
      )
    );
  }, [employeeName]);

  const handleAccountChange = (index, fieldOrPatch, value) => {
    // Always merge against latest state — async IFSC lookup must not overwrite
    // the 11th character with a stale 10-char snapshot from an earlier render.
    let merged = null;
    setBankAccounts((prev) => {
      const current = prev[index] || {};
      merged =
        fieldOrPatch && typeof fieldOrPatch === "object"
          ? { ...current, ...fieldOrPatch }
          : { ...current, [fieldOrPatch]: value };
      return prev.map((acc, i) => (i === index ? merged : acc));
    });

    const patchKeys =
      fieldOrPatch && typeof fieldOrPatch === "object"
        ? Object.keys(fieldOrPatch)
        : [fieldOrPatch];
    const touchingAccount =
      patchKeys.includes("account_number") || patchKeys.includes("confirm_account_number");
    const touchingIfsc =
      patchKeys.includes("ifsc_code") ||
      patchKeys.includes("bank_name") ||
      patchKeys.includes("branch_name");

    setBankErrors((prevErr) => {
      const row = { ...(prevErr?.[index] || {}) };
      patchKeys.forEach((k) => {
        if (k) delete row[k];
      });
      if (touchingIfsc) {
        delete row.ifsc_code;
        delete row.bank_name;
        delete row.branch_name;
      }
      if (touchingAccount && merged) {
        delete row.account_number;
        delete row.confirm_account_number;
        const acct = digitsOnlyAccount(merged.account_number);
        const confirm = digitsOnlyAccount(merged.confirm_account_number);
        if (acct && (acct.length < 9 || acct.length > 18)) {
          row.account_number = "Account number must be 9–18 digits";
        }
        if (confirm) {
          if (!acct) {
            row.confirm_account_number = "Enter account number first";
          } else if (confirm !== acct) {
            row.confirm_account_number = "Account numbers do not match";
          }
        }
      }
      const nextErr = { ...prevErr };
      if (Object.keys(row).length) nextErr[index] = row;
      else delete nextErr[index];
      return nextErr;
    });
  };

  const addAccount = () => setBankAccounts((prev) => [...prev, emptyBankAccount(employeeName)]);

  const removeAccount = (index) => {
    setBankAccounts((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      if (primaryIndex >= updated.length) setPrimaryIndex(0);
      return updated;
    });
    setBankErrors((prev) => {
      const next = {};
      Object.keys(prev || {}).forEach((k) => {
        const ki = Number(k);
        if (ki < index) next[ki] = prev[k];
        else if (ki > index) next[ki - 1] = prev[k];
      });
      return next;
    });
  };

  const collectValidation = useCallback(() => {
    const accounts = accountsRef.current || [];
    const filled = accounts
      .map((acc, index) => ({ acc, index }))
      .filter(({ acc }) => bankAccountHasData(acc));
    if (!filled.length) {
      return { ok: true, empty: true, errors: {}, message: "" };
    }
    const errors = {};
    filled.forEach(({ acc, index }) => {
      const row = validateOnboardingAccount(acc);
      if (Object.keys(row).length) errors[index] = row;
    });
    if (Object.keys(errors).length) {
      return {
        ok: false,
        empty: false,
        errors,
        message: "Banking details are incomplete. Fix the highlighted fields, then continue.",
      };
    }
    return { ok: true, empty: false, errors: {}, message: "" };
  }, []);

  const draftFromAccount = useCallback((acc) => {
    const holder = String(acc?.account_holder_name || employeeName || "").trim();
    return {
      bank_branch_id: acc?.bank_branch_id || "",
      account_holder_name: holder,
      account_number: digitsOnlyAccount(acc?.account_number),
      confirm_account_number: digitsOnlyAccount(acc?.confirm_account_number),
      upi_vpa: null,
      account_type: "savings",
      payment_method: "salary_transfer",
      is_primary: true,
      effective_from: defaultEffectiveFrom || todayIso(),
      branch_display_name: [acc?.bank_name, acc?.branch_name].filter(Boolean).join(" — ") || undefined,
    };
  }, [defaultEffectiveFrom, employeeName]);

  const primaryAccount = useCallback(() => {
    const accounts = accountsRef.current || [];
    const idx = primaryRef.current;
    const primary = accounts[idx];
    if (bankAccountHasData(primary)) return primary;
    return accounts.find((acc) => bankAccountHasData(acc)) || primary || emptyBankAccount(employeeName);
  }, [employeeName]);

  useImperativeHandle(sectionRef, () => ({
    hasData: () => (accountsRef.current || []).some(bankAccountHasData),
    getDraft: () => draftFromAccount(primaryAccount()),
    clear: () => {
      setBankAccounts([emptyBankAccount(employeeName)]);
      setPrimaryIndex(0);
      setBankErrors({});
    },
    validate: () => {
      const check = collectValidation();
      setBankErrors(check.errors || {});
      return check;
    },
    flushSave: async () => {
      if (!employeeId) return { ok: true, empty: true };
      const check = collectValidation();
      if (check.empty) {
        if (requireAccount && savedActive === 0) {
          const errors = {
            0: {
              ifsc_code: "Enter a valid IFSC (e.g. HDFC0001234).",
              account_holder_name: "Enter the account holder name.",
              account_number: "Enter the account number.",
              confirm_account_number: "Re-enter the account number.",
            },
          };
          setBankErrors(errors);
          return {
            ok: false,
            empty: true,
            errors,
            message: "Enter IFSC, account holder name, and account number on this Banking step.",
          };
        }
        return { ok: true, empty: true };
      }
      if (!check.ok) {
        setBankErrors(check.errors || {});
        return check;
      }
      const draft = draftFromAccount(primaryAccount());
      const effectiveFrom = draft.effective_from || todayIso();
      try {
        const existing = await listAccounts(employeeId);
        const rows = Array.isArray(existing) ? existing : [];
        const target = rows.find(
          (row) => String(row?.effective_from || "").slice(0, 10) === String(effectiveFrom).slice(0, 10)
        );
        const payload = {
          bank_branch_id: draft.bank_branch_id,
          account_holder_name: draft.account_holder_name,
          account_number: draft.account_number,
          confirm_account_number: draft.confirm_account_number,
          account_type: "savings",
          payment_method: "salary_transfer",
          is_primary: true,
          branch_display_name: draft.branch_display_name,
        };
        if (target?.bank_account_id) {
          await updateAccount(target.bank_account_id, payload);
        } else {
          await createAccount({
            employee_id: employeeId,
            ...payload,
            effective_from: effectiveFrom,
          });
        }
        setSavedActive((n) => Math.max(n, 1));
        setBankErrors({});
        onSaved?.();
        return { ok: true, empty: false };
      } catch (error) {
        return {
          ok: false,
          message: bankApiError(error) || "Banking details were not saved.",
        };
      }
    },
  }), [
    collectValidation,
    draftFromAccount,
    employeeId,
    employeeName,
    onSaved,
    primaryAccount,
    requireAccount,
    savedActive,
  ]);

  return (
    <div>
      <div className="ec-sec-hd">
        <div className="ec-sec-title">Banking Details</div>
        <div className="ec-sec-sub">
          {requireAccount
            ? "Salary cannot be transferred until this account is saved"
            : "Add salary disbursement account — you can also skip and add later"}
        </div>
      </div>

      {onFile.length > 0 ? (
        <div className="ec-bank-onfile" role="status">
          <div className="ec-bank-onfile-title">Bank details already saved for this employee</div>
          {onFile.map((row) => (
            <div key={row.bank_account_id} className="ec-bank-onfile-row">
              {row.account_holder_name || "Account holder"}
              {" · "}
              {row.branch_display_name || "Bank"}
              {" · "}
              {row.account_number_masked || (row.account_number_last4 ? `****${row.account_number_last4}` : "Account on file")}
              {row.is_primary ? " · Primary" : ""}
              {row.verification_status ? ` · ${String(row.verification_status).replace(/_/g, " ")}` : ""}
            </div>
          ))}
          <div className="ec-bank-onfile-note">
            Enter the account again below to approve it for salary transfer.
          </div>
        </div>
      ) : null}

      <div className="ec-bank-info">
        <div className="ec-bank-info-ico"><EI.Bank /></div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#0f0e1a", marginBottom: 3 }}>
            Bank account for salary disbursement
          </div>
          <div style={{ fontSize: 12.5, color: "#7070a0", lineHeight: 1.6 }}>
            The <strong>primary</strong> account is used for salary transfers. Account number and confirm account number must match exactly.
          </div>
        </div>
      </div>

      {bankAccounts.map((account, index) => (
        <BankAccountCard
          key={index}
          index={index}
          account={account}
          onChange={(field, value) => handleAccountChange(index, field, value)}
          onRemove={() => removeAccount(index)}
          errors={bankErrors?.[index]}
          isPrimary={primaryIndex === index}
          onSetPrimary={() => setPrimaryIndex(index)}
        />
      ))}

      <button type="button" className="ec-add-bank-btn" onClick={addAccount}>
        <EI.Plus /> Add another bank account
      </button>
    </div>
  );
}

const STATUTORY_EMPTY = () => ({
  uan_number: "",
  pf_number: "",
  esic_number: "",
  pt_number: "",
  lwf_number: "",
  pan: "",
  aadhaar: "",
  pf_member: false,
  esi_applicable: false,
  pt_applicable: true,
  lwf_applicable: false,
  bonus_applicable: false,
  employment_category: "employee",
  effective_from: new Date().toISOString().slice(0, 10),
});

function mapApiStatutoryToForm(data) {
  if (!data) return STATUTORY_EMPTY();
  return {
    uan_number: data.uan_number || "",
    pf_number: data.pf_number || "",
    esic_number: data.esic_number || "",
    pt_number: data.pt_number || "",
    lwf_number: data.lwf_number || "",
    pan: data.pan || "",
    aadhaar: "",
    pf_member: data.pf_member != null ? !!data.pf_member : false,
    esi_applicable: data.esi_applicable != null ? !!data.esi_applicable : false,
    pt_applicable: data.pt_applicable,
    lwf_applicable: data.lwf_applicable,
    bonus_applicable: data.bonus_applicable,
    employment_category: data.employment_category || "employee",
    effective_from: toDateInput(data.effective_from) || todayIso(),
  };
}

/* ═══════════════════════════════════════════════════════
   STEP 5 — STATUTORY (draft / wizard inline)
═══════════════════════════════════════════════════════ */
const STATUTORY_FIELD_META = {
  uan_number: {
    label: "UAN Number",
    placeholder: "Enter 12-digit UAN Number",
    hint: "Universal Account Number (UAN) issued by EPFO. Required for employees covered under the Employees' Provident Fund (EPF).",
    tooltip: "Enter the employee's 12-digit EPFO Universal Account Number. Required for EPF contributions and ECR filing.",
    success: "Valid 12-digit UAN",
  },
  pan: {
    label: "PAN Number",
    placeholder: "ABCDE1234F",
    hint: "Permanent Account Number (PAN) issued by the Income Tax Department. Required for income tax (TDS) calculations and statutory reporting.",
    tooltip: "Used for TDS computation, Form 16 generation, and Income Tax compliance.",
    success: "Valid PAN format",
  },
  aadhaar: {
    label: "Aadhaar Number",
    placeholder: "Enter 12-digit Aadhaar Number",
    hint: "Aadhaar number issued by UIDAI. Must be a real 12-digit number (checksum is checked). Leave blank if you do not have it yet.",
    tooltip: "Enter the employee's Aadhaar number without spaces. Dummy numbers are rejected.",
    success: "Valid 12-digit Aadhaar",
  },
  pf_number: {
    label: "PF Member ID",
    placeholder: "DL/CPM/1234567/1234567",
    hint: "Provident Fund Member ID allotted by the EPF establishment. Letters, numbers, / and - only (max 30 characters).",
    tooltip: "Enter the PF Member ID allotted by your EPF establishment.",
    success: "PF Member ID looks valid",
  },
  esic_number: {
    label: "ESIC Insurance Number",
    placeholder: "Enter 10–17 digit ESIC Number",
    hint: "Employee Insurance Number issued by ESIC (10 to 17 digits). Required only for ESIC-eligible employees.",
    tooltip: "Required only for employees eligible under ESIC.",
    success: "Valid ESIC number",
  },
  lwf_number: {
    label: "Labour Welfare Fund Number",
    placeholder: "Enter LWF Registration Number",
    hint: "Labour Welfare Fund registration number issued by the respective State Labour Department.",
    tooltip: "Required only for states where Labour Welfare Fund is applicable.",
    success: "LWF number looks valid",
  },
};

const STATUTORY_ID_FIELDS = [
  { key: "uan_number", label: "UAN Number" },
  { key: "pan", label: "PAN Number" },
  { key: "aadhaar", label: "Aadhaar Number" },
  { key: "pf_number", label: "PF Member ID" },
  { key: "esic_number", label: "ESIC Number" },
  { key: "lwf_number", label: "LWF Number" },
];

function Step5Statutory({ form, setForm, errors = {}, setErrors, aadhaarLast4 = "" }) {
  const pfRequired = !!form.pf_member;
  const esiRequired = !!form.esi_applicable;

  const applyFieldErrors = (nextForm, keys) => {
    if (typeof setErrors !== "function") return;
    const collected = collectStatutoryFormErrors(nextForm);
    setErrors((prev) => {
      const next = { ...prev };
      keys.forEach((key) => {
        if (collected[key]) next[key] = collected[key];
        else delete next[key];
      });
      return next;
    });
  };

  const setField = (key, raw) => {
    const value = sanitizeStatutoryValue(key, raw);
    const next = { ...form, [key]: value };
    setForm(next);
    applyFieldErrors(next, [key]);
  };

  const renderIdField = (key) => {
    const meta = STATUTORY_FIELD_META[key];
    const value = form[key] || "";
    const err = errors[key];
    const ok = isStatutoryFieldValid(key, value);
    const required =
      key === "uan_number" || key === "pf_number"
        ? pfRequired
        : key === "esic_number"
          ? esiRequired
          : false;

    if (key === "aadhaar") {
      return (
        <AadhaarNumberInput
          key={key}
          label={aadhaarLast4 ? `${meta.label} (on file: ****${aadhaarLast4})` : meta.label}
          value={value}
          error={err}
          hint={meta.hint}
          green={ok ? meta.success : ""}
          tooltip={meta.tooltip}
          onChange={(v) => setField("aadhaar", v)}
        />
      );
    }

    return (
      <Field
        key={key}
        label={meta.label}
        required={required}
        error={err}
        hint={meta.hint}
        green={ok ? meta.success : ""}
        tooltip={meta.tooltip}
      >
        <span className="ec-iico"><EI.Hash /></span>
        <input
          className={`ec-inp mono${err ? " err" : ok ? " ok" : ""}`}
          placeholder={meta.placeholder}
          value={value}
          required={required}
          aria-required={required || undefined}
          maxLength={key === "uan_number" ? 12 : key === "esic_number" ? 17 : key === "pan" ? 10 : 30}
          autoComplete="off"
          spellCheck={false}
          inputMode={["uan_number", "esic_number"].includes(key) ? "numeric" : "text"}
          onChange={(e) => setField(key, e.target.value)}
        />
      </Field>
    );
  };

  return (
    <div>
      <div className="ec-sec-hd">
        <div className="ec-sec-title">Statutory & Tax Information</div>
        <div className="ec-sec-sub">
          Provide statutory IDs for payroll and compliance — you can also skip and add later
        </div>
      </div>

      <div className="ec-bank-info">
        <div className="ec-bank-info-ico"><EI.IdCard /></div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#0f0e1a" }}>
            Statutory details are shown based on your organization's payroll configuration. Enter employee-specific details where applicable.
          </div>
        </div>
      </div>

      <div className="ec-g2">
        {renderIdField("uan_number")}
        {renderIdField("pan")}
        {renderIdField("pf_number")}
        {renderIdField("esic_number")}

        {/* <Field
          label="Employment Category"
          hint="Employment classification used for statutory and payroll rules."
          tooltip="Select the employee's employment category for compliance reporting."
        >
          <span className="ec-iico"><EI.User /></span>
          <select
            className="ec-sel"
            value={form.employment_category}
            onChange={(e) => set("employment_category", e.target.value)}
          >
            {["employee", "worker", "apprentice", "trainee", "contract", "consultant", "intern"].map((c) => (
              <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
            ))}
          </select>
          <span className="ec-sel-arr">▾</span>
        </Field> */}

        {/* <Field
          label="Effective From"
          required
          error={errors.effective_from}
          hint="Date from which these statutory details become effective."
          tooltip="Effective date for the statutory record used in payroll filings."
          noIcon
        >
          <DatePickerField
            hideLabel
            compact
            required
            value={form.effective_from}
            onChange={(d) => set("effective_from", d)}
            inputClassName={`ec-inp ec-inp-no-icon${errors.effective_from ? " err" : ""}`}
          />
        </Field> */}
      </div>

      <div className="ec-stat-notes">
        <div className="ec-stat-notes-title"><EI.Info /> Important Notes</div>
        <ul>
          <li>PAN is recommended for all employees and mandatory for TDS compliance.</li>
          <li>Professional Tax is configured once in Organisation Setup and applies to all employees.</li>
          <li>Ensure all statutory details are verified before processing payroll to avoid compliance issues.</li>
        </ul>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════ */
export default function EmployeeCreate({
  embedded = false,
  employeeId: externalEmployeeId,
  onBack,
  onSuccess,
  initialStep,
  needsBankSetup = false,
}) {
  const { employeeId: paramEmployeeId } = useParams();
  const [searchParams] = useSearchParams();
  const employeeId = externalEmployeeId ?? paramEmployeeId;
  const isEditMode = Boolean(employeeId);
  const urlNeedsBank = searchParams.get("needs_bank") === "1";
  const effectiveNeedsBank = needsBankSetup || urlNeedsBank;
  const stepParam = Number(searchParams.get("step") || 0);
  const urlStep = stepParam >= 1 && stepParam <= 5 ? stepParam : undefined;
  const foundationCtx = useFoundationSetup();
  const setupProgressCtx = useSetupProgress();
  const orgProfile = useOrgProfile();
  const [departments,       setDepartments]       = useState([]);
  const [designations,      setDesignations]       = useState([]);
  const [locations,         setLocations]          = useState([]);
  const [dropdownLoading,   setDropdownLoading]    = useState(true);
  const [dropdownError,     setDropdownError]      = useState("");
  const resolvedInitialStep =
    Number(initialStep) >= 1 && Number(initialStep) <= 5
      ? Number(initialStep)
      : urlStep
        ? urlStep
      : effectiveNeedsBank
        ? 4
        : 1;
  const [currentStep,       setCurrentStep]        = useState(resolvedInitialStep);
  const [loading,           setLoading]            = useState(false);
  const [loadingEmployee,   setLoadingEmployee]    = useState(isEditMode);
  const [loadEmployeeError, setLoadEmployeeError]  = useState("");
  const [submitSuccess,     setSubmitSuccess]      = useState(false);
  const [submitError,       setSubmitError]        = useState("");
  const [stepErrors,        setStepErrors]         = useState({});
  const [codeLoading,       setCodeLoading]        = useState(true);
  const [codePrefix,        setCodePrefix]         = useState("EMP");
  const [codeNumber,        setCodeNumber]         = useState("");
  const [codeAuto,          setCodeAuto]           = useState(true);
  const [codeUsePrefix,     setCodeUsePrefix]      = useState(true);
  const [codeNumberLength,  setCodeNumberLength]   = useState(4);
  const [savingOrgDefault,  setSavingOrgDefault]   = useState(false);
  const prefixDebounceRef   = useRef(null);
  const initialCodeLoadedRef = useRef(false);
  const codeModeRef = useRef({ auto: true, usePrefix: true, length: 4 });
  const [createdEmployeeCode, setCreatedEmployeeCode] = useState("");
  // Welcome email recovery (edit mode): saving never sends; the Admin sends explicitly.
  const [welcomeEmailStatus, setWelcomeEmailStatus] = useState(null);
  const [savedEmployeeEmail, setSavedEmployeeEmail] = useState("");
  const [welcomeConfirmOpen, setWelcomeConfirmOpen] = useState(false);
  const [welcomeSending, setWelcomeSending] = useState(false);
  const [welcomeSendResult, setWelcomeSendResult] = useState(null);
  const [createdEmployeeId, setCreatedEmployeeId] = useState("");
  const [bankSaving,        setBankSaving]         = useState(false);
  const [skipBanking,       setSkipBanking]        = useState(false);
  const [skipStatutory,     setSkipStatutory]      = useState(false);
  const [showStatutorySkipModal, setShowStatutorySkipModal] = useState(false);
  const [hasBankAccounts,   setHasBankAccounts]    = useState(false);
  const [hasStatutoryDetails, setHasStatutoryDetails] = useState(false);
  const [loadedEmployee,    setLoadedEmployee]     = useState(null);
  const [bankQueue,        setBankQueue]          = useState([]);
  const [bankQueueLoaded,  setBankQueueLoaded]    = useState(false);
  const [salaryTemplates,   setSalaryTemplates]    = useState([]);
  const [employmentTypes,   setEmploymentTypes]    = useState([]);
  const [reportingManagers, setReportingManagers]  = useState([]);
  const [salaryPackageAssigned, setSalaryPackageAssigned] = useState(false);
  const [compensationAutoAssigned, setCompensationAutoAssigned] = useState(false);
  const [autoAssignedTemplateName, setAutoAssignedTemplateName] = useState("");
  const [statutoryForm,     setStatutoryForm]      = useState(() => STATUTORY_EMPTY());
  const [statutoryErrors,   setStatutoryErrors]    = useState({});
  const [existingStatutory, setExistingStatutory]  = useState(false);
  const [statutoryAadhaarLast4, setStatutoryAadhaarLast4] = useState("");
  const bankSectionRef = useRef(null);
  const displayNameManualRef = useRef(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [form, setForm] = useState({
    employee_code:        "",
    employee_type:        DEFAULT_EMPLOYEE_TYPE,
    role:                 DEFAULT_EMPLOYEE_ROLE,
    employee_name:        "",
    first_name:           "",
    middle_name:          "",
    last_name:            "",
    display_name:         "",
    work_email:           "",
    mobile_phone:         "",
    gender:               "",
    date_of_birth:        "",
    marital_status:       "",
    fathers_name:         "",
    date_of_joining:      firstOfCurrentMonthIso(),
    date_of_leaving:      "",
    status:               "active",
    department_id:        "",
    designation_id:       "",
    location_id:          "",
    business_unit:        "",
    manager_id:           "",
    pay_structure_id:     "",
    salary_template_id:   "",
    annual_ctc:           "",
    pay_frequency:        "Monthly",
    uan_link_status:      "Unlinked",
    is_active:            "true",
  });

  // After create (step 3 save), banking/statutory need this ID — URL id only exists in edit mode
  const effectiveEmployeeId = String(
    employeeId || createdEmployeeId || form._created_employee_id || ""
  );

  const PEOPLE_HUB_LIST = "/employees?module=employees";

  const goBack = useCallback(() => {
    if (onBack) onBack();
    else navigate(PEOPLE_HUB_LIST);
  }, [onBack, navigate]);

  const goToListAfterSuccess = useCallback(() => {
    if (onSuccess) onSuccess();
    else navigate(PEOPLE_HUB_LIST);
  }, [onSuccess, navigate]);

  // Page that opened Edit (profile or filtered list), passed as router state.
  const location = useLocation();
  const rawReturnTo = location.state?.returnTo;
  const returnTo =
    typeof rawReturnTo === "string" && rawReturnTo.startsWith("/") && !rawReturnTo.startsWith("//")
      ? rawReturnTo
      : "";
  const returnsToProfile = /^\/employees\/(?!add(?:[/?]|$))[^/?]+(?:\?|$)/.test(returnTo);

  /** After a successful edit: back to the originating page, else the employee list. */
  const goToOriginAfterSave = useCallback(() => {
    if (onSuccess) {
      onSuccess();
      return;
    }
    if (returnTo && canGoBackInApp()) {
      navigate(-1);
      return;
    }
    navigate(returnTo || PEOPLE_HUB_LIST, { replace: true });
  }, [onSuccess, navigate, returnTo]);

  /* ── Fetch next employee code ─── */
  const applyCodePreview = useCallback((data) => {
    if (!data) return;
    const length = data.number_length || codeModeRef.current.length || 4;
    const usePrefix = data.use_prefix != null ? Boolean(data.use_prefix) : codeModeRef.current.usePrefix;
    const code = data.employee_code || "";
    const prefix = usePrefix
      ? (data.prefix || String(code).replace(/\d+$/, "") || "")
      : "";
    const num = data.next_number != null
      ? String(data.next_number).padStart(length, "0")
      : (String(code).match(/(\d+)$/)?.[1] || "");
    if (usePrefix) setCodePrefix(prefix);
    else setCodePrefix("");
    setCodeNumber(num);
    setForm((prev) => ({ ...prev, employee_code: code }));
  }, []);

  const fetchNextEmployeeCode = useCallback(async (prefix, overrides = {}) => {
    const usePrefix = overrides.usePrefix ?? codeModeRef.current.usePrefix;
    const letters = usePrefix
      ? String(prefix ?? "").replace(/[^a-zA-Z]/g, "").toUpperCase()
      : "";
    if (usePrefix && letters.length < 1) return;
    setCodeLoading(true);
    try {
      const data = await getNextEmployeeCode(letters, { use_prefix: usePrefix });
      applyCodePreview({ ...data, use_prefix: usePrefix });
    } catch (err) {
      console.error("Failed to fetch next employee code:", err);
    } finally {
      setCodeLoading(false);
    }
  }, [applyCodePreview]);

  const syncManualCode = useCallback((prefix, number, usePrefix, length) => {
    setForm((prev) => ({
      ...prev,
      employee_code: composeEmployeeCode(prefix, number, usePrefix, length),
    }));
  }, []);

  const handleCodePrefixChange = useCallback((raw) => {
    const letters = String(raw || "").replace(/[^a-zA-Z]/g, "").toUpperCase().slice(0, 4);
    setCodePrefix(letters);
    clearTimeout(prefixDebounceRef.current);
    const { auto, length } = codeModeRef.current;
    if (auto) {
      if (letters.length >= 1) {
        prefixDebounceRef.current = setTimeout(
          () => fetchNextEmployeeCode(letters, { usePrefix: true }),
          280
        );
      } else {
        setCodeNumber("");
        setForm((prev) => ({ ...prev, employee_code: "" }));
      }
    } else {
      syncManualCode(letters, codeNumber, true, length);
    }
  }, [fetchNextEmployeeCode, codeNumber, syncManualCode]);

  const handleCodeNumberChange = useCallback((raw) => {
    const digits = String(raw || "").replace(/\D/g, "").slice(0, 9);
    setCodeNumber(digits);
    const { usePrefix, length } = codeModeRef.current;
    syncManualCode(codePrefix, digits, usePrefix, length);
  }, [codePrefix, syncManualCode]);

  const handleCodeAutoChange = useCallback((auto) => {
    setCodeAuto(auto);
    codeModeRef.current.auto = auto;
    if (auto) {
      fetchNextEmployeeCode(codePrefix, { usePrefix: codeModeRef.current.usePrefix });
    } else {
      setCodeLoading(false);
      const { usePrefix, length } = codeModeRef.current;
      const digits = codeNumber || String(1);
      setCodeNumber(digits);
      syncManualCode(codePrefix, digits, usePrefix, length);
    }
  }, [codePrefix, codeNumber, fetchNextEmployeeCode, syncManualCode]);

  const handleUsePrefixChange = useCallback((usePrefix) => {
    setCodeUsePrefix(usePrefix);
    codeModeRef.current.usePrefix = usePrefix;
    if (!usePrefix) setCodePrefix("");
    else if (!codePrefix) setCodePrefix("EMP");
    const prefix = usePrefix ? (codePrefix || "EMP") : "";
    if (codeModeRef.current.auto) {
      fetchNextEmployeeCode(prefix, { usePrefix });
    } else {
      syncManualCode(prefix, codeNumber, usePrefix, codeModeRef.current.length);
    }
  }, [codePrefix, codeNumber, fetchNextEmployeeCode, syncManualCode]);

  const handleSaveOrgDefault = useCallback(async () => {
    setSavingOrgDefault(true);
    try {
      const numbering = {
        auto_generate: codeAuto,
        use_prefix: codeUsePrefix,
        prefix: codeUsePrefix ? (codePrefix || "EMP") : "",
        starting_number: Math.max(1, parseInt(String(codeNumber).replace(/\D/g, ""), 10) || 1),
        number_length: codeNumberLength,
      };
      await API.patch("/organisation/me/hr-settings", {
        employee_prefix: numbering.prefix,
        employee_auto_increment: numbering.auto_generate,
        employee_number_start: numbering.starting_number,
        employee_use_prefix: numbering.use_prefix,
        employee_numbering: numbering,
      });
    } catch (err) {
      console.error("Failed to save organisation employee numbering:", err);
    } finally {
      setSavingOrgDefault(false);
    }
  }, [codeAuto, codeUsePrefix, codePrefix, codeNumber, codeNumberLength]);

  useEffect(() => () => clearTimeout(prefixDebounceRef.current), []);

  /* ── Load dropdowns ─── */
  const refreshOrgLookups = async () => {
    const [dept, des, loc] = await Promise.all([
      API.get("/setup/departments"),
      API.get("/setup/designations"),
      API.get("/setup/locations"),
    ]);
    setDepartments(dept.data);
    setDesignations(des.data);
    setLocations(loc.data);
    foundationCtx?.refetch?.();
  };

  useEffect(() => {
    const fetchData = async () => {
      setDropdownError("");
      try {
        const [dept, des, loc] = await Promise.all([
          API.get("/setup/departments"),
          API.get("/setup/designations"),
          API.get("/setup/locations"),
        ]);
        setDepartments(dept.data);
        setDesignations(des.data);
        setLocations(loc.data);
      } catch (err) {
        console.error("Failed to fetch setup dropdown data:", err);
        setDropdownError("Failed to load departments, designations, or locations.");
      } finally {
        setDropdownLoading(false);
      }
    };
    fetchData();

    let cancelled = false;
    getTemplates()
      .then((data) => {
        if (cancelled) return;
        const list = (Array.isArray(data) ? data : []).filter(isSalaryTemplateAssignable);
        setSalaryTemplates(list);
        setForm((prev) => {
          if (!prev.salary_template_id) {
            const def = list.find((t) => t.is_default) || (list.length === 1 ? list[0] : null);
            if (def) return { ...prev, salary_template_id: String(def.template_id) };
          }
          if (list.length === 1 && !prev.salary_template_id) {
            return { ...prev, salary_template_id: String(list[0].template_id) };
          }
          if (
            prev.salary_template_id
            && !list.some((t) => String(t.template_id) === String(prev.salary_template_id))
          ) {
            return { ...prev, salary_template_id: "" };
          }
          return prev;
        });
      })
      .catch((err) => {
        console.error("Failed to fetch salary templates:", err);
        if (!cancelled) setSalaryTemplates([]);
      });

    // Same employee-based list as People Hierarchy: active Admin / HR / Manager
    // (or an employee already leading a team). Nothing is pre-selected.
    if (!isEditMode) {
      const ROLE_ORDER = { admin: 0, hr: 1, manager: 2 };
      getOrgHierarchyForest(20)
        .then((forest) => {
          if (cancelled) return;
          const list = indexForest(Array.isArray(forest) ? forest : []).list
            .filter(isEligibleManagerNode)
            .map((n) => ({
              employee_id: String(n.employee_id),
              employee_name: n.employee_name,
              employee_code: n.employee_code,
              role: hierarchyRoleOf(n).role,
            }))
            .sort(
              (a, b) =>
                (ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9)
                || String(a.employee_name).localeCompare(String(b.employee_name))
            );
          setReportingManagers(list);
        })
        .catch((err) => {
          console.error("Failed to fetch reporting managers:", err);
          if (!cancelled) setReportingManagers([]);
        });
    }

    if (!isEditMode && !initialCodeLoadedRef.current) {
      initialCodeLoadedRef.current = true;
      (async () => {
        let cfg = {
          auto_generate: true,
          use_prefix: true,
          prefix: "EMP",
          starting_number: 1,
          number_length: 4,
        };
        try {
          const { data } = await API.get("/organisation/me/hr-settings");
          cfg = resolveOrgNumbering(data?.hr_settings || {});
          const payroll = data?.payroll_settings || data?.hr_settings?.payroll || {};
          const etList = Array.isArray(payroll.employment_types) ? payroll.employment_types : [];
          if (etList.length > 0) {
            setEmploymentTypes(etList.filter((t) => t && (typeof t === "string" ? t.trim() : t.name?.trim())));
          }
        } catch {
          try {
            const { data } = await API.get("/setup/status");
            cfg = resolveOrgNumbering(data?.org || {});
          } catch {
            /* use default */
          }
        }
        setCodeAuto(cfg.auto_generate);
        setCodeUsePrefix(cfg.use_prefix);
        setCodeNumberLength(cfg.number_length);
        setCodePrefix(cfg.use_prefix ? cfg.prefix : "");
        codeModeRef.current = {
          auto: cfg.auto_generate,
          usePrefix: cfg.use_prefix,
          length: cfg.number_length,
        };
        if (cfg.auto_generate) {
          await fetchNextEmployeeCode(cfg.use_prefix ? cfg.prefix : "", { usePrefix: cfg.use_prefix });
        } else {
          const digits = padCodeNumber(String(cfg.starting_number), cfg.number_length);
          setCodeNumber(digits);
          setForm((prev) => ({
            ...prev,
            employee_code: composeEmployeeCode(cfg.prefix, digits, cfg.use_prefix, cfg.number_length),
          }));
          setCodeLoading(false);
        }
      })();
    }

    return () => {
      cancelled = true;
    };
    // fetchNextEmployeeCode is stable enough; this should run when edit mode changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode]);

  useEffect(() => {
    if (!isEditMode) return;
    (async () => {
      try {
        const { data } = await API.get("/organisation/me/hr-settings");
        const payroll = data?.payroll_settings || data?.hr_settings?.payroll || {};
        const etList = Array.isArray(payroll.employment_types) ? payroll.employment_types : [];
        if (etList.length > 0) {
          setEmploymentTypes(etList.filter((t) => t && (typeof t === "string" ? t.trim() : t.name?.trim())));
        }
      } catch { /* use defaults */ }
    })();
  }, [isEditMode]);

  /* Jump to Banking (or requested step) when opened from dashboard missing-bank alert. */
  useEffect(() => {
    if (!isEditMode) return;
    if (resolvedInitialStep >= 1 && resolvedInitialStep <= 5) {
      setCurrentStep(resolvedInitialStep);
    }
  }, [isEditMode, employeeId, resolvedInitialStep]);

  /* Employees still missing a verified bank account — same order as the dashboard alert. */
  useEffect(() => {
    if (!isEditMode || !effectiveNeedsBank) {
      setBankQueue([]);
      setBankQueueLoaded(false);
      return undefined;
    }
    let cancelled = false;
    setBankQueueLoaded(false);
    getEmployees({
      missing_verified_bank: true,
      active_only: true,
      page: 1,
      page_size: 200,
    })
      .then((data) => {
        if (cancelled) return;
        const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
        setBankQueue(items);
      })
      .catch(() => {
        if (!cancelled) setBankQueue([]);
      })
      .finally(() => {
        if (!cancelled) setBankQueueLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isEditMode, effectiveNeedsBank, employeeId]);

  useEffect(() => {
    if (!isEditMode || !effectiveNeedsBank) return;
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [employeeId, isEditMode, effectiveNeedsBank]);

  /* ── Load employee in edit mode ─── */
  useEffect(() => {
    if (!isEditMode || !employeeId) return;
    const loadEmployee = async () => {
      setLoadingEmployee(true);
      setLoadEmployeeError("");
      try {
        const emp = await getEmployee(employeeId);
        setWelcomeEmailStatus(readWelcomeEmailStatus(emp));
        setSavedEmployeeEmail(String(emp?.email || ""));
        const mapped = mapEmployeeToForm(emp);
        let rawPhone = String(mapped.mobile_phone || "").trim();
        if (rawPhone.startsWith("+91")) rawPhone = rawPhone.slice(3);
        else if (rawPhone.startsWith("91") && rawPhone.length > 10) rawPhone = rawPhone.slice(2);
        mapped.mobile_phone = sanitizePhoneInput(rawPhone);
        if (!mapped.display_name) {
          mapped.display_name = mapped.employee_name;
        }
        displayNameManualRef.current = Boolean(
          mapped.display_name.trim()
          && mapped.display_name.trim() !== mapped.employee_name.trim()
        );
        try {
          const structure = await getEmployeeSalaryStructure(employeeId);
          const templateId = structure?.template_id;
          if (templateId) {
            mapped.salary_template_id = String(templateId);
            setSalaryPackageAssigned(true);
          }
        } catch {
          /* no salary package yet */
        }
        setForm(mapped);
        setLoadedEmployee(emp);
        setCreatedEmployeeCode(emp.employee_code || "");
        try {
          const stat = await getEmployeeStatutory(employeeId);
          if (stat) {
            setExistingStatutory(true);
            setHasStatutoryDetails(true);
            setStatutoryForm(mapApiStatutoryToForm(stat));
            setStatutoryAadhaarLast4(stat.aadhaar_last4 || "");
          } else {
            setExistingStatutory(false);
            setHasStatutoryDetails(false);
            setStatutoryForm(STATUTORY_EMPTY());
            setStatutoryAadhaarLast4("");
          }
        } catch {
          setExistingStatutory(false);
          setStatutoryForm(STATUTORY_EMPTY());
          setStatutoryAadhaarLast4("");
        }
        if (effectiveNeedsBank || resolvedInitialStep === 4) {
          setCurrentStep(4);
        }
      } catch (err) {
        const detail = err?.response?.data?.detail;
        setLoadEmployeeError(
          typeof detail === "string" ? detail : "Failed to load employee. They may have been removed."
        );
      } finally {
        setLoadingEmployee(false);
      }
    };
    loadEmployee();
  }, [isEditMode, employeeId, effectiveNeedsBank, resolvedInitialStep]);

  const handleChange = (e) => {
    const { name } = e.target;
    let value = e.target.value;

    if (name === "mobile_phone") {
      value = sanitizePhoneInput(value);
    }

    if (name === "display_name") {
      displayNameManualRef.current = true;
      // If user matches employee name again, resume auto-sync.
      if (String(value || "").trim() === String(form.employee_name || "").trim()) {
        displayNameManualRef.current = false;
      }
    }

    setForm((prev) => {
      if (name === "employee_name") {
        const parts = splitEmployeeName(value);
        const next = {
          ...prev,
          employee_name: value,
          first_name: parts.first_name,
          middle_name: parts.middle_name,
          last_name: parts.last_name,
        };
        if (!displayNameManualRef.current) {
          next.display_name = value;
        }
        return next;
      }
      return { ...prev, [name]: value };
    });
    if (
      stepErrors[name]
      || (name === "employee_name" && (stepErrors.first_name || stepErrors.last_name || stepErrors.display_name))
      || (name === "mobile_phone" && stepErrors.mobile_phone)
    ) {
      setStepErrors((prev) => {
        const n = { ...prev };
        delete n[name];
        if (name === "employee_name") {
          delete n.first_name;
          delete n.last_name;
          if (!displayNameManualRef.current) delete n.display_name;
        }
        return n;
      });
    }
  };

  const handleDobChange = (value) => {
    setForm((prev) => ({ ...prev, date_of_birth: value || "" }));
    setStepErrors((prev) => {
      const next = { ...prev };
      if (!value) {
        delete next.date_of_birth;
        return next;
      }
      const dobError = validateDateOfBirth(value);
      if (dobError) next.date_of_birth = dobError;
      else delete next.date_of_birth;
      return next;
    });
  };

  /* ── Validation ─── */
  const validateStep = (step) => {
    const errors = {};
    if (step === 1) {
      if (!String(form.employee_name || "").trim()) {
        errors.employee_name = "Enter the employee's full name.";
      }
      const emailErr = validateWorkEmail(form.work_email);
      if (emailErr) errors.work_email = emailErr;
      const phoneErr = validateMobilePhone(form.mobile_phone);
      if (phoneErr) errors.mobile_phone = phoneErr;
      if (!form.gender.trim()) {
        errors.gender = "Select a gender from the list.";
      }
      if (!String(form.display_name || "").trim()) {
        errors.display_name = "Display name is required.";
      }
      if (!String(form.date_of_birth || "").trim()) {
        errors.date_of_birth = "Date of birth is required.";
      } else {
        const dobError = validateDateOfBirth(form.date_of_birth);
        if (dobError) errors.date_of_birth = dobError;
      }
      if (!isEditMode) {
        const digits = String(codeNumber || "").replace(/\D/g, "");
        if (!digits) {
          errors.employee_code = "Enter the employee code number.";
        } else if (codeUsePrefix && !String(codePrefix || "").replace(/[^a-zA-Z]/g, "")) {
          errors.employee_code = "Enter a letter prefix, or turn prefix off for numbers only.";
        }
      }
    }
    if (step === 2) {
      if (!form.employee_type?.trim()) {
        errors.employee_type = "Select an Employment Type (Permanent, Contract, etc.).";
      }
      if (!form.date_of_joining.trim()) {
        errors.date_of_joining = "Select the date of joining.";
      }
      if (!form.role?.trim()) {
        errors.role = "Select a system access role for this employee.";
      }
      if (!form.department_id) {
        errors.department_id = "Select a department — create one in Organisation Setup if the list is empty.";
      }
      if (!form.designation_id) {
        errors.designation_id = "Select a designation — create one in Organisation Setup if the list is empty.";
      }
      if (!form.location_id) {
        errors.location_id = "Select a work location — create one in Organisation Setup if the list is empty.";
      }
    }
    if (step === 3) {
      if (!String(form.annual_ctc).trim()) {
        errors.annual_ctc = "Enter annual CTC in rupees (numbers only).";
      } else if (isNaN(parseFloat(form.annual_ctc))) {
        errors.annual_ctc = "Annual CTC must be a valid number (e.g. 600000).";
      } else if (parseFloat(form.annual_ctc) <= 0) {
        errors.annual_ctc = "Annual CTC must be greater than zero.";
      }
      if (salaryTemplates.length >= 2 && !String(form.salary_template_id || "").trim()) {
        const hasDefault = salaryTemplates.some((t) => t.is_default);
        if (!hasDefault) {
          errors.salary_template_id = "Select a salary template from the list.";
        }
      }
    }
    return errors;
  };

  const validateBankingDraft = () => {
    const api = bankSectionRef.current;
    if (!api) return { ok: true, empty: true, message: "" };
    return api.validate();
  };

  const validateStatutory = () => {
    const errors = collectStatutoryFormErrors(statutoryForm, [
      "uan_number",
      "pan",
      // "aadhaar",
      "pf_number",
      "esic_number",
      // "lwf_number",
    ]);
    return errors;
  };

  const hasStatutoryDraftData = () =>
    Boolean(
      statutoryForm.uan_number.trim()
      || statutoryForm.pan.trim()
      // || statutoryForm.aadhaar.trim()
      || statutoryForm.pf_number.trim()
      || statutoryForm.esic_number.trim()
      // || statutoryForm.lwf_number.trim()
      || statutoryForm.pf_member
      || statutoryForm.esi_applicable
    );

  const unfilledStatutoryIds = STATUTORY_ID_FIELDS.filter((f) => {
    if (String(statutoryForm[f.key] || "").trim()) return false;
    if (f.key === "uan_number" || f.key === "pf_number") return !!statutoryForm.pf_member;
    if (f.key === "esic_number") return !!statutoryForm.esi_applicable;
    return true;
  });

  const handleNext = () => {
    const errors = validateStep(currentStep);
    if (Object.keys(errors).length > 0) {
      setStepErrors(errors);
      setSubmitError(
        "Please fix the highlighted fields on this step, then click Next again."
      );
      scrollToFirstError(errors);
      return;
    }
    setStepErrors({});
    setSubmitError("");
    setCurrentStep((s) => Math.min(s + 1, 5));
  };

  const handleBack = () => {
    setStepErrors({});
    setSubmitError("");
    setCurrentStep((s) => Math.max(s - 1, 1));
  };

  const handleStepSelect = (step) => {
    const next = Number(step);
    if (!Number.isInteger(next) || next < 1 || next > 5 || next === currentStep) return;
    setStepErrors({});
    setSubmitError("");
    setCurrentStep(next);
  };

  const formatApiDetail = (detail, fallback = "Request failed. Please try again.") => {
    if (Array.isArray(detail) && detail.length) {
      return detail.map((d) => {
        const path = Array.isArray(d?.loc) ? d.loc.join(".") : "";
        return `${path ? `${path}: ` : ""}${d?.msg || "Invalid value"}`;
      }).join(" | ");
    }
    if (typeof detail === "string" && detail.trim()) return detail;
    if (detail && typeof detail === "object") {
      if (typeof detail.message === "string" && detail.message.trim()) {
        const errs = Array.isArray(detail.errors) ? detail.errors : [];
        if (errs.length) {
          const parts = errs.map((e) => {
            if (Array.isArray(e) && e.length >= 2) return `${e[0]}: ${e[1]}`;
            return String(e);
          });
          return `${detail.message} (${parts.join(", ")})`;
        }
        return detail.message;
      }
    }
    return fallback;
  };

  const buildSubmitPayload = () => {
    const nameParts = splitEmployeeName(form.employee_name || form.first_name);
    const payload = {
      ...form,
      first_name: nameParts.first_name,
      middle_name: nameParts.middle_name || undefined,
      last_name: nameParts.last_name || undefined,
    };
    const emailErr = validateWorkEmail(form.work_email);
    if (emailErr) throw new Error(emailErr);
    if (!payload.first_name) throw new Error("FIRST_NAME_REQUIRED");
    const phoneErr = validateMobilePhone(form.mobile_phone);
    if (phoneErr) throw new Error(phoneErr);
    if (!payload.date_of_birth) {
      throw new Error("Date of birth is required.");
    }
    const dobError = validateDateOfBirth(payload.date_of_birth);
    if (dobError) throw new Error(dobError);
    payload.email     = form.work_email;
    payload.work_email = form.work_email;
    const cleanPhone = sanitizePhoneInput(form.mobile_phone);
    payload.mobile_phone = `+91${cleanPhone}`;
    payload.phone = payload.mobile_phone;
    if (!String(payload.display_name || "").trim()) {
      throw new Error("Display name is required.");
    }
    payload.annual_ctc = payload.annual_ctc ? parseFloat(payload.annual_ctc) : undefined;
    payload.is_active  = payload.is_active === "true" || payload.is_active === true;
    Object.keys(payload).forEach((k) => {
      if (payload[k] === "" || payload[k] === null || payload[k] === undefined) delete payload[k];
    });
    delete payload._created_employee_id;
    delete payload.employee_name;
    delete payload.salary_template_id;
    // Reporting manager is an employee id (same line People Hierarchy manages).
    // In edit mode ReportingInfoSection saves it directly, so never send it from the form.
    delete payload.reporting_manager_id;
    if (isEditMode) delete payload.manager_id;
    return payload;
  };

  /** Build nested atomic onboard payload from in-memory wizard state (create only). */
  const buildOnboardPayload = ({ forceSkipStatutory = false } = {}) => {
    const employee = buildSubmitPayload();
    if (form.employee_code) {
      employee.employee_code = composeEmployeeCode(
        codePrefix,
        codeNumber,
        codeUsePrefix,
        codeNumberLength,
      ) || form.employee_code;
    }

    const templateId = String(
      form.salary_template_id
      || (salaryTemplates.length === 1 ? salaryTemplates[0].template_id : "")
      || ""
    ).trim();

    let compensation = null;
    if (templateId && form.annual_ctc) {
      compensation = {
        template_id: templateId,
        ctc: Number(form.annual_ctc),
        effective_from: form.date_of_joining || firstOfCurrentMonthIso(),
      };
    }

    let banking = null;
    const bankCheck = validateBankingDraft();
    if (!bankCheck.empty && bankSectionRef.current?.hasData?.()) {
      const draft = bankSectionRef.current.getDraft();
      const holderFallback = (
        form.employee_name
        || [form.first_name, form.last_name].filter(Boolean).join(" ")
        || "Employee"
      ).trim();
      const acctNum = String(draft.account_number || "").replace(/[\s-]/g, "").toUpperCase();
      const confirm = String(draft.confirm_account_number || "").replace(/[\s-]/g, "").toUpperCase();
      banking = {
        bank_branch_id: draft.bank_branch_id,
        account_holder_name: String(draft.account_holder_name || holderFallback).trim() || holderFallback,
        account_number: acctNum,
        confirm_account_number: confirm,
        upi_vpa: draft.upi_vpa || null,
        account_type: draft.account_type || "savings",
        payment_method: draft.payment_method || "salary_transfer",
        is_primary: draft.is_primary !== false,
        effective_from: toDateInput(draft.effective_from) || form.date_of_joining || todayIso(),
      };
    }

    let statutory = null;
    if (!forceSkipStatutory && hasStatutoryDraftData()) {
      statutory = {
        uan_number: blankToNull(statutoryForm.uan_number),
        pf_number: blankToNull(statutoryForm.pf_number),
        pf_member: statutoryForm.pf_member,
        esic_number: blankToNull(statutoryForm.esic_number),
        esi_applicable: statutoryForm.esi_applicable,
        pt_number: null,
        pt_applicable: statutoryForm.pt_applicable,
        lwf_number: blankToNull(statutoryForm.lwf_number),
        lwf_applicable: statutoryForm.lwf_applicable,
        bonus_applicable: statutoryForm.bonus_applicable,
        pan: blankToNull(statutoryForm.pan),
        aadhaar: blankToNull(statutoryForm.aadhaar),
        employment_category: blankToNull(statutoryForm.employment_category),
        effective_from: statutoryForm.effective_from || todayIso(),
      };
    }

    return { employee, compensation, banking, statutory };
  };

  /** Edit mode only — update the existing employee profile record. */
  const persistEmployeeRecord = async () => {
    const payload = buildSubmitPayload();
    const updated = await updateEmployee(employeeId, payload);
    setWelcomeEmailStatus(readWelcomeEmailStatus(updated));
    setSavedEmployeeEmail(String(updated?.email || payload.email || ""));
    setWelcomeSendResult(null);
    const id = String(employeeId);
    setCreatedEmployeeId(id);
    setForm((prev) => ({ ...prev, _created_employee_id: id }));
    invalidateEmployeesCache();
    invalidateDashboardOverview(queryClient);
    return id;
  };

  const handleSaveStatutoryDraft = async (employeeIdOverride = null) => {
    if (!hasStatutoryDraftData() && !existingStatutory) return true;
    const errors = validateStatutory();
    if (Object.keys(errors).length > 0) {
      setStatutoryErrors(errors);
      setSubmitError(
        `Statutory: ${errors[Object.keys(errors)[0]] || "Please fix statutory details before saving."}`
      );
      return false;
    }
    setStatutoryErrors({});
    const empId = String(employeeIdOverride || effectiveEmployeeId || "");
    if (!empId) {
      setSubmitError("Employee record is missing. Complete all steps and click Save on Statutory.");
      return false;
    }
    try {
      const payload = {
        employee_id: empId,
        uan_number: blankToNull(statutoryForm.uan_number),
        pf_number: blankToNull(statutoryForm.pf_number),
        pf_member: statutoryForm.pf_member,
        esic_number: blankToNull(statutoryForm.esic_number),
        esi_applicable: statutoryForm.esi_applicable,
        pt_number: blankToNull(statutoryForm.pt_number),
        pt_applicable: statutoryForm.pt_applicable,
        lwf_number: blankToNull(statutoryForm.lwf_number),
        lwf_applicable: statutoryForm.lwf_applicable,
        bonus_applicable: statutoryForm.bonus_applicable,
        pan: blankToNull(statutoryForm.pan),
        aadhaar: blankToNull(statutoryForm.aadhaar),
        employment_category: blankToNull(statutoryForm.employment_category),
        effective_from: statutoryForm.effective_from,
      };
      if (existingStatutory) {
        const { employee_id: _omit, ...updatePayload } = payload;
        const saved = await upsertEmployeeStatutory(empId, updatePayload);
        if (saved?.aadhaar_last4) setStatutoryAadhaarLast4(saved.aadhaar_last4);
      } else {
        const saved = await createEmployeeStatutory(empId, payload);
        setExistingStatutory(true);
        if (saved?.aadhaar_last4) setStatutoryAadhaarLast4(saved.aadhaar_last4);
      }
      setStatutoryForm((prev) => ({ ...prev, aadhaar: "" }));
      return true;
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const fieldErrs = statutoryErrorsFromApiDetail(detail);
      if (Object.keys(fieldErrs).length) setStatutoryErrors(fieldErrs);
      setSubmitError(
        typeof detail === "string"
          ? detail
          : (detail?.message ? formatApiDetail(detail) : "Failed to save statutory details.")
      );
      return false;
    }
  };

  const bankingGuidedError = (rawMessage, fieldErrors = null) => {
    const message = String(rawMessage || "").trim()
      || "Banking details were not saved. Complete the highlighted fields on Step 4.";
    const flat = {};
    if (fieldErrors && typeof fieldErrors === "object") {
      Object.entries(fieldErrors).forEach(([key, msg]) => {
        if (msg && typeof msg === "object" && !Array.isArray(msg)) {
          Object.entries(msg).forEach(([inner, innerMsg]) => {
            if (innerMsg) flat[inner] = innerMsg;
          });
        } else if (msg) {
          flat[key] = msg;
        }
      });
    }
    const fromFields = Object.entries(flat)
        .filter(([, msg]) => msg)
        .map(([key, msg]) => {
          const labels = {
            ifsc: "IFSC",
            ifsc_code: "IFSC",
            bank_name: "Bank name",
            bank_branch_id: "Bank branch",
            account_holder_name: "Account holder name",
            account_number: "Account number",
            confirm_account_number: "Confirm account number",
          };
          const label = labels[key] || key;
          const detail = String(msg).trim();
          return detail.toLowerCase().includes(String(label).toLowerCase())
            ? detail
            : `${label}: ${detail}`;
        });
    const detailSteps = fromFields.length
      ? fromFields.map((text) => `Fix — ${text}`)
      : [
        "Enter a valid IFSC so the bank and branch fill in",
        "Enter account holder name, account number, and confirm account number",
      ];
    return {
      title: "Banking details were not saved",
      message,
      hint: "The employee profile is unchanged. Only the bank account save failed — fix the fields listed, then try again.",
      steps: [
        ...detailSteps,
        "Click Next after every required field on Banking is filled",
      ],
      guideLinks: [],
    };
  };

  const openNextMissingBankEmployee = async () => {
    const currentId = String(employeeId || "");
    let items = bankQueue;
    try {
      const data = await getEmployees({
        missing_verified_bank: true,
        active_only: true,
        page: 1,
        page_size: 200,
      });
      items = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
      setBankQueue(items);
      setBankQueueLoaded(true);
    } catch {
      /* use the queue already on screen */
    }
    const stillMissing = new Set(items.map((emp) => queueEmployeeId(emp)).filter(Boolean));
    const currentIndex = bankQueue.findIndex((emp) => queueEmployeeId(emp) === currentId);
    const forward = (currentIndex >= 0 ? bankQueue.slice(currentIndex + 1) : bankQueue)
      .find((emp) => stillMissing.has(queueEmployeeId(emp)));
    const next = forward || items.find((emp) => queueEmployeeId(emp) && queueEmployeeId(emp) !== currentId);
    invalidateDashboardOverview(queryClient);
    if (next) {
      navigate(`/employees/${queueEmployeeId(next)}/edit?needs_bank=1&step=4`);
      return;
    }
    navigate("/dashboard");
  };

  const handleStep4Next = async () => {
    if (isEditMode) {
      // Persist any open bank form before leaving step 4 (section stays mounted).
      if (bankSectionRef.current?.flushSave) {
        setBankSaving(true);
        setSubmitError("");
        try {
          const result = await bankSectionRef.current.flushSave();
          if (!result?.ok) {
            setSubmitError(
              bankingGuidedError(
                result?.message || "Banking details were not saved.",
                result?.errors
              )
            );
            requestAnimationFrame(() => {
              document.querySelector(".ec-bank-card, .ebank-form")?.scrollIntoView?.({
                behavior: "smooth",
                block: "start",
              });
            });
            return;
          }
          if (result?.empty === false) setHasBankAccounts(true);
        } finally {
          setBankSaving(false);
        }
      }
      setSubmitError("");
      if (effectiveNeedsBank) {
        setBankSaving(true);
        try {
          await openNextMissingBankEmployee();
        } finally {
          setBankSaving(false);
        }
        return;
      }
      handleNext();
      return;
    }
    // Create: banking stays local until final Save — only validate if user entered data.
    const check = validateBankingDraft();
    if (!check.ok) {
      setSubmitError(
        bankingGuidedError(
          check.message || "Banking details are incomplete.",
          check.errors
        )
      );
      return;
    }
    setSubmitError("");
    setSkipBanking(!!check.empty);
    handleNext();
  };

  /** Explicit Skip on Banking — discard draft and continue without banking. */
  const handleSkipBanking = () => {
    bankSectionRef.current?.clear?.();
    setSubmitError("");
    setSkipBanking(true);
    setStepErrors({});
    setCurrentStep(5);
  };

  const handleFinishComplete = async ({ forceSkipStatutory = false } = {}) => {
    setLoading(true);
    setSubmitError("");
    try {
      // Re-validate every step before any API write.
      for (const step of [1, 2, 3]) {
        const errors = validateStep(step);
        if (Object.keys(errors).length > 0) {
          setStepErrors(errors);
          setCurrentStep(step);
          const firstMsg = errors[Object.keys(errors)[0]];
          setSubmitError(
            `Step ${step}: ${firstMsg} Fix the highlighted field(s), then save again.`
          );
          setTimeout(() => scrollToFirstError(errors), 80);
          return;
        }
      }
      if (!isEditMode) {
        const bankCheck = validateBankingDraft();
        if (!bankCheck.ok) {
          setSubmitError(
            `${bankCheck.message || "Please fix the banking details before saving."} Go to Banking and correct the highlighted fields, or click Skip.`
          );
          setCurrentStep(4);
          return;
        }
        if (!forceSkipStatutory && hasStatutoryDraftData()) {
          const se = validateStatutory();
          if (Object.keys(se).length > 0) {
            setStatutoryErrors(se);
            const firstKey = Object.keys(se)[0];
            setSubmitError(
              `Statutory: ${se[firstKey] || "Please fix statutory details before saving."} Correct the field on this step, or clear it to finish without statutory details.`
            );
            setTimeout(() => scrollToFirstError(se), 80);
            return;
          }
        }

        // ── Create: single atomic onboard API (no DB writes until this call) ──
        try {
          const onboardPayload = buildOnboardPayload({ forceSkipStatutory });
          const result = await onboardEmployee(onboardPayload);
          const newId = String(result?.employee_id || "");
          if (!newId) {
            throw new Error("Employee was onboarded but no ID was returned.");
          }
          setCreatedEmployeeCode(result?.employee_code || form.employee_code || "");
          setCreatedEmployeeId(newId);
          setForm((prev) => ({ ...prev, _created_employee_id: newId }));
          setSalaryPackageAssigned(!!result?.compensation_saved);
          setCompensationAutoAssigned(!!result?.compensation_auto_assigned);
          setAutoAssignedTemplateName(result?.default_template_name || "");
          setHasBankAccounts(!!result?.banking_saved);
          setSkipBanking(!result?.banking_saved);
          setHasStatutoryDetails(!!result?.statutory_saved);
          setSkipStatutory(!result?.statutory_saved);
          if (forceSkipStatutory) {
            setStatutoryForm(STATUTORY_EMPTY());
            setStatutoryErrors({});
          }
          setSubmitSuccess(true);
          invalidateEmployeesCache();
          invalidateDashboardOverview(queryClient);
          void setupProgressCtx?.refetch?.();
          return;
        } catch (error) {
          if (error?.message === "FIRST_NAME_REQUIRED") {
            setCurrentStep(1);
            setStepErrors({ employee_name: "Enter the employee's full name." });
            setSubmitError("Employee name is required. Fix it on Basic Details, then save again.");
            setTimeout(() => scrollToFirstError({ employee_name: true }), 80);
            return;
          }
          if (
            error?.message?.includes("date of birth")
            || error?.message?.includes("4-digit year")
            || error?.message?.includes("email")
            || error?.message?.includes("Phone")
            || error?.message?.includes("phone")
          ) {
            setCurrentStep(1);
            setSubmitError(`${error.message} Fix it on Basic Details, then save again.`);
            return;
          }
          console.error(error);
          const detail = error?.response?.data?.detail;
          const fieldErrs = statutoryErrorsFromApiDetail(detail);
          if (Object.keys(fieldErrs).length) {
            setStatutoryErrors((prev) => ({ ...prev, ...fieldErrs }));
            setCurrentStep(5);
          }
          const msg = formatApiDetail(
            detail,
            error?.message || "Could not onboard the employee. Nothing was saved — fix the errors and try again."
          );
          const lower = String(msg).toLowerCase();
          if (Object.keys(fieldErrs).length || lower.includes("uan") || lower.includes("pan") || lower.includes("aadhaar") || lower.includes("statutory") || lower.includes("pf member") || (lower.includes("pf") && lower.includes("in use"))) {
            setCurrentStep(5);
          } else if (lower.includes("email") || lower.includes("mobile") || lower.includes("phone") || lower.includes("name")) {
            setCurrentStep(1);
          } else if (lower.includes("bank") || lower.includes("account") || lower.includes("ifsc")) {
            setCurrentStep(4);
          } else if (lower.includes("salary") || lower.includes("ctc") || lower.includes("template")) {
            setCurrentStep(3);
          } else if (lower.includes("department") || lower.includes("designation") || lower.includes("manager") || lower.includes("location")) {
            setCurrentStep(2);
          }
          setSubmitError(msg);
          return;
        }
      }

      // ── Edit mode: update profile (+ optional salary / statutory section) ──
      let empId;
      try {
        empId = await persistEmployeeRecord();
      } catch (error) {
        if (error?.message === "FIRST_NAME_REQUIRED") {
          setCurrentStep(1);
          setStepErrors({ employee_name: "Enter the employee's full name." });
          setSubmitError("Employee name is required. Fix it on Basic Details, then save again.");
          setTimeout(() => scrollToFirstError({ employee_name: true }), 80);
          return;
        }
        if (error?.message?.includes("date of birth") || error?.message?.includes("4-digit year") || error?.message?.includes("email") || error?.message?.includes("Phone") || error?.message?.includes("phone")) {
          setCurrentStep(1);
          setSubmitError(`${error.message} Fix it on Basic Details, then save again.`);
          return;
        }
        console.error(error);
        const detail = error?.response?.data?.detail;
        setSubmitError(formatApiDetail(
          detail,
          error?.message || "Could not update the employee. Check the highlighted fields and try again."
        ));
        return;
      }

      const templateId = String(
        form.salary_template_id
        || (salaryTemplates.length === 1 ? salaryTemplates[0].template_id : "")
        || ""
      ).trim();
      if (templateId && form.annual_ctc) {
        try {
          await assignEmployeeSalary({
            employee_id: empId,
            template_id: templateId,
            ctc: Number(form.annual_ctc),
            effective_from: form.date_of_joining || firstOfCurrentMonthIso(),
          });
          setSalaryPackageAssigned(true);
        } catch (assignErr) {
          const detail = assignErr?.response?.data?.detail;
          setSubmitError(
            typeof detail === "string"
              ? detail
              : "Employee saved, but salary template could not be assigned. Assign it from Salary Package."
          );
          setSalaryPackageAssigned(false);
        }
      }

      if (bankSectionRef.current?.flushSave) {
        const bankResult = await bankSectionRef.current.flushSave();
        if (!bankResult?.ok) {
          setCurrentStep(4);
          setSubmitError(
            bankingGuidedError(
              bankResult?.message || "Banking details were not saved.",
              bankResult?.errors
            )
          );
          return;
        }
        if (bankResult?.empty === false) setHasBankAccounts(true);
      }

      if (!forceSkipStatutory) {
        const statOk = await handleSaveStatutoryDraft(empId);
        if (!statOk) {
          setCurrentStep(5);
          return;
        }
        if (hasStatutoryDraftData() || existingStatutory) {
          setHasStatutoryDetails(true);
          setSkipStatutory(false);
        }
      } else if (forceSkipStatutory) {
        setHasStatutoryDetails(false);
        setSkipStatutory(true);
      }
      setSkipBanking(!hasBankAccounts);
      setSubmitSuccess(true);
    } finally {
      setLoading(false);
    }
  };

  const handleFinishClick = () => {
    if (isEditMode) {
      handleFinishComplete();
      return;
    }
    if (!hasStatutoryDraftData()) {
      setShowStatutorySkipModal(true);
      return;
    }
    handleFinishComplete();
  };

  const confirmFinishWithoutStatutory = async () => {
    setShowStatutorySkipModal(false);
    setStatutoryErrors({});
    setSubmitError("");
    await handleFinishComplete({ forceSkipStatutory: true });
  };

  useEffect(() => {
    if (!showStatutorySkipModal) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setShowStatutorySkipModal(false);
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [showStatutorySkipModal]);

  const fullName = (form.employee_name || [form.first_name, form.last_name].filter(Boolean).join(" ")).trim();
  const bankQueueIndex = bankQueue.findIndex((emp) => queueEmployeeId(emp) === String(employeeId || ""));
  const bankQueuePosition = bankQueueIndex >= 0 ? bankQueueIndex + 1 : (bankQueue.length ? 1 : 0);
  const bankQueueTotal = bankQueue.length || (bankQueuePosition ? 1 : 0);
  const nextBankEmployee = bankQueueIndex >= 0
    ? bankQueue[bankQueueIndex + 1]
    : bankQueue.find((emp) => queueEmployeeId(emp) && queueEmployeeId(emp) !== String(employeeId || ""));
  const bankActionLabel = !(isEditMode && effectiveNeedsBank)
    ? "Next"
    : !bankQueueLoaded
      ? "Save bank details"
      : nextBankEmployee
        ? "Save & next employee"
        : "Save & finish";

  /* ── Loading employee (edit mode) ─── */
  if (loadingEmployee) {
    return (
      <div className={`ec-page${embedded ? " ec-embedded" : " ec-page--full"}`} style={{ fontFamily: "'Inter', sans-serif" }}>
        <style>{EC_CSS}</style>
        <div className="ec-wrap">
          {!embedded && (
          <div className="ec-hero" style={{ borderRadius: 20 }}>
            <div className="ec-hero-inner">
              <div>
                <div className="ec-hero-badge"><EI.Pencil /> Edit Mode</div>
                <div className="ec-hero-title">Loading Employee…</div>
              </div>
            </div>
          </div>
          )}
          <div className={`ec-card${embedded ? "" : ""}`} style={{ borderRadius: embedded ? 20 : "0 0 20px 20px" }}>
            <BrixigoPageLoader message="Fetching employee record…" />
          </div>
        </div>
      </div>
    );
  }

  /* ── Load error ─── */
  if (loadEmployeeError) {
    return (
      <div className={`ec-page${embedded ? " ec-embedded" : " ec-page--full"}`} style={{ fontFamily: "'Inter', sans-serif" }}>
        <style>{EC_CSS}</style>
        <div className="ec-wrap">
          {!embedded && (
          <div className="ec-hero" style={{ borderRadius: 20 }}>
            <div className="ec-hero-inner">
              <div>
                <div className="ec-hero-badge"><EI.AlertTriangle /> Error</div>
                <div className="ec-hero-title">Could not load employee</div>
              </div>
            </div>
          </div>
          )}
          <div className="ec-card" style={{ borderRadius: embedded ? 20 : "0 0 20px 20px" }}>
            <div className="ec-loading">
              <EI.Frown />
              <div style={{ fontSize: 14, color: "#991b1b", fontWeight: 600, marginBottom: 16 }}>{loadEmployeeError}</div>
              <button
                type="button"
                onClick={goToListAfterSuccess}
                className="ec-btn-next"
              >
                ← Back to employee list
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ── Success screen ─── */
  if (submitSuccess) {
    // Edit with nothing left to act on: standard success screen, then back to the origin.
    const editNeedsFollowUp =
      WELCOME_RESENDABLE.has(welcomeEmailStatus)
      || Boolean(welcomeSendResult)
      || Boolean(effectiveEmployeeId && form.annual_ctc && !salaryPackageAssigned);
    if (isEditMode && !editNeedsFollowUp) {
      return (
        <div className="w-full max-w-3xl mx-auto px-4 py-8">
          <SuccessScreen
            variant="panel"
            title="Employee updated successfully!"
            message={returnsToProfile ? "Redirecting to employee profile…" : "Redirecting to employee list…"}
            onComplete={goToOriginAfterSave}
          />
        </div>
      );
    }
    const confettiColors = ["#22c55e", "#2563eb", "#a855f7", "#f59e0b", "#06b6d4", "#ef4444"];
    const checklist = [
      { label: "Basic details saved", done: true },
      { label: "Employment details assigned", done: true },
      {
        label: salaryPackageAssigned
          ? (compensationAutoAssigned
            ? `Salary template assigned automatically${autoAssignedTemplateName ? ` ("${autoAssignedTemplateName}")` : ""}`
            : "Compensation recorded")
          : "Compensation recorded",
        done: true,
      },
      {
        label: hasBankAccounts
          ? "Banking details added and approved"
          : "Banking skipped — add before payroll",
        done: hasBankAccounts,
      },
      {
        label: hasStatutoryDetails
          ? "Statutory details saved"
          : "Statutory skipped — add later from employee profile",
        done: hasStatutoryDetails,
      },
    ];
    return (
      <div className="ec-success-page" style={{ fontFamily: "'Inter', sans-serif" }}>
        <style>{EC_CSS}</style>
        <div className="ec-success-card">
          <div className="ec-success-burst" aria-hidden>
            {Array.from({ length: 18 }).map((_, i) => (
              <span
                key={`c${i}`}
                className="ec-success-confetti"
                style={{
                  left: `${8 + ((i * 5.2) % 84)}%`,
                  top: `${10 + (i % 5) * 8}%`,
                  background: confettiColors[i % confettiColors.length],
                  animationDelay: `${0.05 * i}s`,
                }}
              />
            ))}
          </div>
          <div className="flex justify-center">
            <SuccessMark />
          </div>
          <SuccessTitle as="h2" className="mb-2">
            {isEditMode ? "Employee updated successfully!" : "Employee created successfully!"}
          </SuccessTitle>
          <p className="ec-success-p" style={{ fontSize: 16, fontWeight: 700, color: "#1e3a8a" }}>
            {fullName || "Employee"}
            {isEditMode ? " — profile updated" : " — onboarding complete"}
          </p>
          <p className="ec-success-p">
            {isEditMode
              ? "All employee details have been saved successfully."
              : "The employee is live in Brixigo and ready for the next payroll run."}
          </p>
          <div className="ec-success-checklist">
            {checklist.map((item) => (
              <div key={item.label} className="ec-success-checklist-item">
                <span className={item.done ? "ok" : "skip"}>
                  {item.done ? <EI.CheckSm /> : <EI.AlertTriangle />}
                </span>
                <span>{item.label}</span>
              </div>
            ))}
          </div>
          {createdEmployeeCode && (
            <div className="ec-success-code-box">
              <div>
                <div className="ec-success-code-lbl">Employee Code</div>
                <div className="ec-success-code-val">{createdEmployeeCode}</div>
              </div>
              <EI.IdCard />
            </div>
          )}
          {(hasBankAccounts || skipBanking || (skipStatutory && !hasStatutoryDetails) || compensationAutoAssigned) && (
            <div className="ec-success-skip-note">
              {compensationAutoAssigned ? (
                <div>
                  The organisation default template{autoAssignedTemplateName ? ` ("${autoAssignedTemplateName}")` : ""} was assigned automatically because no template was chosen.
                </div>
              ) : null}
              {hasBankAccounts ? (
                <>Tip: Banking details were saved and approved for payroll disbursement.</>
              ) : skipBanking ? (
                <>Tip: Banking was skipped. Add bank details later from the employee profile before payroll.</>
              ) : null}
              {skipStatutory && !hasStatutoryDetails ? (
                <div style={{ marginTop: hasBankAccounts || skipBanking ? 8 : 0 }}>
                  Tip: Statutory was skipped — add UAN / PAN / Aadhaar later from the employee profile.
                </div>
              ) : null}
            </div>
          )}
          {isEditMode && (WELCOME_RESENDABLE.has(welcomeEmailStatus) || welcomeSendResult) && (
            <div
              className="ec-success-skip-note"
              role="status"
              style={{
                textAlign: "left",
                borderColor: welcomeSendResult?.status === "SENT" ? "#86efac" : "#fca5a5",
                background: welcomeSendResult?.status === "SENT" ? "#f0fdf4" : "#fef2f2",
              }}
            >
              {welcomeSendResult ? (
                <div>{welcomeSendResult.message}</div>
              ) : (
                <>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>
                    The previous welcome email could not be delivered.
                  </div>
                  <div>
                    You can now send the welcome email to the updated email address
                    {savedEmployeeEmail ? <> (<strong>{savedEmployeeEmail}</strong>)</> : null}.
                  </div>
                  <button
                    type="button"
                    className="ec-success-btn-salary"
                    style={{ marginTop: 10 }}
                    disabled={welcomeSending || !savedEmployeeEmail}
                    onClick={() => setWelcomeConfirmOpen(true)}
                  >
                    {welcomeSending ? "Sending…" : "Send Welcome Email"}
                  </button>
                </>
              )}
            </div>
          )}
          <ConfirmDialog
            open={welcomeConfirmOpen}
            title="Send Welcome Email?"
            message={`Login details will be sent to: ${savedEmployeeEmail}. Do you want to continue?`}
            confirmLabel="Send Welcome Email"
            cancelLabel="Cancel"
            loading={welcomeSending}
            onCancel={() => { if (!welcomeSending) setWelcomeConfirmOpen(false); }}
            onConfirm={async () => {
              if (welcomeSending) return;
              setWelcomeSending(true);
              try {
                const result = await sendEmployeeWelcomeEmail(effectiveEmployeeId || employeeId);
                setWelcomeSendResult(result);
                setWelcomeEmailStatus(result?.status || null);
              } catch (err) {
                const detail = err?.response?.data?.detail;
                setWelcomeSendResult({
                  status: "ERROR",
                  message: (detail && typeof detail === "object" ? detail.message : detail)
                    || "Could not send the welcome email. Please try again.",
                });
              } finally {
                setWelcomeSending(false);
                setWelcomeConfirmOpen(false);
              }
            }}
          />
          <div className="ec-success-actions">
            <button
              className="ec-success-btn-back"
              onClick={isEditMode ? goToOriginAfterSave : embedded ? goToListAfterSuccess : goBack}
            >
              {isEditMode && returnsToProfile ? "← Employee Profile" : isEditMode || embedded ? "← Employee List" : "← Go Back"}
            </button>
            {effectiveEmployeeId && form.annual_ctc && !salaryPackageAssigned ? (
              <button
                type="button"
                className="ec-success-btn-salary"
                onClick={() => {
                  const params = new URLSearchParams();
                  params.set("employee_id", String(effectiveEmployeeId));
                  params.set("ctc", String(form.annual_ctc));
                  navigate(`/employeesalarystructure?${params.toString()}`);
                }}
              >
                Assign salary package →
              </button>
            ) : null}
            {!isEditMode && (
              <button
                className="ec-success-btn-add"
                onClick={() => {
                  setSubmitSuccess(false);
                  setCreatedEmployeeCode("");
                  setCreatedEmployeeId("");
                  setSkipBanking(false);
                  setSkipStatutory(false);
                  setSalaryPackageAssigned(false);
                  setCompensationAutoAssigned(false);
                  setAutoAssignedTemplateName("");
                  displayNameManualRef.current = false;
                  bankSectionRef.current?.clear?.();
                  const defTpl = salaryTemplates.find((t) => t.is_default) || (salaryTemplates.length === 1 ? salaryTemplates[0] : null);
                  const autoTemplateId = defTpl ? String(defTpl.template_id) : "";
                  setForm({
                    employee_code: "", employee_type: DEFAULT_EMPLOYEE_TYPE, role: DEFAULT_EMPLOYEE_ROLE,
                    employee_name: "", first_name: "", middle_name: "", last_name: "", display_name: "",
                    work_email: "", mobile_phone: "", gender: "", date_of_birth: "", marital_status: "",
                    fathers_name: "", date_of_joining: firstOfCurrentMonthIso(), date_of_leaving: "", status: "active",
                    department_id: "", designation_id: "", location_id: "", business_unit: "",
                    manager_id: "", pay_structure_id: "", salary_template_id: autoTemplateId,
                    annual_ctc: "", pay_frequency: "Monthly",
                    uan_link_status: "Unlinked", is_active: "true", _created_employee_id: "",
                  });
                  setStatutoryForm(STATUTORY_EMPTY());
                  setStatutoryErrors({});
                  setExistingStatutory(false);
                  setStatutoryAadhaarLast4("");
                  setHasBankAccounts(false);
                  setHasStatutoryDetails(false);
                  setStepErrors({});
                  setSubmitError("");
                  setCurrentStep(1);
                  if (codeAuto) {
                    fetchNextEmployeeCode(codePrefix, { usePrefix: codeUsePrefix });
                  } else {
                    setCodeNumber("");
                  }
                }}
              >
                + Add Another Employee
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  /* ══════════════════════════════════════════════════════
     MAIN RENDER
  ══════════════════════════════════════════════════════ */
  if (orgProfile && !orgProfile.status.loading && orgProfile.noOrganisation) {
    return (
      <div className="ec-page">
        <style>{EC_CSS}</style>
        <div className="op-block-page">
          <div className="op-block-card">
            <h1>Cannot add employees</h1>
            <p>Your account is not linked to an organisation. Contact your administrator.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`ec-page${embedded ? " ec-embedded" : " ec-page--full"}`}>
      <style>{EC_CSS}</style>

      <div className="ec-wrap">
        {embedded ? (
          <button type="button" className="ec-back" onClick={goBack}>
            <EI.ArrowLeft />
            Back to People Hub
          </button>
        ) : null}

        {!embedded && (
        <div style={{ marginBottom: 8 }}>
          <OrgProfileBanner />
          <SetupIncompleteBanner compact />
        </div>
        )}

        <div className="ec-card">
        <StepBar current={currentStep} onSelect={handleStepSelect} />
          {/*
            Do NOT remount this container with a changing `key` on step change.
            Remounting unmounts Banking/Statutory mid-async-save and triggers
            React removeChild race errors under StrictMode.
          */}
          <div className={`ec-body${currentStep === 2 ? " ec-body--org" : ""}`}>
            {Object.keys(stepErrors).length > 0 && (
              <div className="ec-step-error-banner" role="alert">
                <strong>Fix these fields to continue</strong>
                <ul>
                  {Object.entries(stepErrors).map(([key, msg]) => (
                    <li key={key}>{msg}</li>
                  ))}
                </ul>
              </div>
            )}
            {currentStep === 1 && (
              <Step1BasicDetails
                form={form}
                handleChange={handleChange}
                handleDobChange={handleDobChange}
                errors={stepErrors}
                codeLoading={codeLoading}
                isEdit={isEditMode}
                codePrefix={codePrefix}
                onPrefixChange={handleCodePrefixChange}
                codeNumber={codeNumber}
                onNumberChange={handleCodeNumberChange}
                codeAuto={codeAuto}
                onCodeAutoChange={handleCodeAutoChange}
                codeUsePrefix={codeUsePrefix}
                onUsePrefixChange={handleUsePrefixChange}
                onSaveOrgDefault={handleSaveOrgDefault}
                savingOrgDefault={savingOrgDefault}
              />
            )}
            {currentStep === 2 && (
              <Step2Employment
                form={form}
                handleChange={handleChange}
                errors={stepErrors}
                departments={departments}
                designations={designations}
                locations={locations}
                loading={dropdownLoading}
                dropdownError={dropdownError}
                onRefreshOrg={refreshOrgLookups}
                onOrgCreated={() => foundationCtx?.refetch?.()}
                employmentTypes={employmentTypes}
                reportingManagers={reportingManagers}
                showReportingManager={!isEditMode}
              >
                {isEditMode && employeeId && loadedEmployee ? (
                  <ReportingInfoSection
                    employeeId={employeeId}
                    employee={loadedEmployee}
                    onUpdated={async () => {
                      const fresh = await getEmployee(employeeId);
                      setLoadedEmployee(fresh);
                    }}
                  />
                ) : null}
              </Step2Employment>
            )}
            {currentStep === 3 && (
              <Step3Compensation
                form={form}
                handleChange={handleChange}
                setForm={setForm}
                errors={stepErrors}
                salaryTemplates={salaryTemplates}
              />
            )}
            {currentStep === 4 && effectiveNeedsBank && isEditMode ? (
              <div className="ec-bank-queue" role="status">
                <div className="ec-bank-queue-top">
                  <div className="ec-bank-queue-ava" aria-hidden>
                    {getInitialsFromName(fullName) || "—"}
                  </div>
                  <div>
                    <div className="ec-bank-queue-kicker">
                      {bankQueueIndex >= 0
                        ? `Adding bank account · Employee ${bankQueuePosition} of ${bankQueueTotal}`
                        : "Adding bank account"}
                    </div>
                    <div className="ec-bank-queue-name">{fullName || "Employee"}</div>
                    {form.employee_code ? (
                      <div className="ec-bank-queue-code">#{form.employee_code}</div>
                    ) : null}
                  </div>
                </div>
                <div className="ec-bank-queue-facts">
                  <span>Department: {loadedEmployee?.department?.name || "—"}</span>
                  <span>Designation: {loadedEmployee?.designation?.title || "—"}</span>
                  <span>Location: {loadedEmployee?.location?.name || "—"}</span>
                  <span>Email: {form.work_email || "—"}</span>
                  <span>Mobile: {form.mobile_phone || "—"}</span>
                  <span>Joined: {form.date_of_joining || "—"}</span>
                </div>
                {bankQueue.length > 1 ? (
                  <div className="ec-bank-queue-facts" aria-label="Employees missing a verified bank account">
                    {bankQueue.map((emp, index) => (
                      <span
                        key={queueEmployeeId(emp) || index}
                        className={index === bankQueueIndex ? "is-current" : undefined}
                      >
                        {index + 1}. {queueEmployeeName(emp)}
                        {emp.employee_code ? ` #${emp.employee_code}` : ""}
                      </span>
                    ))}
                  </div>
                ) : null}
                <div className="ec-bank-queue-next">
                  {nextBankEmployee
                    ? `Next employee: ${queueEmployeeName(nextBankEmployee)}${nextBankEmployee.employee_code ? ` (#${nextBankEmployee.employee_code})` : ""}`
                    : bankQueueLoaded
                      ? "This is the last employee missing a verified bank account."
                      : "Loading the remaining employees…"}
                </div>
              </div>
            ) : null}
            {/* Create and missing-bank setup use the Add Employee onboarding form.
                Keep it mounted so the draft survives Next → Statutory → Save. */}
            {(!isEditMode || effectiveNeedsBank) && (
              <div style={{ display: currentStep === 4 ? "block" : "none" }}>
                <Step4Banking
                  key={isEditMode ? `bank-setup-${effectiveEmployeeId}` : "bank-setup-create"}
                  sectionRef={bankSectionRef}
                  employeeId={isEditMode ? effectiveEmployeeId : undefined}
                  employeeName={form.employee_name || fullName}
                  defaultEffectiveFrom={form.date_of_joining || todayIso()}
                  requireAccount={Boolean(isEditMode && effectiveNeedsBank)}
                  onSaved={() => setHasBankAccounts(true)}
                />
              </div>
            )}
            {/* Regular edit keeps the account manager for verify / deactivate. */}
            {isEditMode && effectiveEmployeeId && !effectiveNeedsBank ? (
              <div style={{ display: currentStep === 4 ? "block" : "none" }}>
                <EmployeeBankSection
                  key={`bank-${effectiveEmployeeId}`}
                  employeeId={effectiveEmployeeId}
                  sectionRef={bankSectionRef}
                  canManage={true}
                  canVerify={true}
                  employeeName={form.employee_name || fullName}
                  defaultEffectiveFrom={form.date_of_joining || todayIso()}
                  onChange={(list) => {
                    const rows = Array.isArray(list) ? list : [];
                    setHasBankAccounts(rows.some((a) => a?.is_active !== false));
                  }}
                />
              </div>
            ) : null}
            {currentStep === 5 && (
              <Step5Statutory
                form={statutoryForm}
                setForm={setStatutoryForm}
                errors={statutoryErrors}
                setErrors={setStatutoryErrors}
                aadhaarLast4={statutoryAadhaarLast4}
              />
            )}

            {/* Clear validation / save errors with step-by-step fix guidance */}
            {submitError && (
              <GuidedNotice
                domain="employee"
                message={submitError}
                variant="error"
                stepsTitle="How to fix this"
                className="mb-4"
                onDismiss={() => setSubmitError("")}
              />
            )}
          </div>

          {/* Navigation footer */}
          <div className="ec-nav">
            <button
              type="button"
              className="ec-btn-back-nav"
              onClick={handleBack}
              disabled={currentStep === 1 || loading}
            >
              <EI.ArrowLeft /> Previous
            </button>

            <span className="ec-step-counter">Step {currentStep} of 5</span>

            {currentStep < 4 ? (
              <button type="button" className="ec-btn-next" onClick={handleNext}>
                Next <EI.ArrowRight />
              </button>
            ) : currentStep === 4 ? (
              <div className="ec-nav-actions">
                {!isEditMode && (
                  <button
                    type="button"
                    className="ec-btn-skip"
                    onClick={handleSkipBanking}
                    disabled={bankSaving || loading}
                    title="Skip banking for now — you can add it later"
                  >
                    Skip
                  </button>
                )}
                <button
                  type="button"
                  className="ec-btn-next"
                  onClick={handleStep4Next}
                  disabled={bankSaving}
                >
                  {bankSaving ? "Saving…" : bankActionLabel}
                  {!bankSaving ? <> <EI.ArrowRight /></> : null}
                </button>
              </div>
            ) : (
              <div className="ec-nav-actions">
                <button
                  type="button"
                  className="ec-btn-save"
                  onClick={handleFinishClick}
                  disabled={loading}
                >
                  {loading ? (
                    <><span className="ec-spinner" /> Saving…</>
                  ) : (
                    <><EI.Check /> {isEditMode ? "Save Changes" : "Finish"}</>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {showStatutorySkipModal && (
        <div
          className="ec-stat-modal-overlay"
          role="presentation"
          onClick={() => setShowStatutorySkipModal(false)}
        >
          <div
            className="ec-stat-modal"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="ec-stat-skip-title"
            aria-describedby="ec-stat-skip-text"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ec-stat-modal-banner">
              <button
                type="button"
                className="ec-stat-modal-close"
                onClick={() => setShowStatutorySkipModal(false)}
                aria-label="Close and stay on statutory page"
              >
                <EI.X />
              </button>
              <div className="ec-stat-modal-icon" aria-hidden>
                <EI.FileText />
              </div>
              <p className="ec-stat-modal-kicker">Step 5 of 5 · Statutory & Tax</p>
              <h2 id="ec-stat-skip-title" className="ec-stat-modal-title">
                Statutory details are not filled
              </h2>
            </div>
            <div className="ec-stat-modal-body">
              <p id="ec-stat-skip-text" className="ec-stat-modal-text">
                You have not entered statutory IDs on this page. We can finish onboarding with an empty statutory profile, or you can stay here and fill the last page now.
              </p>
              {unfilledStatutoryIds.length > 0 && (
                <div className="ec-stat-modal-chips" aria-label="Unfilled statutory fields">
                  {unfilledStatutoryIds.map((f) => (
                    <span key={f.key} className="ec-stat-chip">
                      <span className="ec-stat-chip-dot" aria-hidden />
                      {f.label}
                    </span>
                  ))}
                </div>
              )}
              <div className="ec-stat-choices">
                <button
                  type="button"
                  className="ec-stat-choice ec-stat-choice--fill"
                  onClick={() => setShowStatutorySkipModal(false)}
                >
                  <span className="ec-stat-choice-icon" aria-hidden>
                    <EI.PencilLg />
                  </span>
                  <p className="ec-stat-choice-title">Fill statutory page</p>
                  <p className="ec-stat-choice-sub">
                    Stay on this last step and enter UAN, PAN, Aadhaar, and other IDs.
                  </p>
                </button>
                <button
                  type="button"
                  className="ec-stat-choice ec-stat-choice--go"
                  onClick={confirmFinishWithoutStatutory}
                  disabled={loading}
                >
                  <span className="ec-stat-choice-icon" aria-hidden>
                    {loading ? <span className="ec-spinner" style={{ borderColor: "rgba(4,120,87,.25)", borderTopColor: "#047857" }} /> : <EI.CheckLg />}
                  </span>
                  <p className="ec-stat-choice-title">{loading ? "Saving…" : "Finish anyway"}</p>
                  <p className="ec-stat-choice-sub">
                    Proceed with an empty statutory profile. You can add these IDs later.
                  </p>
                </button>
              </div>
              <p className="ec-stat-modal-foot">
                <EI.Info />
                Statutory IDs can be added anytime from the employee profile after onboarding.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
